import { RecentSearchClient } from '@zillow/recent-search-fetcher';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import React, { useEffect, useCallback, memo, Component, useState, useRef, useMemo } from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import { useDebouncedCallback } from 'use-debounce';
import { ListboxOptionLabel, Gleam, ComboboxOption, Highlighter, IconButton, BUTTON_SIZES, IconLocationArrowOutline, IconLocationArrow, Adornment, token, AdornedInput, Input, IconCloseCircle, IconSearch, Combobox, StyledDropdown, Form, INPUT_SIZES, T_SHIRT_SIZES, VisuallyHidden, IconFavoriteHomeOutline, IconSavedSearchOutline, IconClockOutline, IconLocationOutline, IconSearchOutline, IconGraduationCapOutline, IconChevronLeft } from '@zillow/constellation';
import _toConsumableArray from '@babel/runtime/helpers/toConsumableArray';
import omit from 'lodash/omit';
import isEmpty from 'lodash/isEmpty';
import { isInitialized, track, datalayer, event } from '@zillow/universal-analytics';
import { yieldCallback, useYieldCallback } from '@zillow/yield-callback';
import findIndex from 'lodash/findIndex';
import AutocompleteManager, { FetchTypes } from '@zillow/autocomplete-manager';
import _taggedTemplateLiteralLoose from '@babel/runtime/helpers/taggedTemplateLiteralLoose';
import gql from 'graphql-tag';
import _typeof from '@babel/runtime/helpers/typeof';
import flow from 'lodash/flow';
import identity from 'lodash/identity';
import _extends from '@babel/runtime/helpers/extends';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import _assertThisInitialized from '@babel/runtime/helpers/assertThisInitialized';
import _inheritsLoose from '@babel/runtime/helpers/inheritsLoose';
import PropTypes from 'prop-types';

/* Search/Autocomplete */
// min input length before autocomplete results are fetched
var MIN_AUTOCOMPLETE_CHARS = 3;

// default number of results to show in autocomplete dropdown
var DEFAULT_MAX_SAVED_SEARCHES = 2;
var DEFAULT_MAX_CLAIMED_HOMES = 1;
var DEFAULT_MAX_RECENT_SEARCHES = 3;
var DEFAULT_MAX_REGION_ADDRESS = 10;
var DEFAULT_MAX_SAVED_HOMES = 1;
var DEFAULT_MAX_POST_TYPE_SEMANTIC = 10;
var DEFAULT_MAX_PRE_TYPE_SEMANTIC = 3;

/* FetchSuggestions reasons */
var INIT_LOCAL_RESULTS = 'init-local-results';
var FREEFORM = 'freeform';
var CLEAR_INPUT = 'clear input';

// current location radius to make map bounds from user position
var CURRENT_LOCATION_MAPBOUNDS_RADIUS = 0.005;

// Latitude delta used when we get a MALONE address back from QU
// The resulting URL will be zoomed in and centered on the address
var MALONE_ADDRESS_LATITUDE_DELTA = 0.001;
var AUTOCOMPLETE_REQUEST_DELAY = 400;
var SCREEN_READER_MESSAGE_TIMEOUT = 500;
var MAX_REGION_SELECTION_LENGTH = 5;

/* eslint-disable zillow/@typescript-eslint/consistent-type-imports */

var SuggestionCategory = /*#__PURE__*/function (SuggestionCategory) {
  SuggestionCategory["address"] = "ADDRESS";
  SuggestionCategory["claimedHome"] = "CLAIMED_HOME";
  SuggestionCategory["currentLocation"] = "CURRENT_LOCATION";
  SuggestionCategory["rawSearch"] = "RAW_SEARCH";
  SuggestionCategory["recentSearch"] = "RECENT_SEARCH";
  SuggestionCategory["region"] = "REGION";
  SuggestionCategory["savedHome"] = "SAVED_HOME";
  SuggestionCategory["savedSearch"] = "SAVED_SEARCH";
  SuggestionCategory["school"] = "SCHOOL";
  SuggestionCategory["semantic"] = "SEMANTIC";
  SuggestionCategory["builderCommunity"] = "BUILDER_COMMUNITY";
  return SuggestionCategory;
}({});
var isSuggestion = function isSuggestion(searchTerm) {
  return !('regions' in searchTerm);
};
var isSearchBoxSelection = function isSearchBoxSelection(searchTerm) {
  return 'regions' in searchTerm;
};

/**
 * Additional data describing a suggestion
 */

var isRecentSearch = function isRecentSearch(suggestion) {
  return suggestion.category === SuggestionCategory.recentSearch;
};
var isRegionSuggestion = function isRegionSuggestion(suggestion) {
  return suggestion.category === SuggestionCategory.region;
};
var isRecentRegionSuggestion = function isRecentRegionSuggestion(suggestion) {
  return isRecentSearch(suggestion) && suggestion.subCategory === SuggestionCategory.region;
};
var isCurrentLocationSuggestion = function isCurrentLocationSuggestion(suggestion) {
  return suggestion.category === SuggestionCategory.currentLocation;
};
var isSemanticSuggestion = function isSemanticSuggestion(suggestion) {
  return suggestion.category === SuggestionCategory.semantic;
};
var isRecentSemanticSuggestion = function isRecentSemanticSuggestion(suggestion) {
  return isRecentSearch(suggestion) && suggestion.subCategory === SuggestionCategory.semantic;
};
var isAddressSuggestion = function isAddressSuggestion(suggestion) {
  var _suggestion$data;
  return suggestion.category === SuggestionCategory.address && ((_suggestion$data = suggestion.data) === null || _suggestion$data === void 0 ? void 0 : _suggestion$data.addressType) !== 'community_address';
};
var isRecentAddressSuggestion = function isRecentAddressSuggestion(suggestion) {
  var _suggestion$data2;
  return isRecentSearch(suggestion) && suggestion.subCategory === SuggestionCategory.address && ((_suggestion$data2 = suggestion.data) === null || _suggestion$data2 === void 0 ? void 0 : _suggestion$data2.addressType) !== 'community_address';
};
var isMaloneAddressSuggestion = function isMaloneAddressSuggestion(suggestion) {
  return isAddressSuggestion(suggestion) && suggestion.data.addressType === 'MALONE_ADDRESS';
};
var isSchoolSuggestion = function isSchoolSuggestion(suggestion) {
  return suggestion.category === SuggestionCategory.school;
};
var isRentalCommunitySuggestion = function isRentalCommunitySuggestion(suggestion) {
  var _suggestion$data3;
  return suggestion.category === SuggestionCategory.address && ((_suggestion$data3 = suggestion.data) === null || _suggestion$data3 === void 0 ? void 0 : _suggestion$data3.addressType) === 'community_address';
};
var isRecentRentalCommunitySuggestion = function isRecentRentalCommunitySuggestion(suggestion) {
  var _suggestion$data4;
  return isRecentSearch(suggestion) && suggestion.subCategory === SuggestionCategory.address && ((_suggestion$data4 = suggestion.data) === null || _suggestion$data4 === void 0 ? void 0 : _suggestion$data4.addressType) === 'community_address';
};
var isBuilderCommunitySuggestion = function isBuilderCommunitySuggestion(suggestion) {
  return suggestion.category === SuggestionCategory.builderCommunity;
};
var isRecentBuilderCommunitySuggestion = function isRecentBuilderCommunitySuggestion(suggestion) {
  return isRecentSearch(suggestion) && suggestion.subCategory === SuggestionCategory.builderCommunity;
};
var isSavedSearchSuggestion = function isSavedSearchSuggestion(suggestion) {
  return suggestion.category === SuggestionCategory.savedSearch;
};

var encodeAddressString = function encodeAddressString(address) {
  var ret = encodeURIComponent(address.replace(/;/g, ' '));
  return ret.replace(/%2C/g, ',').replace(/%25/g, '').replace(/%23/g, '.num.').replace(/-/g, '.dash.').replace(/\+/g, '-').replace(/%2B/g, '-').replace(/%20/g, '-').replace(/%2F/g, '-').replace(/\//g, '-').replace(/(-)+/g, '-');
};

/**
 * @typedef {import('./suggestion').SearchTerm} SearchTerm;
 */

var SAVED_HOMES_URL = '/savedhomes/';
var encodeSeoURLStringBldg = function encodeSeoURLStringBldg(str) {
  var addressBreakdown = str.split(/,/g);
  addressBreakdown = addressBreakdown.map(function (addr) {
    return addr.trim().replace(/[;\\/ ]/g, '-');
  });
  var encodedStr = addressBreakdown.join('-');
  return encodedStr;
};

/**
 * Converts an object into stringified query parameters key=value&keyB=valueB
 * @param {Object} queryParamObject
 * @returns {String}
 */
var paramsObjectToString = function paramsObjectToString(queryParamObject) {
  return Object.keys(queryParamObject).map(function (key) {
    return key + "=" + queryParamObject[key];
  }).join('&');
};
var getShouldUseBDPUrl = function getShouldUseBDPUrl(searchTermData) {
  var addressType = searchTermData === null || searchTermData === void 0 ? void 0 : searchTermData.addressType;
  var lat = searchTermData === null || searchTermData === void 0 ? void 0 : searchTermData.lat;
  var lng = searchTermData === null || searchTermData === void 0 ? void 0 : searchTermData.lng;
  return addressType && addressType === 'community_address' && lat && lng;
};
var getShouldUseCDPUrl = function getShouldUseCDPUrl(searchTerm) {
  var _searchTerm$data;
  var category = searchTerm === null || searchTerm === void 0 ? void 0 : searchTerm.category;
  var subCategory = searchTerm === null || searchTerm === void 0 ? void 0 : searchTerm.subCategory;
  var plid = searchTerm === null || searchTerm === void 0 ? void 0 : (_searchTerm$data = searchTerm.data) === null || _searchTerm$data === void 0 ? void 0 : _searchTerm$data.plid;
  return category && (category === SuggestionCategory.builderCommunity || category === SuggestionCategory.recentSearch && subCategory === SuggestionCategory.builderCommunity) && plid;
};
var getShouldUseSemanticUrl = function getShouldUseSemanticUrl(searchTerm) {
  var category = searchTerm === null || searchTerm === void 0 ? void 0 : searchTerm.category;
  var subCategory = searchTerm === null || searchTerm === void 0 ? void 0 : searchTerm.subCategory;
  return category === SuggestionCategory.semantic || category === SuggestionCategory.recentSearch && subCategory === SuggestionCategory.semantic;
};
var getStaticMapBounds = function getStaticMapBounds(latitude, longitude) {
  return {
    north: latitude + CURRENT_LOCATION_MAPBOUNDS_RADIUS,
    south: latitude - CURRENT_LOCATION_MAPBOUNDS_RADIUS,
    east: longitude + CURRENT_LOCATION_MAPBOUNDS_RADIUS,
    west: longitude - CURRENT_LOCATION_MAPBOUNDS_RADIUS
  };
};
var buildURLForSemanticSearch = function buildURLForSemanticSearch(searchTerm, location) {
  var _filters$isForRent;
  var _searchTerm$displayNa = searchTerm.displayName,
    usersSearchTerm = _searchTerm$displayNa === void 0 ? '' : _searchTerm$displayNa,
    _searchTerm$regions = searchTerm.regions,
    regionSelection = _searchTerm$regions === void 0 ? [] : _searchTerm$regions,
    _searchTerm$filters = searchTerm.filters,
    filters = _searchTerm$filters === void 0 ? {} : _searchTerm$filters,
    category = searchTerm.category,
    _searchTerm$queryReso = searchTerm.queryResolutionStatus,
    queryResolutionStatus = _searchTerm$queryReso === void 0 ? '' : _searchTerm$queryReso;
  var filterState = Object.assign({}, filters, {
    sortSelection: {
      value: ((_filters$isForRent = filters.isForRent) === null || _filters$isForRent === void 0 ? void 0 : _filters$isForRent.value) ? 'priorityscore' : 'globalrelevanceex'
    }
  });
  var nlsQueryStatus = queryResolutionStatus !== 'FULL_RESOLUTION' ? queryResolutionStatus : null;
  var searchQueryState = {
    filterState: filterState,
    regionSelection: regionSelection,
    usersSearchTerm: usersSearchTerm
  };
  if (regionSelection.length === 0) {
    delete searchQueryState.regionSelection;
  }
  var url = "/homes/?searchQueryState=" + encodeURIComponent(JSON.stringify(searchQueryState));
  if (nlsQueryStatus) {
    url += "&nlsQueryStatus=" + nlsQueryStatus;
  }
  if ((location === null || location === void 0 ? void 0 : location.latitude) && (location === null || location === void 0 ? void 0 : location.longitude)) {
    var latitude = location.latitude,
      longitude = location.longitude;
    var _ref = location.mapBounds || getStaticMapBounds(latitude, longitude),
      north = _ref.north,
      south = _ref.south,
      east = _ref.east,
      west = _ref.west;
    url += "&userPosition=" + longitude + "," + latitude;
    url += "&userPositionBounds=" + north + "," + east + "," + south + "," + west;
    url += '&currentLocationSearch=true';
  }
  url += "&category=" + category;
  return url;
};

/**
 * Generates search URL based on
 * search input, location, search type, and query params.
 * @param {SearchTerm} searchTerm
 * @param {{ latitude?: number, longitude?: number }} [location={}]
 * @param {string} [searchType]
 * @param {Record<string, unknown>} [extraQueryParams={}] map of additional params
 * @returns {string | { error: string }}
 */
var buildURL = function buildURL(searchTerm, location, searchType, extraQueryParams) {
  var _searchTerm$data2, _searchTerm$data3, _searchTerm$data4;
  if (location === void 0) {
    location = {};
  }
  if (extraQueryParams === void 0) {
    extraQueryParams = {};
  }
  if (!searchTerm.category) {
    throw new Error('SearchBox buildURL invoked without category property');
  }
  var category = searchTerm.category,
    displayName = searchTerm.displayName;
  var _location = location,
    latitude = _location.latitude,
    longitude = _location.longitude;
  var schoolId = searchTerm === null || searchTerm === void 0 ? void 0 : (_searchTerm$data2 = searchTerm.data) === null || _searchTerm$data2 === void 0 ? void 0 : _searchTerm$data2.schoolId;
  var districtId = searchTerm === null || searchTerm === void 0 ? void 0 : (_searchTerm$data3 = searchTerm.data) === null || _searchTerm$data3 === void 0 ? void 0 : _searchTerm$data3.schoolDistrictId;
  var shouldUseBDPUrl = getShouldUseBDPUrl(searchTerm === null || searchTerm === void 0 ? void 0 : searchTerm.data);
  var shouldUseCDPUrl = getShouldUseCDPUrl(searchTerm);
  var queryParams = Object.assign({}, extraQueryParams);
  var shouldIncludeCategoryParam = category === 'RECENT_SEARCH' && !(shouldUseBDPUrl || shouldUseCDPUrl);
  if (shouldIncludeCategoryParam) {
    queryParams.category = category;
  }
  switch (category) {
    case SuggestionCategory.claimedHome:
    case SuggestionCategory.savedSearch:
      return searchTerm.url;
    case SuggestionCategory.savedHome:
      return SAVED_HOMES_URL;
  }
  if (getShouldUseSemanticUrl(searchTerm)) {
    return buildURLForSemanticSearch(searchTerm, location);
  }
  if ((_searchTerm$data4 = searchTerm.data) === null || _searchTerm$data4 === void 0 ? void 0 : _searchTerm$data4.url) {
    return searchTerm.data.url;
  }
  var generatedUrl = '/homes/';
  if (shouldUseBDPUrl) {
    generatedUrl = '/b/';
  }
  if (shouldUseCDPUrl) {
    generatedUrl = '/community/';
  }
  if (schoolId) {
    generatedUrl = "/schools/" + schoolId + "/";
  } else if (districtId) {
    generatedUrl = "/districts/" + districtId + "/";
  }
  if (searchType) {
    generatedUrl += searchType + "/";
  }
  if (typeof displayName === 'string' && category !== 'CURRENT_LOCATION') {
    if (shouldUseBDPUrl) {
      var _searchTerm$data5, _searchTerm$data6;
      generatedUrl = "" + generatedUrl + encodeSeoURLStringBldg(displayName) + "/";
      generatedUrl = "" + generatedUrl + (searchTerm === null || searchTerm === void 0 ? void 0 : (_searchTerm$data5 = searchTerm.data) === null || _searchTerm$data5 === void 0 ? void 0 : _searchTerm$data5.lat) + "," + (searchTerm === null || searchTerm === void 0 ? void 0 : (_searchTerm$data6 = searchTerm.data) === null || _searchTerm$data6 === void 0 ? void 0 : _searchTerm$data6.lng) + "_ll/";
    } else if (shouldUseCDPUrl) {
      generatedUrl = "" + generatedUrl + (searchTerm === null || searchTerm === void 0 ? void 0 : searchTerm.data.plid) + "_plid";
    } else if (!schoolId && !districtId) {
      generatedUrl = "" + generatedUrl + encodeAddressString(displayName) + "_rb/";
    }
  }
  if (category === 'CURRENT_LOCATION') {
    if (typeof latitude !== 'undefined' && typeof longitude !== 'undefined') {
      // adding CURRENT_LOCATION_MAPBOUNDS_RADIUS prevents sending the map bounds with no size
      queryParams.userPosition = longitude + "," + latitude;
      var _getStaticMapBounds = getStaticMapBounds(latitude, longitude),
        north = _getStaticMapBounds.north,
        south = _getStaticMapBounds.south,
        east = _getStaticMapBounds.east,
        west = _getStaticMapBounds.west;
      queryParams.userPositionBounds = north + "," + east + "," + south + "," + west;
    } else {
      return {
        error: 'No location provided for CURRENT_LOCATION search'
      };
    }
    queryParams.currentLocationSearch = true;
  }
  var queryParamsString = paramsObjectToString(queryParams);
  if (queryParamsString) {
    generatedUrl += "?" + queryParamsString;
  }
  return generatedUrl;
};
var recentSearchCategoriesForMultiRegion = ['REGION', 'ADDRESS', 'RECENT_SEARCH', 'BUILDER_COMMUNITY'];
var isSearchTermEligibleForRecentSearch = function isSearchTermEligibleForRecentSearch(searchTerm) {
  return recentSearchCategoriesForMultiRegion.includes(searchTerm.category);
};

/**
 * URL redirect based on search term and/or location
 * @param {SearchTerm} searchTerm - submitted search result
 * @param {{ latitude?: number, longitude?: number }} [location] - { latitude, longitude }
 * @param {string} [searchType] - search type
 * @param {Record<string, unknown>} [extraQueryParams] - query params to append to url
 * @param {boolean} openNewTab - True: Open search result in new tab, False: redirect current tab
 */
function SRPNavigationSubmitHandler(searchTerm, location, searchType, extraQueryParams, openNewTab) {
  if (openNewTab === void 0) {
    openNewTab = false;
  }
  var newURL = buildURL(searchTerm, location, searchType, extraQueryParams);
  if (newURL && newURL.error) {
    console.error("Search handler failed. Reason: " + newURL.error);
  } else if (typeof newURL === 'string' && newURL.length === 0) {
    console.error('Search handler did not produce a URL');
  } else if (!window || !window.location) {
    console.error('Failed to navigate to new search page. window.location undefined');
  } else {
    if (isSearchTermEligibleForRecentSearch(searchTerm)) {
      RecentSearchClient.addToRecentSearches(searchTerm);
    }
    if (openNewTab) {
      window.open(newURL);
    } else {
      window.location.href = newURL;
    }
  }
}

var EventNames = /*#__PURE__*/function (EventNames) {
  EventNames["selectSearchSuggestion"] = "SELECT_SEARCH_SUGGESTION";
  EventNames["rawSearch"] = "RAW_SEARCH";
  EventNames["selectRecentSearch"] = "SELECT_RECENT_SEARCH";
  EventNames["removeMultiRegion"] = "REMOVE_MULTIREGION";
  EventNames["currentLocationSearch"] = "CURRENT_LOCATION_SEARCH";
  EventNames["removeAllMultiRegion"] = "REMOVE_ALL_MULTIREGION";
  EventNames["addNthRegion"] = "ADD_NTH_REGION";
  EventNames["dismissSearch"] = "DISMISS_SEARCH";
  EventNames["queryUnderstandingResponse"] = "QUERY_UNDERSTANDING_RESPONSE";
  EventNames["searchBoxFocus"] = "SEARCH_BOX_FOCUS";
  return EventNames;
}({});

/* eslint-disable zillow/@typescript-eslint/naming-convention */
var EVENT_TYPE_IDS = {
  SEARCH_BOX_FOCUS: '65',
  SELECT_SEARCH_SUGGESTION: '74',
  RAW_SEARCH: '73',
  SELECT_RECENT_SEARCH: '1032',
  REMOVE_MULTIREGION: '1069',
  CURRENT_LOCATION_SEARCH: '1079',
  REMOVE_ALL_MULTIREGION: '1082',
  ADD_NTH_REGION: '1081',
  DISMISS_SEARCH: '1080',
  QUERY_UNDERSTANDING_RESPONSE: '3875'
};
var EVENT_TYPE_VERSION_IDS = {
  SEARCH_BOX_FOCUS: '5',
  SELECT_SEARCH_SUGGESTION: '4',
  RAW_SEARCH: '4',
  SELECT_RECENT_SEARCH: '3',
  REMOVE_MULTIREGION: '3',
  CURRENT_LOCATION_SEARCH: '3',
  REMOVE_ALL_MULTIREGION: '3',
  ADD_NTH_REGION: '3',
  DISMISS_SEARCH: '3',
  QUERY_UNDERSTANDING_RESPONSE: '5'
};
var EVENT_TEMPLATE_IDS = {
  SEARCH_BOX_FOCUS: '16',
  SELECT_SEARCH_SUGGESTION: '16',
  RAW_SEARCH: '16',
  SELECT_RECENT_SEARCH: '1',
  REMOVE_MULTIREGION: '16',
  CURRENT_LOCATION_SEARCH: '16',
  REMOVE_ALL_MULTIREGION: '16',
  ADD_NTH_REGION: '16',
  DISMISS_SEARCH: '16',
  QUERY_UNDERSTANDING_RESPONSE: '278'
};
var TRIGGER_TYPE_NM = {
  SEARCH_BOX_FOCUS: 'interaction',
  SELECT_SEARCH_SUGGESTION: 'interaction',
  RAW_SEARCH: 'interaction',
  SELECT_RECENT_SEARCH: 'interaction',
  REMOVE_MULTIREGION: 'interaction',
  CURRENT_LOCATION_SEARCH: 'interaction',
  REMOVE_ALL_MULTIREGION: 'interaction',
  ADD_NTH_REGION: 'interaction',
  DISMISS_SEARCH: 'interaction',
  QUERY_UNDERSTANDING_RESPONSE: 'interaction'
};
var TRIGGER_OBJECT_NM = {
  SEARCH_BOX_FOCUS: 'search_component',
  SELECT_SEARCH_SUGGESTION: 'search_field',
  RAW_SEARCH: 'search_field',
  SELECT_RECENT_SEARCH: 'search_field',
  REMOVE_MULTIREGION: 'search_field',
  CURRENT_LOCATION_SEARCH: 'search_box',
  REMOVE_ALL_MULTIREGION: 'search_box',
  ADD_NTH_REGION: 'search_box',
  DISMISS_SEARCH: 'no_trigger_object',
  QUERY_UNDERSTANDING_RESPONSE: 'search_field'
};
var TRIGGER_SOURCE_NM = {
  SEARCH_BOX_FOCUS: 'search_bar',
  SELECT_SEARCH_SUGGESTION: 'select_search_suggestion',
  RAW_SEARCH: 'manual_search_selection',
  SELECT_RECENT_SEARCH: 'button_to_select_recent_search',
  REMOVE_MULTIREGION: 'button_to_remove_multiregion',
  CURRENT_LOCATION_SEARCH: 'current_location',
  REMOVE_ALL_MULTIREGION: 'button_remove_regions',
  ADD_NTH_REGION: 'add_region',
  DISMISS_SEARCH: 'button_to_dismiss_search_dropdown',
  QUERY_UNDERSTANDING_RESPONSE: 'query_understanding_response'
};
var SEMANTIC_EVENT_NM = {
  SEARCH_BOX_FOCUS: 'search_box_focus',
  SELECT_SEARCH_SUGGESTION: 'search_home',
  RAW_SEARCH: 'search_home',
  SELECT_RECENT_SEARCH: 'search_home',
  REMOVE_MULTIREGION: 'change_search_filter',
  CURRENT_LOCATION_SEARCH: 'search_home',
  REMOVE_ALL_MULTIREGION: 'search_home',
  ADD_NTH_REGION: 'search_home',
  DISMISS_SEARCH: 'dismiss_search',
  QUERY_UNDERSTANDING_RESPONSE: 'search_home'
};
var TOPIC_TAG_TXT = {
  SEARCH_BOX_FOCUS: ['search_box_focus'],
  SELECT_SEARCH_SUGGESTION: ['search'],
  RAW_SEARCH: ['search'],
  SELECT_RECENT_SEARCH: ['search'],
  REMOVE_MULTIREGION: ['search'],
  CURRENT_LOCATION_SEARCH: ['search'],
  REMOVE_ALL_MULTIREGION: ['search'],
  ADD_NTH_REGION: ['search'],
  DISMISS_SEARCH: ['search'],
  QUERY_UNDERSTANDING_RESPONSE: ['search']
};
/* eslint-disable zillow/@typescript-eslint/naming-convention */

var QueryResolutionStatus = /*#__PURE__*/function (QueryResolutionStatus) {
  QueryResolutionStatus["fullResolution"] = "FULL_RESOLUTION";
  QueryResolutionStatus["partialResolution"] = "PARTIAL_RESOLUTION";
  QueryResolutionStatus["failed"] = "FAILED";
  QueryResolutionStatus["error"] = "ERROR";
  return QueryResolutionStatus;
}({});

/* eslint-disable zillow/@typescript-eslint/consistent-type-imports, zillow/@typescript-eslint/naming-convention */

var CLICKSTREAM_FILTER_IDS = ['ac_ind', 'age_55_plus_ind', 'apt_ind', 'apt_or_condo_ind', 'auction_ind', 'bath_cnt_txt', 'bed_max_cnt_txt', 'bed_min_cnt_txt', 'built_year_max_nb', 'built_year_min_nb', 'city_view_ind', 'coming_soon_ind', 'condo_ind', 'day_on_zillow_nb', 'finished_basement_ind', 'for_rent_ind', 'forclosure_ind', 'fsba_ind', 'fsbo_ind', 'garage_ind', 'hoa_ind', 'hoa_max_amt', 'keywords_txt', 'lot_land_ind', 'lot_size_sqft_max_nb', 'lot_size_sqft_min_nb', 'manufactured_ind', 'monthly_payment_max_amt', 'monthly_payment_min_amt', 'mountain_view_ind', 'multi_family_ind', 'new_construction_ind', 'only_open_house_ind', 'park_view_ind', 'parking_spot_txt', 'pool_ind', 'premarket_foreclosure_ind', 'price_range_max_amt', 'price_range_min_amt', 'recently_sold_ind', 'rent_large_pets_allowed_ind', 'rental_accepts_application_ind', 'rental_cats_allowed_ind', 'rental_in_unit_laundry_ind', 'rental_parking_available_ind', 'rental_income_restricted_ind', 'rental_small_pets_allowed_ind', 'single_family_ind', 'single_story_ind', 'sqft_max_nb', 'sqft_min_nb', 'tour_3d_ind', 'townhouse_ind', 'unfinished_basement_ind', 'water_view_ind', 'waterfront_ind'];
var CLICKSTREAM_SHOW_ONLY_MAP = {
  SINGLE_STORY: 'single_story_ind',
  INCOME_RESTRICTED: 'rental_income_restricted_ind',
  INUNIT_LAUNDRY: 'rental_in_unit_laundry_ind',
  ASSIGNED_PARKING: 'rental_parking_available_ind',
  OPEN_HOUSES: 'only_open_house_ind',
  WATER_FRONT: 'waterfront_ind',
  AIRCONDITIONING: 'ac_ind',
  POOL: 'pool_ind',
  ACCEPTS_RENTAL_APPLICATIONS: 'rental_accepts_application_ind',
  GARAGE: 'garage_ind',
  APPROVED_VIRTUALTOUR: 'tour_3d_ind'
};
var CLICKSTREAM_BASEMENT_MAP = {
  FINISHED: 'finished_basement_ind',
  UNFINISHED: 'unfinished_basement_ind'
};
var CLICKSTREAM_HOME_TYPE_MAP = {
  TOWNHOME: 'townhouse_ind',
  LAND: 'lot_land_ind',
  MANUFACTURED: 'manufactured_ind',
  MULTIFAMILY: 'multi_family_ind',
  APARTMENT: 'apt_ind',
  CONDO: 'condo_ind',
  SINGLEFAMILY: 'single_family_ind'
};
var CLICKSTREAM_LISTING_STATUS_MAP = {
  RECENTLY_SOLD: 'recently_sold_ind',
  SOLD: 'recently_sold_ind',
  // PREFORECLOSURE filter is not currently defined in the search_filter block schema
  // PREFORECLOSURE: 'preforeclosure_ind',
  FORECLOSED: 'premarket_foreclosure_ind',
  FOR_RENT: 'for_rent_ind',
  FSBA: 'fsba_ind',
  FSBO: 'fsbo_ind',
  AUCTION: 'auction_ind',
  COMINGSOON: 'coming_soon_ind',
  FORECLOSURE: 'forclosure_ind',
  NEWCONSTRUCTION: 'new_construction_ind'
};
var CLICKSTREAM_PETS_ALLOWED_MAP = {
  LARGEDOGS: 'rent_large_pets_allowed_ind',
  SMALLDOGS: 'rental_small_pets_allowed_ind',
  CATS: 'rental_cats_allowed_ind'
};
var CLICKSTREAM_VIEW_MAP = {
  CITY: 'city_view_ind',
  PARK: 'park_view_ind',
  MOUNTAIN: 'mountain_view_ind',
  WATER: 'water_view_ind'
};

var yieldWrapper = yieldCallback(function (callback) {
  callback();
});

/**
 * @typedef {import('./suggestion').Suggestion} Suggestion
 * @typedef {import('./suggestion').SuggestionMetadata} SuggestionMetadata
 */

var getEnvelopeBlock = function getEnvelopeBlock(eventName) {
  return {
    event_template_id: EVENT_TEMPLATE_IDS[eventName],
    event_template_version_id: '1',
    event_type_id: EVENT_TYPE_IDS[eventName],
    event_type_version_id: EVENT_TYPE_VERSION_IDS[eventName],
    event_client_start_dtm: new Date().toISOString()
  };
};
var getClickstreamBlock = function getClickstreamBlock(eventName) {
  return {
    trigger_location_nm: 'search_results',
    trigger_type_nm: TRIGGER_TYPE_NM[eventName],
    trigger_object_nm: TRIGGER_OBJECT_NM[eventName],
    trigger_source_nm: TRIGGER_SOURCE_NM[eventName]
  };
};
var getSemanticBlock = function getSemanticBlock(eventName) {
  return {
    semantic_event_nm: SEMANTIC_EVENT_NM[eventName],
    topic_tag_txt: TOPIC_TAG_TXT[eventName]
  };
};
var suggestionToSearchType = function suggestionToSearchType(suggestion) {
  if (isRegionSuggestion(suggestion) || isRecentRegionSuggestion(suggestion)) {
    return 'REGION';
  }
  if (isSemanticSuggestion(suggestion) || isRecentSemanticSuggestion(suggestion)) {
    return 'NATURAL';
  }
  if (isAddressSuggestion(suggestion) || isRecentAddressSuggestion(suggestion)) {
    return 'ADDRESS';
  }
  if (isRentalCommunitySuggestion(suggestion) || isRecentRentalCommunitySuggestion(suggestion)) {
    return 'RENTAL_COMMUNITY';
  }
  if (isSchoolSuggestion(suggestion)) {
    return 'SCHOOL';
  }
  if (isBuilderCommunitySuggestion(suggestion) || isRecentBuilderCommunitySuggestion(suggestion)) {
    return 'BUILDER_COMMUNITY';
  }
  return null;
};
var getModifiedRegionName = function getModifiedRegionName(displayName) {
  var shouldModifyRegionName = displayName.includes(',');
  return shouldModifyRegionName ? displayName.split(',')[0] : displayName;
};
var createEmptyRegionFields = function createEmptyRegionFields() {
  return {
    city_nm: null,
    city_id: null,
    state_id: null,
    state_cd: null,
    zip_cd: null,
    neighborhood_nm: null,
    neighborhood_id: null,
    custom_region_ids: [],
    region_types: []
  };
};

/**
 * @param {Suggestion} suggestion
 */
var getRegionTypeFields = function getRegionTypeFields(suggestion) {
  var _suggestion$data2, _suggestion$data3, _suggestion$data4;
  if (isRegionSuggestion(suggestion) || isRecentRegionSuggestion(suggestion)) {
    var _suggestion$data;
    var fields = createEmptyRegionFields();
    var name = getModifiedRegionName(suggestion.displayName);
    switch ((_suggestion$data = suggestion.data) === null || _suggestion$data === void 0 ? void 0 : _suggestion$data.regionType) {
      case 'city':
        fields.city_nm = name;
        fields.city_id = (_suggestion$data2 = suggestion.data) === null || _suggestion$data2 === void 0 ? void 0 : _suggestion$data2.regionId;
        return fields;
      case 'zipcode':
        fields.zip_cd = name;
        return fields;
      case 'neighborhood':
        fields.neighborhood_nm = name;
        fields.neighborhood_id = (_suggestion$data3 = suggestion.data) === null || _suggestion$data3 === void 0 ? void 0 : _suggestion$data3.regionId;
        return fields;
      case 'state':
        fields.state_cd = name;
        fields.state_id = (_suggestion$data4 = suggestion.data) === null || _suggestion$data4 === void 0 ? void 0 : _suggestion$data4.regionId;
        return fields;
      default:
        return fields;
    }
  }
  if (isSemanticSuggestion(suggestion) || isRecentSemanticSuggestion(suggestion)) {
    var _suggestion$data$regi, _suggestion$data5;
    var _fields = createEmptyRegionFields();
    var regions = (_suggestion$data$regi = (_suggestion$data5 = suggestion.data) === null || _suggestion$data5 === void 0 ? void 0 : _suggestion$data5.regions) !== null && _suggestion$data$regi !== void 0 ? _suggestion$data$regi : [];
    if (regions.length === 0) {
      return _fields;
    }
    _fields.custom_region_ids = regions.map(function (entry) {
      return entry.id;
    });
    _fields.region_types = regions.map(function (entry) {
      return entry.type;
    });
    var region = regions[0];
    var regionName = getModifiedRegionName(region.displayName);
    switch (region.type) {
      case 'city':
        _fields.city_nm = regionName;
        _fields.city_id = region.id;
        return _fields;
      case 'zipcode':
        _fields.zip_cd = regionName;
        return _fields;
      case 'neighborhood':
        _fields.neighborhood_nm = regionName;
        _fields.neighborhood_id = region.id;
        return _fields;
      case 'state':
        _fields.state_cd = regionName;
        _fields.state_id = region.id;
        return _fields;
      default:
        return _fields;
    }
  }
  return createEmptyRegionFields();
};

/**
 * @param {Suggestion & { metadata?: SuggestionMetadata }} suggestion
 * @param {*} newLaneEventProperties
 */
var getUpdatedSearchFilterBlock = function getUpdatedSearchFilterBlock(suggestion, newLaneEventProperties) {
  var updateSearchFilter = (newLaneEventProperties === null || newLaneEventProperties === void 0 ? void 0 : newLaneEventProperties.search_filter) || {};
  if (!suggestion || isEmpty(suggestion)) {
    return updateSearchFilter;
  }
  var displayName = suggestion.displayName,
    data = suggestion.data,
    metadata = suggestion.metadata;
  updateSearchFilter.request_id = metadata === null || metadata === void 0 ? void 0 : metadata.requestId;
  updateSearchFilter.interaction_id = metadata === null || metadata === void 0 ? void 0 : metadata.interactionId;
  updateSearchFilter.search_type_txt = suggestionToSearchType(suggestion);
  updateSearchFilter.search_selection_type_txt = metadata === null || metadata === void 0 ? void 0 : metadata.selectionType;
  updateSearchFilter.selection_index_nb = metadata === null || metadata === void 0 ? void 0 : metadata.selectionIndex;
  updateSearchFilter.understood_query_txt = displayName;
  updateSearchFilter.user_search_terms_txt = (metadata === null || metadata === void 0 ? void 0 : metadata.rawValue) ? [metadata === null || metadata === void 0 ? void 0 : metadata.rawValue] : [];
  if (isRegionSuggestion(suggestion) || isRecentRegionSuggestion(suggestion)) {
    var _suggestion$data6;
    updateSearchFilter.region_types = [(_suggestion$data6 = suggestion.data) === null || _suggestion$data6 === void 0 ? void 0 : _suggestion$data6.regionType];
    Object.entries(getRegionTypeFields(suggestion)).forEach(function (_ref) {
      var _ref2 = _slicedToArray(_ref, 2),
        key = _ref2[0],
        value = _ref2[1];
      updateSearchFilter[key] = value;
    });
  }
  if (isSemanticSuggestion(suggestion) || isRecentSemanticSuggestion(suggestion)) {
    var _metadata$searchFilte;
    updateSearchFilter.query_resolution_status_txt = data.queryResolutionStatus;
    Object.entries(getRegionTypeFields(suggestion)).forEach(function (_ref3) {
      var _ref4 = _slicedToArray(_ref3, 2),
        key = _ref4[0],
        value = _ref4[1];
      updateSearchFilter[key] = value;
    });
    if (metadata === null || metadata === void 0 ? void 0 : metadata.searchFilterBlock) {
      CLICKSTREAM_FILTER_IDS.forEach(function (key) {
        if (key in updateSearchFilter && metadata.searchFilterBlock[key] === undefined) {
          delete updateSearchFilter[key];
        }
        if (metadata.searchFilterBlock[key] !== undefined) {
          updateSearchFilter[key] = metadata.searchFilterBlock[key];
        }
      });
    }
    updateSearchFilter.changed_filter_fields = (_metadata$searchFilte = metadata === null || metadata === void 0 ? void 0 : metadata.searchFilterBlock.changed_filter_fields) !== null && _metadata$searchFilte !== void 0 ? _metadata$searchFilte : [];
  } else {
    updateSearchFilter.query_resolution_status_txt = QueryResolutionStatus.fullResolution;
  }
  return updateSearchFilter;
};

/**
 * @param {Suggestion & { metadata?: SuggestionMetadata }} suggestion
 * @param {*} newLaneEventProperties
 */
var getUpdatedSearchMapBlock = function getUpdatedSearchMapBlock(suggestion, newLaneEventProperties) {
  var updatedSearchMap = newLaneEventProperties.search_map || {};
  if (!suggestion || isEmpty(suggestion)) {
    return updatedSearchMap;
  }
  if (isRegionSuggestion(suggestion) || isRecentRegionSuggestion(suggestion)) {
    updatedSearchMap.selected_regions_txt = [suggestion.displayName];
  }
  if (isSemanticSuggestion(suggestion) || isRecentSemanticSuggestion(suggestion)) {
    var _suggestion$data$regi2, _suggestion$data7, _suggestion$data7$reg;
    updatedSearchMap.selected_regions_txt = (_suggestion$data$regi2 = (_suggestion$data7 = suggestion.data) === null || _suggestion$data7 === void 0 ? void 0 : (_suggestion$data7$reg = _suggestion$data7.regions) === null || _suggestion$data7$reg === void 0 ? void 0 : _suggestion$data7$reg.map(function (entry) {
      return entry.displayName;
    })) !== null && _suggestion$data$regi2 !== void 0 ? _suggestion$data$regi2 : [];
  }
  return updatedSearchMap;
};
var getEventSpecificData = function getEventSpecificData(result, newLaneEventProperties) {
  switch (result === null || result === void 0 ? void 0 : result.category) {
    case 'RECENT_SEARCH':
      var updatedNewLaneEventProperties = omit(newLaneEventProperties, ['search_map', 'search_result']);
      return Object.assign({}, updatedNewLaneEventProperties, {
        semantic: getSemanticBlock(EventNames.selectRecentSearch),
        clickstream_trigger: getClickstreamBlock(EventNames.selectRecentSearch),
        envelope: getEnvelopeBlock(EventNames.selectRecentSearch)
      });
    case 'RAW_SEARCH':
      return Object.assign({}, newLaneEventProperties, {
        semantic: getSemanticBlock(EventNames.rawSearch),
        clickstream_trigger: getClickstreamBlock(EventNames.rawSearch),
        envelope: getEnvelopeBlock(EventNames.rawSearch)
      });
    case 'CURRENT_LOCATION':
      return Object.assign({}, newLaneEventProperties, {
        semantic: getSemanticBlock(EventNames.currentLocationSearch),
        clickstream_trigger: getClickstreamBlock(EventNames.currentLocationSearch),
        envelope: getEnvelopeBlock(EventNames.currentLocationSearch)
      });
    default:
      return Object.assign({}, newLaneEventProperties, {
        semantic: getSemanticBlock(EventNames.selectSearchSuggestion),
        clickstream_trigger: getClickstreamBlock(EventNames.selectSearchSuggestion),
        envelope: getEnvelopeBlock(EventNames.selectSearchSuggestion)
      });
  }
};
var getRemoveRegionEventData = function getRemoveRegionEventData(eventName, newLaneEventProperties, removedRegionIndex) {
  var updateSearchFilter = newLaneEventProperties.search_filter || {};
  var updatedSearchMap = newLaneEventProperties.search_map || {};
  var userSearchTermsArray = _toConsumableArray(updateSearchFilter.user_search_terms_txt);
  var customRegionIdsArray = _toConsumableArray(updateSearchFilter.custom_region_ids);
  var regionTypesArray = _toConsumableArray(updateSearchFilter.region_types);
  var selectedRegionsArray = _toConsumableArray(updatedSearchMap.selected_regions_txt);
  switch (eventName) {
    case EventNames.removeMultiRegion:
      var _updateSearchFilter$c;
      userSearchTermsArray.splice(removedRegionIndex - 1, 1);
      customRegionIdsArray.splice(removedRegionIndex - 1, 1);
      regionTypesArray.splice(removedRegionIndex - 1, 1);
      selectedRegionsArray.splice(removedRegionIndex - 1, 1);
      updateSearchFilter.user_search_terms_txt = userSearchTermsArray;
      updateSearchFilter.custom_region_ids = customRegionIdsArray;
      updateSearchFilter.region_types = regionTypesArray;
      updatedSearchMap.selected_regions_txt = selectedRegionsArray;
      if (!((_updateSearchFilter$c = updateSearchFilter.custom_region_ids) === null || _updateSearchFilter$c === void 0 ? void 0 : _updateSearchFilter$c.length)) {
        updateSearchFilter.city_nm = null;
        updateSearchFilter.city_id = null;
        updateSearchFilter.state_id = null;
        updateSearchFilter.state_cd = null;
        updateSearchFilter.zip_cd = null;
        updateSearchFilter.neighborhood_nm = null;
        updateSearchFilter.neighborhood_id = null;
      }
      return Object.assign({}, newLaneEventProperties, {
        search_filter: updateSearchFilter,
        search_map: updatedSearchMap,
        semantic: getSemanticBlock(EventNames.removeMultiRegion),
        clickstream_trigger: getClickstreamBlock(EventNames.removeMultiRegion),
        envelope: getEnvelopeBlock(EventNames.removeMultiRegion)
      });
    case EventNames.removeAllMultiRegion:
      userSearchTermsArray.splice(0);
      customRegionIdsArray.splice(0);
      regionTypesArray.splice(0);
      selectedRegionsArray.splice(0);
      updateSearchFilter.user_search_terms_txt = userSearchTermsArray;
      updateSearchFilter.custom_region_ids = customRegionIdsArray;
      updateSearchFilter.region_types = regionTypesArray;
      updateSearchFilter.city_nm = null;
      updateSearchFilter.city_id = null;
      updateSearchFilter.state_id = null;
      updateSearchFilter.state_cd = null;
      updateSearchFilter.zip_cd = null;
      updateSearchFilter.neighborhood_nm = null;
      updateSearchFilter.neighborhood_id = null;
      updatedSearchMap.selected_regions_txt = selectedRegionsArray;
      return Object.assign({}, newLaneEventProperties, {
        search_filter: updateSearchFilter,
        search_map: updatedSearchMap,
        semantic: getSemanticBlock(EventNames.removeAllMultiRegion),
        clickstream_trigger: getClickstreamBlock(EventNames.removeAllMultiRegion),
        envelope: getEnvelopeBlock(EventNames.removeAllMultiRegion)
      });
    default:
      return null;
  }
};
var getAddRegionEventData = function getAddRegionEventData(selection, newLaneEventProperties, isMultiregionSearch, isRegionInMultiRegionSelection, isPointRegionSelected) {
  if (isRegionInMultiRegionSelection === void 0) {
    isRegionInMultiRegionSelection = false;
  }
  var selectedRegionTxt = null;
  var updateSearchFilter = newLaneEventProperties.search_filter || {};
  var updatedSearchMap = newLaneEventProperties.search_map || {};
  var regionId = null;
  var regionType = null;
  var updatedNewLaneEventProperties = getEventSpecificData(selection, newLaneEventProperties);
  if (selection && (selection === null || selection === void 0 ? void 0 : selection.displayName)) {
    var _selection$displayNam, _updateSearchFilter$u;
    var displayName = (_selection$displayNam = selection === null || selection === void 0 ? void 0 : selection.displayName) !== null && _selection$displayNam !== void 0 ? _selection$displayNam : '';
    selectedRegionTxt = getModifiedRegionName(displayName);
    if (!((updateSearchFilter === null || updateSearchFilter === void 0 ? void 0 : updateSearchFilter.custom_region_ids) && (updateSearchFilter === null || updateSearchFilter === void 0 ? void 0 : updateSearchFilter.region_types)) && (updateSearchFilter === null || updateSearchFilter === void 0 ? void 0 : (_updateSearchFilter$u = updateSearchFilter.user_search_terms_txt) === null || _updateSearchFilter$u === void 0 ? void 0 : _updateSearchFilter$u.length) === 1) {
      updateSearchFilter.user_search_terms_txt = [];
      updateSearchFilter.school_id = null;
    }
    if (selection === null || selection === void 0 ? void 0 : selection.data) {
      var _selection$data, _selection$data2;
      regionId = selection === null || selection === void 0 ? void 0 : (_selection$data = selection.data) === null || _selection$data === void 0 ? void 0 : _selection$data.regionId;
      regionType = selection === null || selection === void 0 ? void 0 : (_selection$data2 = selection.data) === null || _selection$data2 === void 0 ? void 0 : _selection$data2.regionType;
      if (isPointRegionSelected) {
        updateSearchFilter.custom_region_ids = [];
        updateSearchFilter.region_types = [];
        updateSearchFilter.user_search_terms_txt = [];
      }
      if (!((updateSearchFilter === null || updateSearchFilter === void 0 ? void 0 : updateSearchFilter.custom_region_ids) && (updateSearchFilter === null || updateSearchFilter === void 0 ? void 0 : updateSearchFilter.region_types)) || isPointRegionSelected) {
        Object.entries(getRegionTypeFields(selection)).forEach(function (_ref5) {
          var _ref6 = _slicedToArray(_ref5, 2),
            key = _ref6[0],
            value = _ref6[1];
          updateSearchFilter[key] = value;
        });
      }
    }
    if (selection.category === 'CURRENT_LOCATION') {
      updateSearchFilter.user_search_terms_txt = [displayName];
      updateSearchFilter.custom_region_ids = [];
      updateSearchFilter.region_types = [];
      updateSearchFilter.city_id = null;
      updateSearchFilter.city_nm = null;
      updateSearchFilter.neighborhood_id = null;
      updateSearchFilter.neighborhood_nm = null;
      updateSearchFilter.state_cd = null;
      updateSearchFilter.state_id = null;
      updateSearchFilter.zip_cd = null;
      updatedSearchMap.selected_regions_txt = [];
    } else if (isMultiregionSearch) {
      if (isRegionInMultiRegionSelection) {
        return Object.assign({}, updatedNewLaneEventProperties);
      }
      updateSearchFilter.user_search_terms_txt = [].concat(_toConsumableArray((updateSearchFilter === null || updateSearchFilter === void 0 ? void 0 : updateSearchFilter.user_search_terms_txt) || []), [displayName]);
      if (!updateSearchFilter.custom_region_ids) {
        updateSearchFilter.custom_region_ids = [];
      }
      updateSearchFilter.custom_region_ids = [].concat(_toConsumableArray(updateSearchFilter.custom_region_ids), [regionId]);
      if (!updateSearchFilter.region_types) {
        updateSearchFilter.region_types = [];
      }
      updateSearchFilter.region_types = [].concat(_toConsumableArray(updateSearchFilter.region_types), [regionType]);
      if (selection.category !== 'RECENT_SEARCH' && (selection.category === 'REGION' || (selection === null || selection === void 0 ? void 0 : selection.subCategory) === 'REGION')) {
        updatedSearchMap.selected_regions_txt = [].concat(_toConsumableArray((updatedSearchMap === null || updatedSearchMap === void 0 ? void 0 : updatedSearchMap.selected_regions_txt) || []), [selectedRegionTxt]);
      }
    } else {
      updateSearchFilter.user_search_terms_txt = [displayName];
      updateSearchFilter.custom_region_ids = [regionId];
      updateSearchFilter.region_types = [regionType];
      if (selection.category !== 'RECENT_SEARCH' && (selection.category === 'REGION' || (selection === null || selection === void 0 ? void 0 : selection.subCategory) === 'REGION')) {
        updatedSearchMap.selected_regions_txt = [selectedRegionTxt];
      }
    }
    if (selection.category !== 'RECENT_SEARCH') {
      return Object.assign({}, updatedNewLaneEventProperties, {
        search_filter: updateSearchFilter,
        search_map: updatedSearchMap
      });
    }
    return Object.assign({}, updatedNewLaneEventProperties, {
      search_filter: updateSearchFilter
    });
  }
  return null;
};

/**
 * @returns {Object} - The new lane properties object.
 */
function getNewLaneProperties(eventName) {
  return {
    semantic: getSemanticBlock(eventName),
    clickstream_trigger: getClickstreamBlock(eventName),
    envelope: getEnvelopeBlock(eventName)
  };
}

/**
 *
 * @param {*} trackEventProp
 * @param {Suggestion | null} autocompleteResult
 * @param {number | null} [regionIndex]
 * @param {boolean} isMultiregionSearch
 * @param {boolean} isRegionInMultiRegionSelection
 * @param {string} eventName
 */
var fireTrackEvent = function fireTrackEvent(_ref7) {
  var trackEventProp = _ref7.trackEventProp,
    _ref7$autocompleteRes = _ref7.autocompleteResult,
    autocompleteResult = _ref7$autocompleteRes === void 0 ? null : _ref7$autocompleteRes,
    _ref7$regionIndex = _ref7.regionIndex,
    regionIndex = _ref7$regionIndex === void 0 ? null : _ref7$regionIndex,
    _ref7$isMultiregionSe = _ref7.isMultiregionSearch,
    isMultiregionSearch = _ref7$isMultiregionSe === void 0 ? false : _ref7$isMultiregionSe,
    _ref7$isRegionInMulti = _ref7.isRegionInMultiRegionSelection,
    isRegionInMultiRegionSelection = _ref7$isRegionInMulti === void 0 ? false : _ref7$isRegionInMulti,
    _ref7$eventName = _ref7.eventName,
    eventName = _ref7$eventName === void 0 ? '' : _ref7$eventName,
    _ref7$clickstreamTrig = _ref7.clickstreamTriggerOverrides,
    clickstreamTriggerOverrides = _ref7$clickstreamTrig === void 0 ? null : _ref7$clickstreamTrig,
    _ref7$clickstreamDeco = _ref7.clickstreamDecorators,
    clickstreamDecorators = _ref7$clickstreamDeco === void 0 ? null : _ref7$clickstreamDeco,
    _ref7$screenName = _ref7.screenName,
    screenName = _ref7$screenName === void 0 ? 'sxp' : _ref7$screenName,
    _ref7$allowEmptyIniti = _ref7.allowEmptyInitialClickstreamBlocks,
    allowEmptyInitialClickstreamBlocks = _ref7$allowEmptyIniti === void 0 ? false : _ref7$allowEmptyIniti,
    _ref7$isPointRegionSe = _ref7.isPointRegionSelected,
    isPointRegionSelected = _ref7$isPointRegionSe === void 0 ? false : _ref7$isPointRegionSe;
  yieldWrapper(function () {
    return new Promise(function ($return, $error) {
      var newLaneEventProperties, data;
      newLaneEventProperties = {};
      if (isInitialized()) {
        var $Try_1_Finally = function ($Try_1_Exit) {
          return function ($Try_1_Value) {
            try {
              if (data && !isEmpty(data)) {
                newLaneEventProperties = data;
              }
              if (allowEmptyInitialClickstreamBlocks || Object.keys(newLaneEventProperties).length) {
                if (eventName === EventNames.removeMultiRegion || eventName === EventNames.removeAllMultiRegion) {
                  newLaneEventProperties = getRemoveRegionEventData(eventName, newLaneEventProperties, regionIndex);
                } else if (eventName === EventNames.searchBoxFocus) {
                  newLaneEventProperties = Object.assign({}, newLaneEventProperties, getNewLaneProperties(eventName));
                } else {
                  newLaneEventProperties = getAddRegionEventData(autocompleteResult, newLaneEventProperties, isMultiregionSearch, isRegionInMultiRegionSelection, isPointRegionSelected);
                }
                if (clickstreamTriggerOverrides) {
                  newLaneEventProperties = Object.assign({}, newLaneEventProperties, {
                    clickstream_trigger: Object.assign({}, newLaneEventProperties.clickstream_trigger, clickstreamTriggerOverrides)
                  });
                }
                if (clickstreamDecorators) {
                  newLaneEventProperties = Object.assign({}, newLaneEventProperties, clickstreamDecorators);
                }
                track(trackEventProp, {
                  newLaneEvent: newLaneEventProperties
                });
              } else {
                track(trackEventProp);
              }
              return $Try_1_Exit && $Try_1_Exit.call(this, $Try_1_Value);
            } catch ($boundEx) {
              return $error($boundEx);
            }
          }.bind(this);
        }.bind(this);
        var $Try_1_Post = function () {
          try {
            return $If_5.call(this);
          } catch ($boundEx) {
            return $error($boundEx);
          }
        }.bind(this);
        var $Try_1_Catch = function ($exception_2) {
          try {
            throw $exception_2;
          } catch ($boundEx) {
            return $Try_1_Finally($error)($boundEx);
          }
        };
        try {
          if (!allowEmptyInitialClickstreamBlocks && screenName) {
            return Promise.resolve(datalayer.getAll({
              screenName: screenName
            })).then(function ($await_9) {
              try {
                data = $await_9;
                return $If_6.call(this);
              } catch ($boundEx) {
                return $Try_1_Catch($boundEx);
              }
            }.bind(this), $Try_1_Catch);
          }
          function $If_6() {
            return $Try_1_Finally($Try_1_Post)();
          }
          return $If_6.call(this);
        } catch ($exception_2) {
          $Try_1_Catch($exception_2);
        }
      } else {
        track(trackEventProp);
        return $If_5.call(this);
      }
      function $If_5() {
        return $return();
      }
    });
  });
};
var fireEvent = function fireEvent(parameters) {
  var eventName = parameters.eventName,
    _parameters$suggestio = parameters.suggestion,
    suggestion = _parameters$suggestio === void 0 ? null : _parameters$suggestio,
    _parameters$clickstre = parameters.clickstreamTriggerOverrides,
    clickstreamTriggerOverrides = _parameters$clickstre === void 0 ? null : _parameters$clickstre,
    _parameters$clickstre2 = parameters.clickstreamDecorators,
    clickstreamDecorators = _parameters$clickstre2 === void 0 ? null : _parameters$clickstre2,
    _parameters$screenNam = parameters.screenName,
    screenName = _parameters$screenNam === void 0 ? 'sxp' : _parameters$screenNam,
    _parameters$allowEmpt = parameters.allowEmptyInitialClickstreamBlocks,
    allowEmptyInitialClickstreamBlocks = _parameters$allowEmpt === void 0 ? false : _parameters$allowEmpt;
  yieldWrapper(function () {
    return new Promise(function ($return, $error) {
      var newLaneEventProperties, data;
      newLaneEventProperties = {};
      if (isInitialized()) {
        var $Try_3_Finally = function ($Try_3_Exit) {
          return function ($Try_3_Value) {
            try {
              if (data && !isEmpty(data)) {
                newLaneEventProperties = data;
              }
              if (allowEmptyInitialClickstreamBlocks || Object.keys(newLaneEventProperties).length) {
                newLaneEventProperties = Object.assign({}, newLaneEventProperties, {
                  semantic: getSemanticBlock(eventName),
                  clickstream_trigger: getClickstreamBlock(eventName),
                  envelope: getEnvelopeBlock(eventName)
                });
                if (suggestion && eventName === EventNames.queryUnderstandingResponse) {
                  newLaneEventProperties = Object.assign({}, newLaneEventProperties, {
                    search_filter: getUpdatedSearchFilterBlock(suggestion, newLaneEventProperties),
                    search_map: getUpdatedSearchMapBlock(suggestion, newLaneEventProperties)
                  });
                }
                if (clickstreamTriggerOverrides) {
                  newLaneEventProperties = Object.assign({}, newLaneEventProperties, {
                    clickstream_trigger: Object.assign({}, newLaneEventProperties.clickstream_trigger, clickstreamTriggerOverrides)
                  });
                }
                if (clickstreamDecorators) {
                  newLaneEventProperties = Object.assign({}, newLaneEventProperties, clickstreamDecorators);
                }
                event(newLaneEventProperties);
              }
              return $Try_3_Exit && $Try_3_Exit.call(this, $Try_3_Value);
            } catch ($boundEx) {
              return $error($boundEx);
            }
          }.bind(this);
        }.bind(this);
        var $Try_3_Post = function () {
          try {
            return $If_7.call(this);
          } catch ($boundEx) {
            return $error($boundEx);
          }
        }.bind(this);
        var $Try_3_Catch = function ($exception_4) {
          try {
            throw $exception_4;
          } catch ($boundEx) {
            return $Try_3_Finally($error)($boundEx);
          }
        };
        try {
          if (!allowEmptyInitialClickstreamBlocks && screenName) {
            return Promise.resolve(datalayer.getAll({
              screenName: screenName
            })).then(function ($await_10) {
              try {
                data = $await_10;
                return $If_8.call(this);
              } catch ($boundEx) {
                return $Try_3_Catch($boundEx);
              }
            }.bind(this), $Try_3_Catch);
          }
          function $If_8() {
            return $Try_3_Finally($Try_3_Post)();
          }
          return $If_8.call(this);
        } catch ($exception_4) {
          $Try_3_Catch($exception_4);
        }
      }
      function $If_7() {
        return $return();
      }
      return $If_7.call(this);
    });
  });
};

/* eslint-disable zillow/@typescript-eslint/consistent-type-imports */
var getLabelSuggestionIndex = function getLabelSuggestionIndex(_ref) {
  var chosenSuggestion = _ref.chosenSuggestion,
    chosenIndex = _ref.chosenIndex;
  return chosenSuggestion.category + " | " + chosenIndex;
};
var getLabelSuggestionIndexValue = function getLabelSuggestionIndexValue(_ref2) {
  var _chosenSuggestion$dis;
  var chosenSuggestion = _ref2.chosenSuggestion,
    chosenIndex = _ref2.chosenIndex,
    searchBoxValue = _ref2.searchBoxValue;
  return chosenSuggestion.category + " | " + chosenIndex + " | " + (searchBoxValue !== null && searchBoxValue !== void 0 ? searchBoxValue : '') + " | " + ((_chosenSuggestion$dis = chosenSuggestion.displayName) !== null && _chosenSuggestion$dis !== void 0 ? _chosenSuggestion$dis : '');
};

/**
 * Category-specific tracking label generation functions.
 * getSearchBoxClickLabel takes a config object with the following four attributes:
 * @param {Object} chosenSuggestion - the suggestion object that was chosen by the user
 * @param {Array} allSuggestions - the array of suggestions that were being shown to the user at time of selection
 * @param {Number} chosenIndex - the 1-based index of chosenSuggestion in allSuggestions
 * @param {String} searchBoxValue - the text that was typed into the search box at the time an option was chosen
 */
var trackingConfigsByCategory = {
  SAVED_SEARCH: {
    getSearchBoxClickLabel: function getSearchBoxClickLabel(_ref3) {
      var _chosenSuggestion$not;
      var chosenSuggestion = _ref3.chosenSuggestion,
        chosenIndex = _ref3.chosenIndex;
      return chosenSuggestion.category + " | " + chosenIndex + " | " + ((_chosenSuggestion$not = chosenSuggestion.notificationCount) !== null && _chosenSuggestion$not !== void 0 ? _chosenSuggestion$not : '');
    }
  },
  RECENT_SEARCH: {
    getSearchBoxClickLabel: function getSearchBoxClickLabel(_ref4) {
      var chosenSuggestion = _ref4.chosenSuggestion,
        chosenIndex = _ref4.chosenIndex,
        _ref4$allSuggestions = _ref4.allSuggestions,
        allSuggestions = _ref4$allSuggestions === void 0 ? [] : _ref4$allSuggestions;
      var isSuggestionRecentSearch = function isSuggestionRecentSearch(_ref5) {
        var category = _ref5.category;
        return category === 'RECENT_SEARCH';
      };
      var totalRecentSearches = allSuggestions.filter(isSuggestionRecentSearch).length;
      var firstRecentSearchIndex = findIndex(allSuggestions, isSuggestionRecentSearch);
      var chosenRecentSearchIndex = chosenIndex - firstRecentSearchIndex;
      return chosenSuggestion.category + " | " + chosenIndex + " | " + chosenRecentSearchIndex + " | " + totalRecentSearches;
    }
  },
  CLAIMED_HOME: {
    getSearchBoxClickLabel: getLabelSuggestionIndex
  },
  SAVED_HOME: {
    getSearchBoxClickLabel: getLabelSuggestionIndex
  },
  CURRENT_LOCATION: {
    getSearchBoxClickLabel: getLabelSuggestionIndex
  },
  REGION: {
    getSearchBoxClickLabel: getLabelSuggestionIndexValue
  },
  SEMANTIC: {
    getSearchBoxClickLabel: getLabelSuggestionIndexValue
  },
  SCHOOL: {
    getSearchBoxClickLabel: getLabelSuggestionIndexValue
  },
  ADDRESS: {
    getSearchBoxClickLabel: getLabelSuggestionIndexValue
  },
  RAW_SEARCH: {
    getSearchBoxClickLabel: function getSearchBoxClickLabel(_ref6) {
      var _chosenSuggestion$dis2;
      var chosenSuggestion = _ref6.chosenSuggestion,
        searchBoxValue = _ref6.searchBoxValue;
      return chosenSuggestion.category + " | " + (searchBoxValue !== null && searchBoxValue !== void 0 ? searchBoxValue : '') + " | " + ((_chosenSuggestion$dis2 = chosenSuggestion.displayName) !== null && _chosenSuggestion$dis2 !== void 0 ? _chosenSuggestion$dis2 : '');
    }
  },
  BUILDER_COMMUNITY: {
    getSearchBoxClickLabel: getLabelSuggestionIndexValue
  }
};

function _templateObject6() {
  var data = _taggedTemplateLiteralLoose(["\n    fragment SemanticResultFields on SearchAssistanceSemanticResult {\n        nearMe\n        regionIds\n        regionTypes\n        regionDisplayIds\n        queryResolutionStatus\n        viewLatitudeDelta\n        filters {\n            basementStatusType\n            baths {\n                min\n                max\n            }\n            beds {\n                min\n                max\n            }\n            excludeTypes\n            hoaFeesPerMonth {\n                min\n                max\n            }\n            homeType\n            keywords\n            listingStatusType\n            livingAreaSqft {\n                min\n                max\n            }\n            lotSizeSqft {\n                min\n                max\n            }\n            parkingSpots {\n                min\n                max\n            }\n            price {\n                min\n                max\n            }\n            searchRentalFilters {\n                monthlyPayment {\n                    min\n                    max\n                }\n                petsAllowed\n                rentalAvailabilityDate {\n                    min\n                    max\n                }\n            }\n            searchSaleFilters {\n                daysOnZillow {\n                    min\n                    max\n                }\n            }\n            showOnlyType\n            view\n            yearBuilt {\n                min\n                max\n            }\n        }\n    }\n"]);
  _templateObject6 = function _templateObject6() {
    return data;
  };
  return data;
}
function _templateObject5() {
  var data = _taggedTemplateLiteralLoose(["\n    fragment SchoolResultFields on SearchAssistanceSchoolResult {\n        id\n        schoolDistrictId\n        schoolId\n    }\n"]);
  _templateObject5 = function _templateObject5() {
    return data;
  };
  return data;
}
function _templateObject4() {
  var data = _taggedTemplateLiteralLoose(["\n    fragment BuilderCommunityResultFields on SearchAssistanceBuilderCommunityResult {\n        plid\n    }\n"]);
  _templateObject4 = function _templateObject4() {
    return data;
  };
  return data;
}
function _templateObject3() {
  var data = _taggedTemplateLiteralLoose(["\n    fragment RentalCommunityResultFields on SearchAssistanceRentalCommunityResult {\n        location {\n            latitude\n            longitude\n        }\n    }\n"]);
  _templateObject3 = function _templateObject3() {
    return data;
  };
  return data;
}
function _templateObject2$1() {
  var data = _taggedTemplateLiteralLoose(["\n    fragment RegionResultFields on SearchAssistanceRegionResult {\n        regionId\n        subType\n    }\n"]);
  _templateObject2$1 = function _templateObject2() {
    return data;
  };
  return data;
}
function _templateObject$5() {
  var data = _taggedTemplateLiteralLoose(["\n    fragment AddressResultFields on SearchAssistanceAddressResult {\n        zpid\n        addressSubType: subType\n        location {\n            latitude\n            longitude\n        }\n    }\n"]);
  _templateObject$5 = function _templateObject() {
    return data;
  };
  return data;
}
var ADDRESS_RESULT_FIELDS = gql(_templateObject$5());
var REGION_RESULT_FIELDS = gql(_templateObject2$1());
var RENTAL_COMMUNITY_RESULT_FIELDS = gql(_templateObject3());
var BUILDER_COMMUNITY_RESULT_FIELDS = gql(_templateObject4());
var SCHOOL_RESULT_FIELDS = gql(_templateObject5());
var SEMANTIC_RESULT_FIELDS = gql(_templateObject6());

function _templateObject$4() {
  var data = _taggedTemplateLiteralLoose(["\n    query getAutocompleteResults(\n        $query: String!\n        $queryOptions: SearchAssistanceQueryOptions\n        $resultType: [SearchAssistanceResultType]\n    ) {\n        searchAssistanceResult: zgsAutocompleteRequest(\n            query: $query\n            queryOptions: $queryOptions\n            resultType: $resultType\n        ) {\n            requestId\n            results {\n                __typename\n                id\n                ...RegionResultFields\n                ...SemanticResultFields\n                ...RentalCommunityResultFields\n                ...SchoolResultFields\n                ...BuilderCommunityResultFields\n            }\n        }\n    }\n    ", "\n    ", "\n    ", "\n    ", "\n    ", "\n"]);
  _templateObject$4 = function _templateObject() {
    return data;
  };
  return data;
}
var getAutocompleteResults = gql(_templateObject$4(), REGION_RESULT_FIELDS, SCHOOL_RESULT_FIELDS, SEMANTIC_RESULT_FIELDS, RENTAL_COMMUNITY_RESULT_FIELDS, BUILDER_COMMUNITY_RESULT_FIELDS);

/**
 * @typedef {import('./suggestion').Suggestion} Suggestion
 */

var defaultFetchSuggestionsCache = {};
var currentLocationSuggestion = {
  category: SuggestionCategory.currentLocation,
  displayName: 'Current Location'
};
var createRegionsFromSemanticResult = function createRegionsFromSemanticResult(result) {
  if (!result.regionIds || !result.regionTypes || !result.regionDisplayIds) {
    return [];
  }

  // We source the id, type, and display name from different arrays.
  // These arrays should be of equal length and sorted in the same order,
  // but we'll be safe and only iterate over the smallest of the three.
  var length = Math.min(result.regionIds.length, result.regionTypes.length, result.regionDisplayIds.length, MAX_REGION_SELECTION_LENGTH);
  var regions = [];
  for (var i = 0; i < length; i += 1) {
    var _result$regionTypes$i;
    regions.push({
      id: parseInt(result.regionIds[i], 10),
      type: (_result$regionTypes$i = result.regionTypes[i]) === null || _result$regionTypes$i === void 0 ? void 0 : _result$regionTypes$i.toLowerCase(),
      displayName: result.regionDisplayIds[i]
    });
  }
  return regions;
};
var zgsAutocompleteResultToSuggestion = function zgsAutocompleteResultToSuggestion(result) {
  var _result$location, _result$location2, _result$location3, _result$location4;
  switch (result.__typename) {
    case 'SearchAssistanceRegionResult':
      return {
        category: SuggestionCategory.region,
        displayName: result.id,
        data: {
          regionId: result.regionId,
          regionType: result.subType.toLowerCase()
        }
      };
    case 'SearchAssistanceSemanticResult':
      return {
        category: SuggestionCategory.semantic,
        displayName: result.id,
        data: {
          nearMeSearch: result.nearMe,
          filters: result.filters,
          regions: createRegionsFromSemanticResult(result),
          queryResolutionStatus: result.queryResolutionStatus,
          viewLatitudeDelta: result.viewLatitudeDelta
        }
      };
    case 'SearchAssistanceAddressResult':
      return {
        category: SuggestionCategory.address,
        displayName: result.id,
        data: {
          zpid: result.zpid,
          addressType: result.addressSubType,
          lat: (_result$location = result.location) === null || _result$location === void 0 ? void 0 : _result$location.latitude,
          lng: (_result$location2 = result.location) === null || _result$location2 === void 0 ? void 0 : _result$location2.longitude
        }
      };
    case 'SearchAssistanceRentalCommunityResult':
      return {
        category: SuggestionCategory.address,
        displayName: result.id,
        data: {
          addressType: 'community_address',
          lat: (_result$location3 = result.location) === null || _result$location3 === void 0 ? void 0 : _result$location3.latitude,
          lng: (_result$location4 = result.location) === null || _result$location4 === void 0 ? void 0 : _result$location4.longitude
        }
      };
    case 'SearchAssistanceSchoolResult':
      return {
        category: SuggestionCategory.school,
        displayName: result.id,
        data: {
          schoolId: result.schoolId,
          schoolDistrictId: result.schoolDistrictId
        }
      };
    case 'SearchAssistanceBuilderCommunityResult':
      return {
        category: SuggestionCategory.builderCommunity,
        displayName: result.id,
        data: {
          plid: result.plid
        }
      };
    default:
      return null;
  }
};
var getViewPort = function getViewPort(mapBounds) {
  return {
    minX: mapBounds === null || mapBounds === void 0 ? void 0 : mapBounds.south,
    maxX: mapBounds === null || mapBounds === void 0 ? void 0 : mapBounds.north,
    minY: mapBounds === null || mapBounds === void 0 ? void 0 : mapBounds.west,
    maxY: mapBounds === null || mapBounds === void 0 ? void 0 : mapBounds.east
  };
};

/**
 * Fetch suggestions from Zillow Graph
 * @param {string} query
 * @param {Object} autocompleteConfig
 * @returns {Promise<{ suggestions: Suggestion[]; requestId?: string; requestError?: Error }>}
 */
var fetchZgGraphSuggestions = function fetchZgGraphSuggestions(query, _ref) {
  return new Promise(function ($return, $error) {
    var zgGraphClient, graphqlQuery, autocompleteConfig, _ref$extraVariables, extraVariables, _ref$mapBounds, mapBounds, _data$searchAssistanc, _data$searchAssistanc2, _data$searchAssistanc3, abKey, userSearchContext, centroid, fetchLimit, resultTypes, variables, _await$zgGraphClient$, data, errors;
    zgGraphClient = _ref.zgGraphClient, graphqlQuery = _ref.graphqlQuery, autocompleteConfig = _ref.autocompleteConfig, _ref$extraVariables = _ref.extraVariables, extraVariables = _ref$extraVariables === void 0 ? {} : _ref$extraVariables, _ref$mapBounds = _ref.mapBounds, mapBounds = _ref$mapBounds === void 0 ? null : _ref$mapBounds;
    var $Try_1_Catch = function (err) {
      try {
        console.error(err);
        if (Array.isArray(err.cause)) {
          err.cause.forEach(function (_ref2) {
            var message = _ref2.message;
            if (message) {
              console.error(message);
            }
          });
        }
        return $return({
          suggestions: [],
          requestError: err
        });
      } catch ($boundEx) {
        return $error($boundEx);
      }
    };
    try {
      abKey = autocompleteConfig.abKey, userSearchContext = autocompleteConfig.userSearchContext, centroid = autocompleteConfig.centroid, fetchLimit = autocompleteConfig.fetchLimit, resultTypes = autocompleteConfig.resultTypes;
      variables = Object.assign({
        query: query,
        queryOptions: {
          maxResults: fetchLimit,
          userIdentity: {
            abKey: abKey
          },
          userSearchContext: userSearchContext
        },
        resultType: resultTypes
      }, extraVariables);
      if (mapBounds) {
        variables.queryOptions.viewPort = getViewPort(mapBounds);
      }
      if ((centroid === null || centroid === void 0 ? void 0 : centroid.lat) && (centroid === null || centroid === void 0 ? void 0 : centroid.lon)) {
        variables.queryOptions.userLocation = {
          latitude: centroid.lat,
          longitude: centroid.lon
        };
      }
      return Promise.resolve(zgGraphClient.query({
        query: graphqlQuery,
        variables: variables
      })).then(function ($await_2) {
        try {
          _await$zgGraphClient$ = $await_2, data = _await$zgGraphClient$.data, errors = _await$zgGraphClient$.errors;
          if (!data) {
            throw new Error('Error(s) encountered querying search-assistance-zillow', {
              cause: errors
            });
          }
          return $return({
            suggestions: (_data$searchAssistanc = data === null || data === void 0 ? void 0 : (_data$searchAssistanc2 = data.searchAssistanceResult) === null || _data$searchAssistanc2 === void 0 ? void 0 : _data$searchAssistanc2.results.map(zgsAutocompleteResultToSuggestion).filter(Boolean)) !== null && _data$searchAssistanc !== void 0 ? _data$searchAssistanc : [],
            requestId: data === null || data === void 0 ? void 0 : (_data$searchAssistanc3 = data.searchAssistanceResult) === null || _data$searchAssistanc3 === void 0 ? void 0 : _data$searchAssistanc3.requestId
          });
        } catch ($boundEx) {
          return $Try_1_Catch($boundEx);
        }
      }, $Try_1_Catch);
    } catch (err) {
      $Try_1_Catch(err);
    }
  });
};

/**
 * Fetches locally stored/saved searches.
 * @param {String} query - search text
 * @param {Object} autocompleteManager - instance of AutocompleteManager to use
 * @param {Object} autocompleteConfig - config options for autocomplete
 * @return {Promise} local search autocomplete suggestions
 */
function fetchLocalSuggestions(query, autocompleteManager, autocompleteConfig, disableCurrentLocation) {
  return autocompleteManager.fetchResults(query, [FetchTypes.RECENT_SEARCH, FetchTypes.SAVED_HOMES, FetchTypes.SAVED_SEARCH], autocompleteConfig).then(function (results) {
    if (disableCurrentLocation) {
      return results;
    }
    return [currentLocationSuggestion].concat(_toConsumableArray(results));
  });
}

/**
 * Fetches autocomplete suggestions
 * @param {String} query - search query suggestions are based off
 * @param {{ latitude?: number; longitude?: number }} location - { latitude, longitude } - override location for autocompleteConfig.centroid
 * @param {Object} autocompleteConfig - autocomplete config
 * @param {String} reason - the reason a fetch is being requested
 * @param {Number} [minAutocompleteChars] - minimum number of characters to trigger autocomplete
 * @param {Object} [zgGraphClient] - zgGraphClient instance
 * @return {Promise} suggestions promise
 */
function defaultFetchSuggestions(query, _ref3, autocompleteConfig, reason, minAutocompleteChars, zgGraphClient, mapBounds) {
  return new Promise(function ($return, $error) {
    var _defaultFetchSuggesti, latitude, longitude, disableCurrentLocation, updatedAutocompleteConfig, cacheKey, keyedFetchedSuggestionsCache, returnLocalSuggestions, getLocalSuggestions, zgGraphAutocompleteConfig, getZgGraphPromise, _await$Promise$all, _await$Promise$all2, zgGraphSuggestions, localSuggestions;
    latitude = _ref3.latitude, longitude = _ref3.longitude;
    if (minAutocompleteChars === void 0) {
      minAutocompleteChars = MIN_AUTOCOMPLETE_CHARS;
    }
    disableCurrentLocation = autocompleteConfig.disableCurrentLocation;
    updatedAutocompleteConfig = Object.assign({}, autocompleteConfig);
    cacheKey = autocompleteConfig.clientId || 'default-key';
    defaultFetchSuggestionsCache[cacheKey] = (_defaultFetchSuggesti = defaultFetchSuggestionsCache[cacheKey]) !== null && _defaultFetchSuggesti !== void 0 ? _defaultFetchSuggesti : {};
    keyedFetchedSuggestionsCache = defaultFetchSuggestionsCache[cacheKey];
    // apply location override if a latitude/longitude is passed in
    if (typeof latitude !== 'undefined' && typeof longitude !== 'undefined') {
      updatedAutocompleteConfig.centroid = {
        lat: latitude,
        lon: longitude
      };
    }
    // we have to reset autocomplete manager here to retrieve user personalization data
    // this would happen if the user started the session anonymously, then signed in later.
    if (reason === INIT_LOCAL_RESULTS) {
      keyedFetchedSuggestionsCache.autocompleteManager = null;
    }

    // if we have a new location context, create a new AutocompleteManager with updated centroid
    if (updatedAutocompleteConfig.centroid && (updatedAutocompleteConfig.centroid.lat !== keyedFetchedSuggestionsCache.latitude || updatedAutocompleteConfig.centroid.lon !== keyedFetchedSuggestionsCache.longitude)) {
      keyedFetchedSuggestionsCache.latitude = updatedAutocompleteConfig.centroid.lat;
      keyedFetchedSuggestionsCache.longitude = updatedAutocompleteConfig.centroid.lon;
      keyedFetchedSuggestionsCache.autocompleteManager = null;
    }
    if (!keyedFetchedSuggestionsCache.autocompleteManager) {
      keyedFetchedSuggestionsCache.autocompleteManager = new AutocompleteManager(updatedAutocompleteConfig);
    }
    returnLocalSuggestions = query.length < minAutocompleteChars || reason === 'input-focused' || reason === INIT_LOCAL_RESULTS;
    getLocalSuggestions = function getLocalSuggestions() {
      return fetchLocalSuggestions('', keyedFetchedSuggestionsCache.autocompleteManager, updatedAutocompleteConfig, disableCurrentLocation);
    };
    zgGraphAutocompleteConfig = Object.assign({}, updatedAutocompleteConfig, {
      fetchLimit: returnLocalSuggestions ? DEFAULT_MAX_PRE_TYPE_SEMANTIC : DEFAULT_MAX_POST_TYPE_SEMANTIC
    });
    getZgGraphPromise = function getZgGraphPromise() {
      var options = {
        zgGraphClient: zgGraphClient,
        graphqlQuery: getAutocompleteResults,
        autocompleteConfig: zgGraphAutocompleteConfig,
        mapBounds: mapBounds
      };
      if (autocompleteConfig.resultTypes.includes('SEMANTIC_REGIONS')) {
        return fetchZgGraphSuggestions(returnLocalSuggestions ? '' : query, options);
      }
      if (returnLocalSuggestions) {
        return Promise.resolve({
          suggestions: []
        });
      }
      return fetchZgGraphSuggestions(query, options);
    };
    return Promise.resolve(Promise.all([getZgGraphPromise(), returnLocalSuggestions ? getLocalSuggestions() : Promise.resolve([])])).then(function ($await_3) {
      try {
        _await$Promise$all = $await_3, _await$Promise$all2 = _slicedToArray(_await$Promise$all, 2), zgGraphSuggestions = _await$Promise$all2[0], localSuggestions = _await$Promise$all2[1];
        return $return({
          suggestions: [].concat(_toConsumableArray(localSuggestions), _toConsumableArray(zgGraphSuggestions.suggestions)),
          requestId: zgGraphSuggestions.requestId,
          isLocalQuery: returnLocalSuggestions
        });
      } catch ($boundEx) {
        return $error($boundEx);
      }
    }, $error);
  });
}
var filterRecentSearches = function filterRecentSearches(results) {
  var getIsValidSubCategory = function getIsValidSubCategory(subCategory) {
    return [SuggestionCategory.semantic, SuggestionCategory.region, SuggestionCategory.address, SuggestionCategory.builderCommunity].includes(subCategory);
  };
  return results.filter(function (_ref4) {
    var category = _ref4.category,
      subCategory = _ref4.subCategory;
    return category === SuggestionCategory.recentSearch && getIsValidSubCategory(subCategory);
  });
};

/**
 * Default function for filtering autocomplete suggestions. Enforces max limits for various result types
 * @param {Suggestion[]} suggestions - Array of suggestions to filter
 * @return {Suggestion[]} filtered set of suggestions
 */
function defaultFilterSuggestions(suggestions) {
  var filteredCurrentLocation = suggestions.filter(function (suggestion) {
    return suggestion.category === SuggestionCategory.currentLocation;
  });
  var filteredSavedSearches = suggestions.filter(function (suggestion) {
    return suggestion.category === SuggestionCategory.savedSearch;
  });
  filteredSavedSearches.splice(DEFAULT_MAX_SAVED_SEARCHES);
  var filteredClaimedHomes = suggestions.filter(function (suggestion) {
    return suggestion.category === SuggestionCategory.claimedHome;
  });
  filteredClaimedHomes.splice(DEFAULT_MAX_CLAIMED_HOMES);
  var filteredSavedHomes = suggestions.filter(function (suggestion) {
    return suggestion.category === SuggestionCategory.savedHome;
  });
  filteredSavedHomes.splice(DEFAULT_MAX_SAVED_HOMES);
  var filteredRecentSearches = filterRecentSearches(suggestions);
  filteredRecentSearches.splice(DEFAULT_MAX_RECENT_SEARCHES);
  var filteredRegionAddress = suggestions.filter(function (suggestion) {
    return [SuggestionCategory.region, SuggestionCategory.address, SuggestionCategory.school, SuggestionCategory.semantic, SuggestionCategory.builderCommunity].indexOf(suggestion.category) >= 0;
  });
  filteredRegionAddress.splice(DEFAULT_MAX_REGION_ADDRESS);
  return [].concat(filteredCurrentLocation, filteredSavedSearches, filteredClaimedHomes, filteredSavedHomes, filteredRecentSearches, filteredRegionAddress);
}

/**
 * Extracts search value from selected suggestion.
 * @param {Suggestion} suggestion
 *     @param {String} suggestion.displayName - value of fetched suggestion
 * @return {String} suggestion value
 */
function getSuggestionValue(_ref5) {
  var category = _ref5.category,
    displayName = _ref5.displayName;
  if ([SuggestionCategory.region, SuggestionCategory.school, SuggestionCategory.semantic, SuggestionCategory.address, SuggestionCategory.recentSearch, SuggestionCategory.rawSearch, SuggestionCategory.claimedHome, SuggestionCategory.savedHome].indexOf(category) >= 0) {
    return displayName;
  }
  return '';
}

var defaultPlatformConfig = {
  DESKTOP: {
    placeholderText: 'Enter an address, neighborhood, city, or ZIP code'
  },
  DESKTOP_RESULTS_PAGE: {
    placeholderText: 'Address, neighborhood, city, ZIP'
  },
  MOBILE: {
    placeholderText: 'Address, neighborhood, city, ZIP'
  },
  MOBILE_RESULTS_PAGE: {
    placeholderText: 'Address, neighborhood, city, ZIP'
  }
};

var _DOZ_MAP;
/* eslint-disable zillow/@typescript-eslint/consistent-type-imports, zillow/@typescript-eslint/naming-convention */

var FOR_SALE_LISTING_STATUS_TYPES = ['FOR_SALE', 'FSBA', 'FSBO', 'AUCTION', 'COMINGSOON', 'FORECLOSURE', 'NEWCONSTRUCTION'];
var SHOW_ONLY_MAP = {
  SINGLE_STORY: 'singleStory',
  INCOME_RESTRICTED: 'onlyRentalIncomeRestricted',
  INUNIT_LAUNDRY: 'onlyRentalInUnitLaundry',
  ASSIGNED_PARKING: 'onlyRentalParkingAvailable',
  OPEN_HOUSES: 'isOpenHousesOnly',
  WATER_FRONT: 'isWaterfront',
  AIRCONDITIONING: 'hasAirConditioning',
  POOL: 'hasPool',
  ACCEPTS_RENTAL_APPLICATIONS: 'onlyRentalAcceptsApplications',
  GARAGE: 'hasGarage',
  APPROVED_VIRTUALTOUR: 'is3dHome'
};
var BASEMENT_MAP = {
  FINISHED: 'isBasementFinished',
  UNFINISHED: 'isBasementUnfinished'
};
var HOME_TYPE_MAP = {
  TOWNHOME: 'isTownhouse',
  LAND: 'isLotLand',
  MANUFACTURED: 'isManufactured',
  MULTIFAMILY: 'isMultiFamily',
  APARTMENT: 'isApartment',
  CONDO: 'isCondo',
  SINGLEFAMILY: 'isSingleFamily'
};
var LISTING_STATUS_MAP = {
  RECENTLY_SOLD: 'isRecentlySold',
  SOLD: 'isRecentlySold',
  PREFORECLOSURE: 'isPreMarketPreForeclosure',
  FORECLOSED: 'isPreMarketForeclosure',
  FOR_RENT: 'isForRent',
  FSBA: 'isForSaleByAgent',
  FSBO: 'isForSaleByOwner',
  AUCTION: 'isAuction',
  COMINGSOON: 'isComingSoon',
  FORECLOSURE: 'isForSaleForeclosure',
  NEWCONSTRUCTION: 'isNewConstruction'
};
var PETS_ALLOWED_MAP = {
  LARGEDOGS: 'onlyRentalLargeDogsAllowed',
  SMALLDOGS: 'onlyRentalSmallDogsAllowed',
  CATS: 'onlyRentalCatsAllowed'
};
var VIEW_MAP = {
  CITY: 'isCityView',
  PARK: 'isParkView',
  MOUNTAIN: 'isMountainView',
  WATER: 'isWaterView'
};
var ONE_DAY = 1;
var ONE_WEEK = 7;
var TWO_WEEKS = 14;
var ONE_MONTH = 30;
var THREE_MONTHS = 90;
var SIX_MONTHS = 182;
var ONE_YEAR = 365;
var TWO_YEARS = 730;
var THREE_YEARS = 1095;
var DOZ_MAP = (_DOZ_MAP = {}, _DOZ_MAP[ONE_DAY] = '1', _DOZ_MAP[ONE_WEEK] = '7', _DOZ_MAP[TWO_WEEKS] = '14', _DOZ_MAP[ONE_MONTH] = '30', _DOZ_MAP[THREE_MONTHS] = '90', _DOZ_MAP[SIX_MONTHS] = '6m', _DOZ_MAP[ONE_YEAR] = '12m', _DOZ_MAP[TWO_YEARS] = '24m', _DOZ_MAP[THREE_YEARS] = '36m', _DOZ_MAP);
var ONE_FOURTH_ACRE = 10890;
var HALF_ACRE = 21780;
var ONE_ACRE = 43560;
var TWO_ACRES = 87120;
var FIVE_ACRES = 217800;
var TEN_ACRES = 435600;
var TWENTY_ACRES = 871200;
var FIFTY_ACRES = 2178000;
var HUNDRED_ACRES = 4356000;

/**
 * Function the maps a QU search filter object to a corresponding search filter state object
 */

/**
 * Function the maps a QU search filter object to a corresponding search filter block object sent in Clickstream events
 */

/**
 * Function that transforms an incoming QU search filter object to a new QU search filter object.
 * Used to transform a value before mapping it to a search filter state object
 */

// eslint-disable-next-line zillow/@typescript-eslint/no-type-alias

var roundToNearest = function roundToNearest(num, multiple, type) {
  var func = type === 'up' ? Math.ceil : Math.floor;
  return func(num / multiple) * multiple;
};
var getValueBounds = function getValueBounds(value, bounds) {
  var _bounds$find;
  return (_bounds$find = bounds.find(function (_ref) {
    var _ref2 = _slicedToArray(_ref, 2),
      start = _ref2[0],
      end = _ref2[1];
    return start <= value && value <= end;
  })) !== null && _bounds$find !== void 0 ? _bounds$find : [];
};
var getAreaValue = function getAreaValue(value, bounds, type) {
  var min = bounds[0][0];
  var max = bounds[bounds.length - 1][1];
  if (value < min) {
    return type === 'min' ? null : min;
  }
  if (value > max) {
    return type === 'min' ? max : null;
  }
  var _getValueBounds = getValueBounds(value, bounds),
    _getValueBounds2 = _slicedToArray(_getValueBounds, 2),
    start = _getValueBounds2[0],
    end = _getValueBounds2[1];
  if (value === start || value === end) {
    return value;
  }
  return type === 'min' ? start : end;
};

/**
 * @param map - map of possible QU SearchFilter values to their corresponding filter ids
 * @returns SearchFilterState object with all filters set to false
 */
var getInitialSearchFilterState = function getInitialSearchFilterState(map) {
  return Object.values(map).reduce(function (acc, filterId) {
    acc[filterId] = {
      value: false
    };
    return acc;
  }, {});
};

/**
 * @param values - list of QU SearchFilter values
 * @param map - map of possible QU SearchFilter values to their corresponding filter ids
 * @returns SearchFilterState object with filters present in values set to true
 */
var getSearchFilterState = function getSearchFilterState(values, map) {
  return values.reduce(function (acc, searchFilterValue) {
    if (searchFilterValue && searchFilterValue in map) {
      var filterId = map[searchFilterValue];
      acc[filterId] = {
        value: true
      };
      return acc;
    }
    return acc;
  }, {});
};

/**
 * @param map - map of possible QU SearchFilter values to their corresponding SearchFilterBlock block fields
 * @returns SearchFilterBlock object with all filters set to false
 */
var getInitialSearchFilterBlock = function getInitialSearchFilterBlock(map) {
  return Object.values(map).reduce(function (acc, filterId) {
    acc[filterId] = false;
    return acc;
  }, {});
};

/**
 * @param values - list of QU SearchFilter values
 * @param map - map of possible QU SearchFilter values to their corresponding SearchFilterBlock block fields
 * @returns SearchFilterBlock object with filters present in values set to true
 */
var getSearchFilterBlock = function getSearchFilterBlock(values, map) {
  return values.reduce(function (acc, searchFilterValue) {
    if (searchFilterValue && searchFilterValue in map) {
      var filterId = map[searchFilterValue];
      acc[filterId] = true;
      return acc;
    }
    return acc;
  }, {});
};

/* eslint-disable zillow/@typescript-eslint/naming-convention */

var bathsMapper = {
  toSearchFilterState: function toSearchFilterState(baths) {
    if (baths === null || baths === void 0 ? void 0 : baths.min) {
      return {
        baths: {
          min: baths.min,
          max: null
        }
      };
    }
    return {};
  },
  toSearchFilterBlock: function toSearchFilterBlock(baths) {
    var _baths$min;
    return {
      bath_cnt_txt: baths === null || baths === void 0 ? void 0 : (_baths$min = baths.min) === null || _baths$min === void 0 ? void 0 : _baths$min.toString()
    };
  }
};
var bedsMapper = {
  toSearchFilterState: function toSearchFilterState(beds) {
    if (beds) {
      var _beds$min = beds.min,
        min = _beds$min === void 0 ? null : _beds$min,
        _beds$max = beds.max,
        max = _beds$max === void 0 ? null : _beds$max;
      return {
        beds: {
          min: min,
          max: max
        }
      };
    }
    return {};
  },
  toSearchFilterBlock: function toSearchFilterBlock(beds) {
    var _beds$min2, _beds$max2;
    return {
      bed_min_cnt_txt: beds === null || beds === void 0 ? void 0 : (_beds$min2 = beds.min) === null || _beds$min2 === void 0 ? void 0 : _beds$min2.toString(),
      bed_max_cnt_txt: beds === null || beds === void 0 ? void 0 : (_beds$max2 = beds.max) === null || _beds$max2 === void 0 ? void 0 : _beds$max2.toString()
    };
  }
};
var hoaFeesPerMonthMapper = {
  transform: function transform(hoaFeesPerMonth) {
    if (typeof (hoaFeesPerMonth === null || hoaFeesPerMonth === void 0 ? void 0 : hoaFeesPerMonth.max) !== 'number') {
      return hoaFeesPerMonth;
    }
    if (hoaFeesPerMonth.max > 1000) {
      return {
        max: null
      };
    }
    if (hoaFeesPerMonth.max < 75) {
      return {
        max: roundToNearest(hoaFeesPerMonth.max, 50, 'up')
      };
    }
    return {
      max: roundToNearest(hoaFeesPerMonth.max, 100, 'up')
    };
  },
  toSearchFilterState: function toSearchFilterState(hoaFeesPerMonth) {
    if (typeof (hoaFeesPerMonth === null || hoaFeesPerMonth === void 0 ? void 0 : hoaFeesPerMonth.max) === 'number' || (hoaFeesPerMonth === null || hoaFeesPerMonth === void 0 ? void 0 : hoaFeesPerMonth.max) === null) {
      var max = hoaFeesPerMonth.max;
      return {
        hoa: {
          min: null,
          max: max
        }
      };
    }
    return {};
  },
  toSearchFilterBlock: function toSearchFilterBlock(hoaFeesPerMonth) {
    var _hoaFeesPerMonth$max;
    return {
      hoa_max_amt: (_hoaFeesPerMonth$max = hoaFeesPerMonth === null || hoaFeesPerMonth === void 0 ? void 0 : hoaFeesPerMonth.max) !== null && _hoaFeesPerMonth$max !== void 0 ? _hoaFeesPerMonth$max : undefined
    };
  }
};
var livingAreaSqftMapper = {
  transform: function transform(livingAreaSqft) {
    if (livingAreaSqft) {
      var min = livingAreaSqft.min,
        max = livingAreaSqft.max;
      var bounds = [[500, 750], [750, 1000], [1000, 1250], [1250, 1500], [1500, 1750], [1750, 2000], [2000, 2250], [2250, 2500], [2500, 2750], [2750, 3000], [3000, 3500], [3500, 4000], [4000, 5000], [5000, 7500]];
      return {
        min: min ? getAreaValue(min, bounds, 'min') : null,
        max: max ? getAreaValue(max, bounds, 'max') : null
      };
    }
    return livingAreaSqft;
  },
  toSearchFilterState: function toSearchFilterState(livingAreaSqft) {
    if (livingAreaSqft) {
      var _livingAreaSqft$min = livingAreaSqft.min,
        min = _livingAreaSqft$min === void 0 ? null : _livingAreaSqft$min,
        _livingAreaSqft$max = livingAreaSqft.max,
        max = _livingAreaSqft$max === void 0 ? null : _livingAreaSqft$max;
      return {
        sqft: {
          min: min,
          max: max
        }
      };
    }
    return {};
  },
  toSearchFilterBlock: function toSearchFilterBlock(livingAreaSqft) {
    var _livingAreaSqft$min2, _livingAreaSqft$max2;
    return {
      sqft_min_nb: (_livingAreaSqft$min2 = livingAreaSqft === null || livingAreaSqft === void 0 ? void 0 : livingAreaSqft.min) !== null && _livingAreaSqft$min2 !== void 0 ? _livingAreaSqft$min2 : undefined,
      sqft_max_nb: (_livingAreaSqft$max2 = livingAreaSqft === null || livingAreaSqft === void 0 ? void 0 : livingAreaSqft.max) !== null && _livingAreaSqft$max2 !== void 0 ? _livingAreaSqft$max2 : undefined
    };
  }
};
var lotSizeSqftMapper = {
  transform: function transform(lotSizeSqft) {
    if (lotSizeSqft) {
      var min = lotSizeSqft.min,
        max = lotSizeSqft.max;
      var bounds = [[1000, 2000], [2000, 3000], [4000, 5000], [5000, 7500], [7500, ONE_FOURTH_ACRE], [ONE_FOURTH_ACRE, HALF_ACRE], [HALF_ACRE, ONE_ACRE], [ONE_ACRE, TWO_ACRES], [TWO_ACRES, FIVE_ACRES], [FIVE_ACRES, TEN_ACRES], [TEN_ACRES, TWENTY_ACRES], [TWENTY_ACRES, FIFTY_ACRES], [FIFTY_ACRES, HUNDRED_ACRES]];
      return {
        min: min ? getAreaValue(min, bounds, 'min') : null,
        max: max ? getAreaValue(max, bounds, 'max') : null
      };
    }
    return lotSizeSqft;
  },
  toSearchFilterState: function toSearchFilterState(lotSizeSqft) {
    if (lotSizeSqft) {
      var _lotSizeSqft$min = lotSizeSqft.min,
        min = _lotSizeSqft$min === void 0 ? null : _lotSizeSqft$min,
        _lotSizeSqft$max = lotSizeSqft.max,
        max = _lotSizeSqft$max === void 0 ? null : _lotSizeSqft$max;
      return {
        lotSize: {
          min: min,
          max: max
        }
      };
    }
    return {};
  },
  toSearchFilterBlock: function toSearchFilterBlock(lotSizeSqft) {
    var _lotSizeSqft$min2, _lotSizeSqft$max2;
    return {
      lot_size_sqft_min_nb: (_lotSizeSqft$min2 = lotSizeSqft === null || lotSizeSqft === void 0 ? void 0 : lotSizeSqft.min) !== null && _lotSizeSqft$min2 !== void 0 ? _lotSizeSqft$min2 : undefined,
      lot_size_sqft_max_nb: (_lotSizeSqft$max2 = lotSizeSqft === null || lotSizeSqft === void 0 ? void 0 : lotSizeSqft.max) !== null && _lotSizeSqft$max2 !== void 0 ? _lotSizeSqft$max2 : undefined
    };
  }
};
var parkingSpotsMapper = {
  toSearchFilterState: function toSearchFilterState(parkingSpots) {
    if (parkingSpots === null || parkingSpots === void 0 ? void 0 : parkingSpots.min) {
      return {
        parkingSpots: {
          min: parkingSpots.min,
          max: null
        }
      };
    }
    return {};
  },
  toSearchFilterBlock: function toSearchFilterBlock(parkingSpots) {
    var _parkingSpots$min;
    return {
      parking_spot_txt: parkingSpots === null || parkingSpots === void 0 ? void 0 : (_parkingSpots$min = parkingSpots.min) === null || _parkingSpots$min === void 0 ? void 0 : _parkingSpots$min.toString()
    };
  }
};
var priceMapper = {
  toSearchFilterState: function toSearchFilterState(price) {
    if (price) {
      var _price$min = price.min,
        min = _price$min === void 0 ? null : _price$min,
        _price$max = price.max,
        max = _price$max === void 0 ? null : _price$max;
      return {
        price: {
          min: min,
          max: max
        }
      };
    }
    return {};
  },
  toSearchFilterBlock: function toSearchFilterBlock(price) {
    var _price$min2, _price$max2;
    return {
      price_range_min_amt: (_price$min2 = price === null || price === void 0 ? void 0 : price.min) !== null && _price$min2 !== void 0 ? _price$min2 : undefined,
      price_range_max_amt: (_price$max2 = price === null || price === void 0 ? void 0 : price.max) !== null && _price$max2 !== void 0 ? _price$max2 : undefined
    };
  }
};
var yearBuiltMapper = {
  toSearchFilterState: function toSearchFilterState(yearBuilt) {
    if (yearBuilt) {
      var _yearBuilt$min = yearBuilt.min,
        min = _yearBuilt$min === void 0 ? null : _yearBuilt$min,
        _yearBuilt$max = yearBuilt.max,
        max = _yearBuilt$max === void 0 ? null : _yearBuilt$max;
      return {
        built: {
          min: min,
          max: max
        }
      };
    }
    return {};
  },
  toSearchFilterBlock: function toSearchFilterBlock(yearBuilt) {
    var _yearBuilt$min2, _yearBuilt$max2;
    return {
      built_year_min_nb: (_yearBuilt$min2 = yearBuilt === null || yearBuilt === void 0 ? void 0 : yearBuilt.min) !== null && _yearBuilt$min2 !== void 0 ? _yearBuilt$min2 : undefined,
      built_year_max_nb: (_yearBuilt$max2 = yearBuilt === null || yearBuilt === void 0 ? void 0 : yearBuilt.max) !== null && _yearBuilt$max2 !== void 0 ? _yearBuilt$max2 : undefined
    };
  }
};
var searchRentalFiltersMapper = {
  toSearchFilterState: function toSearchFilterState(searchRentalFilters) {
    var _searchRentalFilters$4;
    // eslint-disable-next-line zillow/@typescript-eslint/init-declarations
    var searchFilterState = {};
    if (searchRentalFilters === null || searchRentalFilters === void 0 ? void 0 : searchRentalFilters.monthlyPayment) {
      var _searchRentalFilters$ = searchRentalFilters.monthlyPayment,
        _searchRentalFilters$2 = _searchRentalFilters$.min,
        min = _searchRentalFilters$2 === void 0 ? null : _searchRentalFilters$2,
        _searchRentalFilters$3 = _searchRentalFilters$.max,
        max = _searchRentalFilters$3 === void 0 ? null : _searchRentalFilters$3;
      searchFilterState.monthlyPayment = {
        min: min,
        max: max
      };
    }
    if (searchRentalFilters === null || searchRentalFilters === void 0 ? void 0 : (_searchRentalFilters$4 = searchRentalFilters.petsAllowed) === null || _searchRentalFilters$4 === void 0 ? void 0 : _searchRentalFilters$4.length) {
      var petsAllowed = searchRentalFilters.petsAllowed;
      searchFilterState = Object.assign({}, searchFilterState, getInitialSearchFilterState(PETS_ALLOWED_MAP), getSearchFilterState(petsAllowed, PETS_ALLOWED_MAP));
    }
    return searchFilterState;
  },
  toSearchFilterBlock: function toSearchFilterBlock(searchRentalFilters) {
    var mapMonthlyPayment = function mapMonthlyPayment() {
      var _searchRentalFilters$5, _searchRentalFilters$6, _searchRentalFilters$7, _searchRentalFilters$8;
      return {
        monthly_payment_min_amt: (_searchRentalFilters$5 = searchRentalFilters === null || searchRentalFilters === void 0 ? void 0 : (_searchRentalFilters$6 = searchRentalFilters.monthlyPayment) === null || _searchRentalFilters$6 === void 0 ? void 0 : _searchRentalFilters$6.min) !== null && _searchRentalFilters$5 !== void 0 ? _searchRentalFilters$5 : undefined,
        monthly_payment_max_amt: (_searchRentalFilters$7 = searchRentalFilters === null || searchRentalFilters === void 0 ? void 0 : (_searchRentalFilters$8 = searchRentalFilters.monthlyPayment) === null || _searchRentalFilters$8 === void 0 ? void 0 : _searchRentalFilters$8.max) !== null && _searchRentalFilters$7 !== void 0 ? _searchRentalFilters$7 : undefined
      };
    };
    var mapPetsAllowed = function mapPetsAllowed() {
      if (searchRentalFilters === null || searchRentalFilters === void 0 ? void 0 : searchRentalFilters.petsAllowed) {
        var searchFilterBlock = getInitialSearchFilterBlock(CLICKSTREAM_PETS_ALLOWED_MAP);
        return Object.assign({}, searchFilterBlock, getSearchFilterBlock(searchRentalFilters.petsAllowed, CLICKSTREAM_PETS_ALLOWED_MAP));
      }
      return {};
    };
    return Object.assign({}, mapMonthlyPayment(), mapPetsAllowed());
  }
};
var searchSaleFiltersMapper = {
  transform: function transform(searchSaleFilters) {
    var _searchSaleFilters$da;
    if (searchSaleFilters === null || searchSaleFilters === void 0 ? void 0 : (_searchSaleFilters$da = searchSaleFilters.daysOnZillow) === null || _searchSaleFilters$da === void 0 ? void 0 : _searchSaleFilters$da.max) {
      var bounds = [[ONE_DAY, ONE_WEEK], [ONE_WEEK, TWO_WEEKS], [TWO_WEEKS, ONE_MONTH], [ONE_MONTH, THREE_MONTHS], [THREE_MONTHS, SIX_MONTHS], [SIX_MONTHS, ONE_YEAR], [ONE_YEAR, TWO_YEARS], [TWO_YEARS, THREE_YEARS]];
      var getDaysOnZillow = function getDaysOnZillow(value) {
        var _getValueBounds3 = getValueBounds(value, bounds),
          _getValueBounds4 = _slicedToArray(_getValueBounds3, 2),
          start = _getValueBounds4[0],
          end = _getValueBounds4[1];
        if (value === start || value === end) {
          return value;
        }
        return typeof start === 'number' && typeof end === 'number' ? end : null;
      };
      var dozValue = getDaysOnZillow(searchSaleFilters.daysOnZillow.max);
      return Object.assign({}, searchSaleFilters, {
        daysOnZillow: {
          min: null,
          max: dozValue
        }
      });
    }
    return searchSaleFilters;
  },
  toSearchFilterState: function toSearchFilterState(searchSaleFilters) {
    var _searchSaleFilters$da2;
    var max = searchSaleFilters === null || searchSaleFilters === void 0 ? void 0 : (_searchSaleFilters$da2 = searchSaleFilters.daysOnZillow) === null || _searchSaleFilters$da2 === void 0 ? void 0 : _searchSaleFilters$da2.max;
    if (max && max in DOZ_MAP) {
      return {
        doz: {
          value: DOZ_MAP[max]
        }
      };
    }
    return {};
  },
  toSearchFilterBlock: function toSearchFilterBlock(searchSaleFilters) {
    var _searchSaleFilters$da3, _searchSaleFilters$da4;
    return {
      day_on_zillow_nb: (_searchSaleFilters$da3 = searchSaleFilters === null || searchSaleFilters === void 0 ? void 0 : (_searchSaleFilters$da4 = searchSaleFilters.daysOnZillow) === null || _searchSaleFilters$da4 === void 0 ? void 0 : _searchSaleFilters$da4.max) !== null && _searchSaleFilters$da3 !== void 0 ? _searchSaleFilters$da3 : undefined
    };
  }
};
var keywordsMapper = {
  toSearchFilterState: function toSearchFilterState(keywords) {
    if (keywords) {
      return {
        keywords: {
          value: keywords.toString()
        }
      };
    }
    return {};
  },
  toSearchFilterBlock: function toSearchFilterBlock(keywords) {
    return {
      keywords_txt: keywords === null || keywords === void 0 ? void 0 : keywords.toString().replace(/\s*,\s*/g, ',').split(',')
    };
  }
};
var showOnlyTypeMapper = {
  toSearchFilterState: function toSearchFilterState(showOnlyType) {
    if (showOnlyType === null || showOnlyType === void 0 ? void 0 : showOnlyType.length) {
      return Object.assign({}, getInitialSearchFilterState(SHOW_ONLY_MAP), getSearchFilterState(showOnlyType, SHOW_ONLY_MAP));
    }
    return {};
  },
  toSearchFilterBlock: function toSearchFilterBlock(showOnlyType) {
    var searchFilterBlock = getInitialSearchFilterBlock(CLICKSTREAM_SHOW_ONLY_MAP);
    return showOnlyType ? Object.assign({}, searchFilterBlock, getSearchFilterBlock(showOnlyType, CLICKSTREAM_SHOW_ONLY_MAP)) : searchFilterBlock;
  }
};
var basementStatusTypeMapper = {
  toSearchFilterState: function toSearchFilterState(basementStatusType) {
    if (basementStatusType === null || basementStatusType === void 0 ? void 0 : basementStatusType.length) {
      return Object.assign({}, getInitialSearchFilterState(BASEMENT_MAP), getSearchFilterState(basementStatusType, BASEMENT_MAP));
    }
    return {};
  },
  toSearchFilterBlock: function toSearchFilterBlock(basementStatusType) {
    var searchFilterBlock = getInitialSearchFilterBlock(CLICKSTREAM_BASEMENT_MAP);
    return basementStatusType ? Object.assign({}, searchFilterBlock, getSearchFilterBlock(basementStatusType, CLICKSTREAM_BASEMENT_MAP)) : searchFilterBlock;
  }
};
var homeTypeMapper = {
  toSearchFilterState: function toSearchFilterState(homeType) {
    if (homeType === null || homeType === void 0 ? void 0 : homeType.length) {
      return Object.assign({}, getInitialSearchFilterState(HOME_TYPE_MAP), getSearchFilterState(homeType, HOME_TYPE_MAP), {
        isApartmentOrCondo: {
          value: homeType.includes('APARTMENT') || homeType.includes('CONDO')
        }
      });
    }
    return {};
  },
  toSearchFilterBlock: function toSearchFilterBlock(homeType) {
    var searchFilterBlock = Object.assign({}, getInitialSearchFilterBlock(CLICKSTREAM_HOME_TYPE_MAP), {
      apt_or_condo_ind: false
    });
    return (homeType === null || homeType === void 0 ? void 0 : homeType.length) ? Object.assign({}, searchFilterBlock, getSearchFilterBlock(homeType, CLICKSTREAM_HOME_TYPE_MAP), {
      apt_or_condo_ind: homeType.includes('APARTMENT') || homeType.includes('CONDO')
    }) : searchFilterBlock;
  }
};
var listingStatusTypeMapper = {
  transform: function transform(listingStatusType) {
    if (listingStatusType === null || listingStatusType === void 0 ? void 0 : listingStatusType.length) {
      var searchFilterList;
      // Set all the For Sale listing statuses. Also remove SOLD if it is included with FOR_SALE
      if (listingStatusType.includes('FOR_SALE')) {
        searchFilterList = _toConsumableArray(FOR_SALE_LISTING_STATUS_TYPES);
      }
      // If SOLD is included with any other listing status, remove SOLD
      else if (listingStatusType.includes('SOLD') && listingStatusType.length > 1) {
        searchFilterList = listingStatusType.filter(function (type) {
          return type !== 'SOLD';
        });
      } else {
        searchFilterList = listingStatusType;
      }
      return searchFilterList;
    }
    return listingStatusType;
  },
  toSearchFilterState: function toSearchFilterState(listingStatusType) {
    if (listingStatusType === null || listingStatusType === void 0 ? void 0 : listingStatusType.length) {
      return Object.assign({}, getInitialSearchFilterState(LISTING_STATUS_MAP), getSearchFilterState(listingStatusType, LISTING_STATUS_MAP));
    }
    return {};
  },
  toSearchFilterBlock: function toSearchFilterBlock(listingStatusType) {
    var searchFilterBlock = getInitialSearchFilterBlock(CLICKSTREAM_LISTING_STATUS_MAP);
    return listingStatusType ? Object.assign({}, searchFilterBlock, getSearchFilterBlock(listingStatusType, CLICKSTREAM_LISTING_STATUS_MAP)) : searchFilterBlock;
  }
};
var viewMapper = {
  toSearchFilterState: function toSearchFilterState(view) {
    if (view === null || view === void 0 ? void 0 : view.length) {
      return Object.assign({}, getInitialSearchFilterState(VIEW_MAP), getSearchFilterState(view, VIEW_MAP));
    }
    return {};
  },
  toSearchFilterBlock: function toSearchFilterBlock(view) {
    var searchFilterBlock = getInitialSearchFilterBlock(CLICKSTREAM_VIEW_MAP);
    return view ? Object.assign({}, searchFilterBlock, getSearchFilterBlock(view, CLICKSTREAM_VIEW_MAP)) : searchFilterBlock;
  }
};
var excludeTypesMapper = {
  toSearchFilterState: function toSearchFilterState(excludeTypes) {
    var searchFilterState = {};
    if (excludeTypes === null || excludeTypes === void 0 ? void 0 : excludeTypes.includes('NO_HOA_DATA')) {
      searchFilterState.includeHomesWithNoHoaData = {
        value: false
      };
    }
    if (excludeTypes === null || excludeTypes === void 0 ? void 0 : excludeTypes.includes('SENIOR_COMMUNITY')) {
      searchFilterState.ageRestricted55Plus = {
        value: 'e'
      };
    }
    return searchFilterState;
  },
  toSearchFilterBlock: function toSearchFilterBlock(excludeTypes) {
    var searchFilterBlock = {
      hoa_ind: true,
      age_55_plus_ind: true
    };
    if (excludeTypes === null || excludeTypes === void 0 ? void 0 : excludeTypes.includes('NO_HOA_DATA')) {
      searchFilterBlock.hoa_ind = false;
    }
    if (excludeTypes === null || excludeTypes === void 0 ? void 0 : excludeTypes.includes('SENIOR_COMMUNITY')) {
      searchFilterBlock.age_55_plus_ind = false;
    }
    return searchFilterBlock;
  }
};

/* eslint-enable zillow/@typescript-eslint/naming-convention */

var mappers = {
  baths: bathsMapper,
  beds: bedsMapper,
  hoaFeesPerMonth: hoaFeesPerMonthMapper,
  livingAreaSqft: livingAreaSqftMapper,
  lotSizeSqft: lotSizeSqftMapper,
  parkingSpots: parkingSpotsMapper,
  price: priceMapper,
  yearBuilt: yearBuiltMapper,
  searchRentalFilters: searchRentalFiltersMapper,
  searchSaleFilters: searchSaleFiltersMapper,
  keywords: keywordsMapper,
  showOnlyType: showOnlyTypeMapper,
  basementStatusType: basementStatusTypeMapper,
  homeType: homeTypeMapper,
  listingStatusType: listingStatusTypeMapper,
  view: viewMapper,
  excludeTypes: excludeTypesMapper
};
var mappers$1 = mappers;

/**
 * Maps ZG Graph SearchFilters object to SearchFilterState object
 * @param {SearchFilters} searchFilters - ZG Graph SearchFilters object returned from
 * the search-assistance subgraph
 * @param {string[]} regionIds - region ids associated with the search
 * @return {UpdatedFilters} Object containing SearchFilterState object used to set filters on the SRP and
 * SearchFilterBlock object sent in clickstream events
 */
var getUpdatedFilterData = function getUpdatedFilterData(searchFilters) {
  var filterData = {
    filterState: {},
    filterBlock: {
      // eslint-disable-next-line zillow/@typescript-eslint/naming-convention
      changed_filter_fields: []
    }
  };
  if (!searchFilters) {
    return filterData;
  }
  Object.entries(searchFilters).forEach(function (_ref) {
    var _ref2 = _slicedToArray(_ref, 2),
      key = _ref2[0],
      value = _ref2[1];
    if (key in mappers$1 && value !== null) {
      var _mapper$transform, _mapper$transform2;
      var mapper = mappers$1[key];
      var searchFilterState = flow((_mapper$transform = mapper.transform) !== null && _mapper$transform !== void 0 ? _mapper$transform : identity, mapper.toSearchFilterState)(value);
      var searchFilterBlock = flow((_mapper$transform2 = mapper.transform) !== null && _mapper$transform2 !== void 0 ? _mapper$transform2 : identity, mapper.toSearchFilterBlock)(value);
      Object.assign(filterData.filterState, searchFilterState);
      Object.assign(filterData.filterBlock, searchFilterBlock);
      Object.entries(searchFilterBlock).forEach(function (_ref3) {
        var _ref4 = _slicedToArray(_ref3, 2),
          filterId = _ref4[0],
          filterBlockValue = _ref4[1];
        if (filterBlockValue !== undefined) {
          var _filterData$filterBlo;
          (_filterData$filterBlo = filterData.filterBlock.changed_filter_fields) === null || _filterData$filterBlo === void 0 ? void 0 : _filterData$filterBlo.push(filterId);
        }
      });
    }
  });
  return filterData;
};

/**
 * Checks if only simple filters were updated. Confirms every filter is a simple filter or is null (no change).
 * listingStatusType, homeType, and userSearchContext are considered "simple" filters.
 */
var isSimpleFilterChange = function isSimpleFilterChange(newFilters) {
  var simpleFilters = ['listingStatusType', 'homeType', 'userSearchContext'];
  return Object.entries(newFilters).every(function (_ref5) {
    var _ref6 = _slicedToArray(_ref5, 2),
      filter = _ref6[0],
      value = _ref6[1];
    // Filter value could be an object, if so, recurse through it
    if (_typeof(value) === 'object' && value !== null && !Array.isArray(value)) {
      return isSimpleFilterChange(value);
    }
    return simpleFilters.includes(filter) || value === null;
  });
};

/* eslint-disable zillow/@typescript-eslint/consistent-type-imports */
var useOnClickInsideOutside = function useOnClickInsideOutside(ref, onInsideHandler, onOutsideHandler) {
  useEffect(function () {
    var listener = function listener(_ref) {
      var target = _ref.target;
      if (ref.current) {
        if (ref.current.contains(target)) {
          onInsideHandler === null || onInsideHandler === void 0 ? void 0 : onInsideHandler(target);
        } else {
          onOutsideHandler === null || onOutsideHandler === void 0 ? void 0 : onOutsideHandler();
        }
      }
    };
    document.addEventListener('click', listener);
    return function () {
      document.removeEventListener('click', listener);
    };
  }, [ref, onOutsideHandler, onInsideHandler]);
};

function _templateObject$3() {
  var data = _taggedTemplateLiteralLoose(["\n    query getQueryUnderstandingResults(\n        $query: String!\n        $queryOptions: SearchAssistanceQueryOptions\n        $querySource: SearchAssistanceQuerySource = UNKNOWN\n        $resultType: [SearchAssistanceResultType]\n    ) {\n        searchAssistanceResult: zgsQueryUnderstandingRequest(\n            query: $query\n            queryOptions: $queryOptions\n            querySource: $querySource\n            resultType: $resultType\n        ) {\n            requestId\n            results {\n                __typename\n                id\n                ...RegionResultFields\n                ...SemanticResultFields\n                ...RentalCommunityResultFields\n                ...SchoolResultFields\n                ...AddressResultFields\n            }\n        }\n    }\n    ", "\n    ", "\n    ", "\n    ", "\n    ", "\n"]);
  _templateObject$3 = function _templateObject() {
    return data;
  };
  return data;
}
var getQueryUnderstandingResults = gql(_templateObject$3(), REGION_RESULT_FIELDS, SEMANTIC_RESULT_FIELDS, RENTAL_COMMUNITY_RESULT_FIELDS, SCHOOL_RESULT_FIELDS, ADDRESS_RESULT_FIELDS);

/* eslint-disable zillow/@typescript-eslint/consistent-type-imports */

/**
 * Sets the listbox height to fill the remaining vertical screen below the search box.
 */
var useMaximizeListboxHeight = function useMaximizeListboxHeight(parentRef, paddingBottom) {
  if (paddingBottom === void 0) {
    paddingBottom = 16;
  }
  useEffect(function () {
    var callback = function callback() {
      var _parentRef$current;
      var listbox = (_parentRef$current = parentRef.current) === null || _parentRef$current === void 0 ? void 0 : _parentRef$current.querySelector('[role="listbox"]');
      if (listbox) {
        window.requestAnimationFrame(function () {
          /**
           * Max height is 100vh minus the distance from the top of the screen to the top of the listbox, with some padding added
           */
          listbox.style.maxHeight = "calc(100vh - (" + listbox.getBoundingClientRect().top + "px + " + paddingBottom + "px))";
        });
      }
    };
    var observer = new MutationObserver(callback);
    if (parentRef.current) {
      observer.observe(parentRef.current, {
        childList: true,
        subtree: true
      });
    }
    return function () {
      observer.disconnect();
    };
  }, [parentRef, paddingBottom]);
};

var UNIT_SPECIFIER_SUBSTRINGS_FOR_BDP_REDIRECT_LOGIC = ['apt', 'unit', 'apartment', '#'];
var SUBSTRINGS_TO_IGNORE_FOR_BDP_REDIRECT_LOGIC = [','].concat(UNIT_SPECIFIER_SUBSTRINGS_FOR_BDP_REDIRECT_LOGIC);
var PADDING_FOR_BUILDING_LATLONG = 0.00001; // margin for deciding if units belong to the same building

var isNotBlank = function isNotBlank(str) {
  return !!str && str.trim().length > 0;
};
var getHasUnitSpecifiers = function getHasUnitSpecifiers(query) {
  var lowercaseQuery = query.toLowerCase();
  return UNIT_SPECIFIER_SUBSTRINGS_FOR_BDP_REDIRECT_LOGIC.some(function (subStr) {
    return lowercaseQuery.includes(subStr);
  });
};

/**
 * Removes unit specifier substrings from the query and normalizes spaces
 */
var getQueryWithoutUnitSpecifiers = function getQueryWithoutUnitSpecifiers(query) {
  return SUBSTRINGS_TO_IGNORE_FOR_BDP_REDIRECT_LOGIC.reduce(function (updatedQuery, substringToIgnore) {
    return updatedQuery.split(substringToIgnore).join('');
  }, query.toLowerCase()) // Initialize with the lowercase query
  .replace(/\s+/g, ' ');
}; // Normalize spaces

var getIsSameQuery = function getIsSameQuery(firstQuery, secondQuery) {
  /**
   * Sometimes the user's search doesn't exactly match the id of the topMatchingResult due to commas and apartment descriptors.
   * Example:
   * User searches -> "737 Olive Way APT 2308, Seattle, WA 98101"
   * QU returns -> "737 Olive Way #2308 Seattle, WA 98101" (same address but not the exact same string)
   */
  var newFirstQuery = getQueryWithoutUnitSpecifiers(firstQuery).trim();
  var newSecondQuery = getQueryWithoutUnitSpecifiers(secondQuery).trim();
  return newFirstQuery === newSecondQuery;
};
var belongsToSameBuilding = function belongsToSameBuilding(_ref, _ref2) {
  var d1 = _ref.data;
  var d2 = _ref2.data;
  if (!d1.lat || !d1.lng || !d2.lat || !d2.lng) {
    return false;
  }
  var latDiff = Math.abs(d1.lat - d2.lat);
  var longDiff = Math.abs(d1.lng - d2.lng);
  return latDiff < PADDING_FOR_BUILDING_LATLONG && longDiff < PADDING_FOR_BUILDING_LATLONG;
};
var shouldCallBuildingService = function shouldCallBuildingService(results, query) {
  if (results.length < 2) {
    return false;
  }
  var firstMatchingResult = results[0];
  var parsedId = isNotBlank(firstMatchingResult.displayName) ? firstMatchingResult.displayName : null;
  if (parsedId !== null && getHasUnitSpecifiers(parsedId) && getIsSameQuery(query, parsedId)) {
    return false;
  }
  var secondMatchingResult = results[1];
  if (isAddressSuggestion(firstMatchingResult) && isAddressSuggestion(secondMatchingResult)) {
    return belongsToSameBuilding(firstMatchingResult, secondMatchingResult);
  }
  return false;
};

var _this$3 = undefined,
  _jsxFileName$4 = "/builds/zillow/searchxp/static-search-box/src/components/SearchBoxOption.tsx";
var StyledListboxOptionLabelText = styled.span.withConfig({
  displayName: "SearchBoxOption__StyledListboxOptionLabelText",
  componentId: "sxp-sb__sc-1rb3buu-0"
})(["overflow:hidden;text-overflow:ellipsis;white-space:nowrap;"]);
var StyledListboxOptionLabel = styled(ListboxOptionLabel).withConfig({
  displayName: "SearchBoxOption__StyledListboxOptionLabel",
  componentId: "sxp-sb__sc-1rb3buu-1"
})(["display:flex;"]);
var StyledListboxOptionLabelGleam = styled(Gleam).withConfig({
  displayName: "SearchBoxOption__StyledListboxOptionLabelGleam",
  componentId: "sxp-sb__sc-1rb3buu-2"
})(["align-self:center;margin-left:auto;"]);

// eslint-disable-next-line zillow/@typescript-eslint/no-type-alias

var ComboboxOptionLabel = /*#__PURE__*/memo(function (_ref) {
  var label = _ref.label,
    notificationCount = _ref.notificationCount,
    currentSearchTerm = _ref.currentSearchTerm,
    getHighlightPattern = _ref.getHighlightPattern;
  return /*#__PURE__*/React.createElement(StyledListboxOptionLabel, {
    __self: _this$3,
    __source: {
      fileName: _jsxFileName$4,
      lineNumber: 47,
      columnNumber: 9
    }
  }, /*#__PURE__*/React.createElement(StyledListboxOptionLabelText, {
    __self: _this$3,
    __source: {
      fileName: _jsxFileName$4,
      lineNumber: 48,
      columnNumber: 13
    }
  }, /*#__PURE__*/React.createElement(Highlighter, {
    text: label,
    pattern: getHighlightPattern(getSuggestionValue(currentSearchTerm)),
    __self: _this$3,
    __source: {
      fileName: _jsxFileName$4,
      lineNumber: 49,
      columnNumber: 17
    }
  })), notificationCount ? /*#__PURE__*/React.createElement(StyledListboxOptionLabelGleam, {
    count: notificationCount,
    maxSuffix: true,
    __self: _this$3,
    __source: {
      fileName: _jsxFileName$4,
      lineNumber: 55,
      columnNumber: 17
    }
  }) : null);
});

// eslint-disable-next-line zillow/@typescript-eslint/no-type-alias

// eslint-disable-next-line zillow/@typescript-eslint/no-type-alias

var useSearchBoxOption = function useSearchBoxOption(_ref2) {
  var isLocalQuery = _ref2.isLocalQuery,
    currentSearchTerm = _ref2.currentSearchTerm;
  // We restrict highlighting to the autocomplete suggestions
  // eslint-disable-next-line no-confusing-arrow
  var getHighlightPattern = useCallback(function (inputValue) {
    if (!isLocalQuery && inputValue.trim()) {
      return inputValue;
    }
    return null;
  }, [isLocalQuery]);
  var renderOption = useCallback(function (optionProps) {
    var label = optionProps.label,
      notificationCount = optionProps.notificationCount,
      onOptionClick = optionProps.onOptionClick;
    return /*#__PURE__*/React.createElement(ComboboxOption, _extends({}, optionProps, {
      // TODO SXP-15483: This improves the interaction time of clicking on an option, but a new frame isn't rendered
      // until after onOptionClick runs. Figure out a way to quickly render a new frame when clicking on an option
      onOptionClick: onOptionClick ? yieldCallback(onOptionClick) : onOptionClick,
      label: typeof label === 'string' ? /*#__PURE__*/React.createElement(ComboboxOptionLabel, {
        label: label,
        notificationCount: notificationCount,
        currentSearchTerm: currentSearchTerm,
        getHighlightPattern: getHighlightPattern,
        __self: _this$3,
        __source: {
          fileName: _jsxFileName$4,
          lineNumber: 107,
          columnNumber: 29
        }
      }) : label,
      __self: _this$3,
      __source: {
        fileName: _jsxFileName$4,
        lineNumber: 100,
        columnNumber: 17
      }
    }));
  }, [currentSearchTerm, getHighlightPattern]);
  return renderOption;
};

var _jsxFileName$3 = "/builds/zillow/searchxp/static-search-box/src/LocationAccessButton.jsx";
var LocationAccessButton = /*#__PURE__*/function (_Component) {
  _inheritsLoose(LocationAccessButton, _Component);
  function LocationAccessButton(props) {
    var _this;
    _this = _Component.call(this, props) || this;
    _this._requestLocationAccess = _this._requestLocationAccess.bind(_assertThisInitialized(_this));
    return _this;
  }
  var _proto = LocationAccessButton.prototype;
  _proto._requestLocationAccess = function _requestLocationAccess(e) {
    e.preventDefault();
    if (global.navigator && global.navigator.geolocation) {
      global.navigator.geolocation.getCurrentPosition(this.props.onLocationAccessGranted, this.props.onLocationAccessFailed);
    } else {
      console.warn('No location object available');
    }
  };
  _proto.render = function render() {
    var _this2 = this;
    var _this$props = this.props,
      isCurrentLocationSearch = _this$props.isCurrentLocationSearch,
      isUserPositionInMapBounds = _this$props.isUserPositionInMapBounds,
      onReCenter = _this$props.onReCenter,
      size = _this$props.size,
      padding = _this$props.padding;
    var LocationOutlineIcon = function LocationOutlineIcon(props) {
      return /*#__PURE__*/React.createElement(IconLocationArrowOutline, _extends({}, props, {
        __self: _this2,
        __source: {
          fileName: _jsxFileName$3,
          lineNumber: 34,
          columnNumber: 46
        }
      }));
    };
    var LocationIcon = function LocationIcon(props) {
      return /*#__PURE__*/React.createElement(IconLocationArrow, _extends({}, props, {
        __self: _this2,
        __source: {
          fileName: _jsxFileName$3,
          lineNumber: 35,
          columnNumber: 39
        }
      }));
    };
    return /*#__PURE__*/React.createElement(IconButton, {
      ref: this.props.buttonRef,
      title: "Use your location",
      onMouseDown: !isCurrentLocationSearch ? this._requestLocationAccess : onReCenter,
      icon: isCurrentLocationSearch && !isUserPositionInMapBounds ? /*#__PURE__*/React.createElement(LocationOutlineIcon, {
        __self: this
      }) : /*#__PURE__*/React.createElement(LocationIcon, {
        __self: this
      }),
      size: size,
      padding: padding,
      type: "button",
      bare: true,
      __self: this,
      __source: {
        fileName: _jsxFileName$3,
        lineNumber: 38,
        columnNumber: 13
      }
    });
  };
  return LocationAccessButton;
}(Component);
LocationAccessButton.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * Callback on user allowing permission location access.
   * @param {Object} position
   *     @param {Object} position.coords - { latitude, longitude, accuracy }
   */
  onLocationAccessGranted: PropTypes.func,
  /**
   * Callback on location request failing.
   * @param {PositionError} error - the reason for geolocation failure
   *
   */
  onLocationAccessFailed: PropTypes.func,
  /**
   * whether or a current location search is active
   */
  isCurrentLocationSearch: PropTypes.bool,
  /**
   * whether the user position is visible in the map
   */
  isUserPositionInMapBounds: PropTypes.bool,
  /**
   * update map bounds to center around user position
   */
  onReCenter: PropTypes.func,
  /**
   * Size of the button, passed through to IconButton
   */
  size: PropTypes.oneOf(Object.values(BUTTON_SIZES)),
  /**
   * Padding for the button, passed through to IconButton
   */
  // eslint-disable-next-line zillow/react/forbid-prop-types
  padding: PropTypes.object,
  /**
   * Ref to the button
   */
  // eslint-disable-next-line zillow/react/forbid-prop-types
  buttonRef: PropTypes.object
} : {};
LocationAccessButton.defaultProps = {
  onLocationAccessGranted: function onLocationAccessGranted() {},
  onLocationAccessFailed: function onLocationAccessFailed() {},
  isUserPositionInMapBounds: true,
  onReCenter: function onReCenter() {},
  isCurrentLocationSearch: false,
  size: BUTTON_SIZES.sm,
  padding: {}
};
var LocationAccessButton$1 = LocationAccessButton;

var _jsxFileName$2 = "/builds/zillow/searchxp/static-search-box/src/components/SearchBoxInput.tsx",
  _this$2 = undefined;
var categoryValuePattern = /^\[[A-Z_]+\]/g;

// Fix for the default icon padding
// increasing the height of the search box
var StyledSearchBoxAdornment = styled(Adornment).withConfig({
  displayName: "SearchBoxInput__StyledSearchBoxAdornment",
  componentId: "sxp-sb__sc-1hknwe6-0"
})(["padding:0 0 0 5px;border-radius:0 ", "px ", "px 0;button{border-radius:0;&:focus{z-index:1;}&:last-of-type{border-radius:0 ", "px ", "px 0;}}"], token('radii.md'), token('radii.md'), token('radii.md'), token('radii.md'));
var getDisplayTextFromOptionValue = function getDisplayTextFromOptionValue(value) {
  return value.replace(categoryValuePattern, '');
};
var StyledClearButton = /*#__PURE__*/memo(styled(IconButton).withConfig({
  displayName: "SearchBoxInput__StyledClearButton",
  componentId: "sxp-sb__sc-1hknwe6-1"
})(["", ""], function (_ref) {
  var hideButton = _ref.hideButton;
  return hideButton && 'display: none !important;';
}));
var getAdornmentToShow = function getAdornmentToShow(_ref2) {
  var disableCurrentLocation = _ref2.disableCurrentLocation,
    showLocationIcon = _ref2.showLocationIcon,
    isCurrentLocationSearch = _ref2.isCurrentLocationSearch,
    displayName = _ref2.currentSearchTerm.displayName;
  var locationIconAvailable = !disableCurrentLocation && showLocationIcon && isCurrentLocationSearch;
  if (locationIconAvailable) {
    return 'location';
  }
  if (displayName) {
    return 'clear';
  }
  return 'search';
};

/**
 * Storing the following as variables to fix why-did-you-render warnings:
 *  - closeButtonIcon
 *  - searchIcon
 *  - adornmentMouseDown
 *  - submitButtonOnClick
 * If they are not stored like this, the components that consume them will
 * be re-rendered unnecessarily due to their children props being different.
 */
var closeButtonIcon = /*#__PURE__*/React.createElement(IconCloseCircle, {
  __self: undefined,
  __source: {
    fileName: _jsxFileName$2,
    lineNumber: 104,
    columnNumber: 25
  }
});
var searchIcon = /*#__PURE__*/React.createElement(IconSearch, {
  __self: undefined,
  __source: {
    fileName: _jsxFileName$2,
    lineNumber: 105,
    columnNumber: 20
  }
});
var adornmentMouseDown = function adornmentMouseDown(e) {
  e.preventDefault();
};
var submitButtonOnClick = function submitButtonOnClick(e) {
  // Prevents options from opening when clicking button or when hitting enter on the keyboard
  e.stopPropagation();
};
var InputAdornment = /*#__PURE__*/memo(function (_ref3) {
  var disableCurrentLocation = _ref3.disableCurrentLocation,
    showLocationIcon = _ref3.showLocationIcon,
    isCurrentLocationSearch = _ref3.isCurrentLocationSearch,
    currentSearchTerm = _ref3.currentSearchTerm,
    size = _ref3.size,
    padding = _ref3.padding,
    clearInput = _ref3.clearInput,
    clearButtonRef = _ref3.clearButtonRef,
    submitButtonRef = _ref3.submitButtonRef,
    currentLocationButtonRef = _ref3.currentLocationButtonRef,
    onLocationAccessGranted = _ref3.onLocationAccessGranted,
    onLocationAccessFailed = _ref3.onLocationAccessFailed,
    onReCenter = _ref3.onReCenter,
    isUserPositionInMapBounds = _ref3.isUserPositionInMapBounds;
  var adornmentToShow = getAdornmentToShow({
    disableCurrentLocation: disableCurrentLocation,
    showLocationIcon: showLocationIcon,
    isCurrentLocationSearch: isCurrentLocationSearch,
    currentSearchTerm: currentSearchTerm
  });
  var clearButtonKeyUp = useCallback(function (e) {
    if (e.key === 'Enter' || e.key === ' ') {
      var _clearButtonRef$curre;
      clearInput();
      (_clearButtonRef$curre = clearButtonRef.current) === null || _clearButtonRef$curre === void 0 ? void 0 : _clearButtonRef$curre.blur(); // Remove focus from hidden button
    }
  }, [clearButtonRef, clearInput]);
  if (adornmentToShow === 'location') {
    return /*#__PURE__*/React.createElement(LocationAccessButton$1, {
      buttonRef: currentLocationButtonRef,
      size: size,
      padding: padding,
      onLocationAccessGranted: onLocationAccessGranted,
      onLocationAccessFailed: onLocationAccessFailed,
      isCurrentLocationSearch: isCurrentLocationSearch,
      isUserPositionInMapBounds: isUserPositionInMapBounds,
      onReCenter: onReCenter,
      __self: _this$2,
      __source: {
        fileName: _jsxFileName$2,
        lineNumber: 152,
        columnNumber: 17
      }
    });
  }
  return /*#__PURE__*/React.createElement(React.Fragment, {
    __self: _this$2,
    __source: {
      fileName: _jsxFileName$2,
      lineNumber: 166,
      columnNumber: 13
    }
  }, /*#__PURE__*/React.createElement(StyledClearButton, {
    size: size,
    padding: padding,
    bare: true,
    hideButton: adornmentToShow !== 'clear',
    icon: closeButtonIcon,
    onMouseDown: adornmentMouseDown // Prevent blur of input
    // Using onClick instead causing the options to disappear on click
    ,
    onMouseUp: clearInput,
    onKeyUp: clearButtonKeyUp,
    ref: clearButtonRef,
    title: "clear search text button",
    type: "button",
    tabIndex: adornmentToShow !== 'clear' ? -1 : 0,
    __self: _this$2,
    __source: {
      fileName: _jsxFileName$2,
      lineNumber: 167,
      columnNumber: 17
    }
  }), /*#__PURE__*/React.createElement(IconButton, {
    ref: submitButtonRef,
    size: size,
    padding: padding,
    title: "Submit Search",
    icon: searchIcon,
    type: "submit",
    onClick: submitButtonOnClick,
    bare: true,
    __self: _this$2,
    __source: {
      fileName: _jsxFileName$2,
      lineNumber: 182,
      columnNumber: 17
    }
  }));
});
var useSearchBoxInput = function useSearchBoxInput(_ref4) {
  var disableCurrentLocation = _ref4.disableCurrentLocation,
    showLocationIcon = _ref4.showLocationIcon,
    isCurrentLocationSearch = _ref4.isCurrentLocationSearch,
    size = _ref4.size,
    buttonPadding = _ref4.buttonPadding,
    inputPadding = _ref4.inputPadding,
    clearInput = _ref4.clearInput,
    isUserPositionInMapBounds = _ref4.isUserPositionInMapBounds,
    currentSearchTerm = _ref4.currentSearchTerm,
    onLocationAccessGranted = _ref4.onLocationAccessGranted,
    onLocationAccessFailed = _ref4.onLocationAccessFailed,
    onReCenter = _ref4.onReCenter,
    inputRef = _ref4.inputRef,
    clearButtonRef = _ref4.clearButtonRef,
    submitButtonRef = _ref4.submitButtonRef,
    currentLocationButtonRef = _ref4.currentLocationButtonRef,
    _onBlur = _ref4.onBlur,
    showAdornments = _ref4.showAdornments,
    renderInputProp = _ref4.renderInput;
  var renderInput = useCallback(function (_ref5) {
    _ref5.onClear;
      _ref5.onTagClose;
      var inputPropOnBlur = _ref5.onBlur,
      value = _ref5.value,
      inputProps = _objectWithoutProperties(_ref5, ["onClear", "onTagClose", "onBlur", "value"]);
    var newProps = Object.assign({}, inputProps, inputPadding, {
      autoComplete: 'off',
      enterKeyHint: 'search',
      onBlur: function onBlur(e) {
        _onBlur(e, inputPropOnBlur);
      },
      // Workaround for SXP-12248
      value: getDisplayTextFromOptionValue(value)
    });
    if (renderInputProp) {
      return renderInputProp(Object.assign({
        ref: inputRef
      }, newProps));
    }
    if (showAdornments) {
      return /*#__PURE__*/React.createElement(AdornedInput, {
        input: /*#__PURE__*/React.createElement(Input, _extends({
          ref: inputRef
        }, newProps, {
          "aria-label": "Search",
          __self: _this$2,
          __source: {
            fileName: _jsxFileName$2,
            lineNumber: 261,
            columnNumber: 32
          }
        })),
        rightAdornment: /*#__PURE__*/React.createElement(StyledSearchBoxAdornment, {
          "aria-hidden": "false",
          __self: _this$2,
          __source: {
            fileName: _jsxFileName$2,
            lineNumber: 263,
            columnNumber: 29
          }
        }, /*#__PURE__*/React.createElement(InputAdornment, {
          disableCurrentLocation: disableCurrentLocation,
          showLocationIcon: showLocationIcon,
          isCurrentLocationSearch: isCurrentLocationSearch,
          currentSearchTerm: currentSearchTerm,
          size: size,
          padding: buttonPadding,
          clearInput: clearInput,
          clearButtonRef: clearButtonRef,
          submitButtonRef: submitButtonRef,
          currentLocationButtonRef: currentLocationButtonRef,
          onLocationAccessGranted: onLocationAccessGranted,
          onLocationAccessFailed: onLocationAccessFailed,
          onReCenter: onReCenter,
          isUserPositionInMapBounds: isUserPositionInMapBounds,
          __self: _this$2,
          __source: {
            fileName: _jsxFileName$2,
            lineNumber: 264,
            columnNumber: 33
          }
        })),
        __self: _this$2,
        __source: {
          fileName: _jsxFileName$2,
          lineNumber: 260,
          columnNumber: 21
        }
      });
    }
    return /*#__PURE__*/React.createElement(Input, _extends({
      ref: inputRef
    }, newProps, {
      "aria-label": "Search",
      __self: _this$2,
      __source: {
        fileName: _jsxFileName$2,
        lineNumber: 285,
        columnNumber: 20
      }
    }));
  }, [buttonPadding, clearButtonRef, clearInput, currentLocationButtonRef, currentSearchTerm, disableCurrentLocation, inputPadding, inputRef, isCurrentLocationSearch, isUserPositionInMapBounds, _onBlur, onLocationAccessFailed, onLocationAccessGranted, onReCenter, renderInputProp, showAdornments, showLocationIcon, size, submitButtonRef]);
  return renderInput;
};

function _templateObject2() {
  var data = _taggedTemplateLiteralLoose(["\n    query buildingUrlForSearchBox($lotIds: [ID]!) {\n        vl_buildingSummaries(lotIds: $lotIds) {\n            url\n        }\n    }\n"]);
  _templateObject2 = function _templateObject2() {
    return data;
  };
  return data;
}
function _templateObject$2() {
  var data = _taggedTemplateLiteralLoose(["\n    query nearbyBuildingAttributesForSearchBox($latitude: Float, $longitude: Float) {\n        nearbyBuildingAttributes(latitude: $latitude, longitude: $longitude) {\n            lotId\n            city\n            state\n            buildingName\n            streetAddress1\n        }\n    }\n"]);
  _templateObject$2 = function _templateObject() {
    return data;
  };
  return data;
}
var NEARBY_BUILDING_ATTRIBUTES_QUERY = gql(_templateObject$2());
var BUILDING_URL_QUERY = gql(_templateObject2());

var fetchBuildingAttributes = function fetchBuildingAttributes(client, lat, lon) {
  return new Promise(function ($return, $error) {
    var _data$nearbyBuildingA, _await$client$query, data, errors, cause;
    var $Try_1_Post = function () {
      try {
        return $return([]);
      } catch ($boundEx) {
        return $error($boundEx);
      }
    };
    var $Try_1_Catch = function (err) {
      try {
        console.error(err);
        cause = err.cause;
        if (Array.isArray(cause)) {
          cause.forEach(function (_ref) {
            var message = _ref.message;
            if (message) {
              console.error(message);
            }
          });
        }
        return $Try_1_Post();
      } catch ($boundEx) {
        return $error($boundEx);
      }
    };
    try {
      return Promise.resolve(client.query({
        query: NEARBY_BUILDING_ATTRIBUTES_QUERY,
        variables: {
          latitude: lat,
          longitude: lon
        }
      })).then(function ($await_4) {
        try {
          _await$client$query = $await_4, data = _await$client$query.data, errors = _await$client$query.errors;
          if (!data || errors) {
            throw new Error('Error(s) encountered while fetching building attributes', {
              cause: errors
            });
          }
          return $return((_data$nearbyBuildingA = data.nearbyBuildingAttributes) !== null && _data$nearbyBuildingA !== void 0 ? _data$nearbyBuildingA : []);
        } catch ($boundEx) {
          return $Try_1_Catch($boundEx);
        }
      }, $Try_1_Catch);
    } catch (err) {
      $Try_1_Catch(err);
    }
  });
};
var fetchBuildingUrl = function fetchBuildingUrl(client, lotIds) {
  return new Promise(function ($return, $error) {
    var _data$vl_buildingSumm, _data$vl_buildingSumm2, _data$vl_buildingSumm3, _await$client$query2, data, errors, cause;
    var $Try_2_Post = function () {
      try {
        return $return('');
      } catch ($boundEx) {
        return $error($boundEx);
      }
    };
    var $Try_2_Catch = function (err) {
      try {
        console.error(err);
        cause = err.cause;
        if (Array.isArray(cause)) {
          cause.forEach(function (_ref2) {
            var message = _ref2.message;
            if (message) {
              console.error(message);
            }
          });
        }
        return $Try_2_Post();
      } catch ($boundEx) {
        return $error($boundEx);
      }
    };
    try {
      return Promise.resolve(client.query({
        query: BUILDING_URL_QUERY,
        variables: {
          lotIds: lotIds
        }
      })).then(function ($await_5) {
        try {
          _await$client$query2 = $await_5, data = _await$client$query2.data, errors = _await$client$query2.errors;
          if (!data || errors) {
            throw new Error('Error(s) encountered while fetching building URL', {
              cause: errors
            });
          }
          return $return((_data$vl_buildingSumm = (_data$vl_buildingSumm2 = data.vl_buildingSummaries) === null || _data$vl_buildingSumm2 === void 0 ? void 0 : (_data$vl_buildingSumm3 = _data$vl_buildingSumm2[0]) === null || _data$vl_buildingSumm3 === void 0 ? void 0 : _data$vl_buildingSumm3.url) !== null && _data$vl_buildingSumm !== void 0 ? _data$vl_buildingSumm : '');
        } catch ($boundEx) {
          return $Try_2_Catch($boundEx);
        }
      }, $Try_2_Catch);
    } catch (err) {
      $Try_2_Catch(err);
    }
  });
};
var encodeLatLon = function encodeLatLon(lat, lon) {
  var latStr = lat ? lat.toString() : '';
  var lonStr = lon ? lon.toString() : '';
  return latStr + "," + lonStr + "_ll";
};
var createLatLongUrl = function createLatLongUrl(lat, lon, attributes) {
  var encodedStreetAddress;
  encodedStreetAddress = '';
  if (attributes && (attributes.buildingName || attributes.streetAddress1)) {
    var _attributes$buildingN;
    var buildingName;
    buildingName = (_attributes$buildingN = attributes.buildingName) !== null && _attributes$buildingN !== void 0 ? _attributes$buildingN : attributes.streetAddress1;
    buildingName = attributes.city ? buildingName + " " + attributes.city : buildingName;
    buildingName = attributes.state ? buildingName + " " + attributes.state : buildingName;
    encodedStreetAddress = encodeAddressString(buildingName);
  }
  var encodedLatLon = encodeLatLon(lat, lon);
  var finalUrl;
  finalUrl = '/b/';
  if (encodedStreetAddress.length) {
    finalUrl = "" + finalUrl + encodedStreetAddress + "/";
  }
  finalUrl = "" + finalUrl + encodedLatLon + "/";
  return finalUrl;
};
var getBuildingUrl = function getBuildingUrl(client, lat, lon) {
  return new Promise(function ($return, $error) {
    var attributesArr, bdpUrl, lotIdArr;
    if (!lat || !lon) {
      return $return('');
    }
    return Promise.resolve(fetchBuildingAttributes(client, lat, lon)).then(function ($await_6) {
      try {
        attributesArr = $await_6;
        bdpUrl = '';
        if (attributesArr.length) {
          lotIdArr = attributesArr.map(function (attr) {
            return attr.lotId;
          }).filter(function (lotId) {
            return lotId;
          });
          return Promise.resolve(fetchBuildingUrl(client, lotIdArr)).then(function ($await_7) {
            try {
              bdpUrl = $await_7;
              return $If_3.call(this);
            } catch ($boundEx) {
              return $error($boundEx);
            }
          }.bind(this), $error);
        }
        function $If_3() {
          if (!bdpUrl) {
            bdpUrl = createLatLongUrl(lat, lon, attributesArr[0]);
          }
          return $return(bdpUrl);
        }
        return $If_3.call(this);
      } catch ($boundEx) {
        return $error($boundEx);
      }
    }.bind(this), $error);
  });
};

function _templateObject$1() {
  var data = _taggedTemplateLiteralLoose(["\n    query hdpUrlForSearch($zpid: ID) {\n        property(zpid: $zpid) {\n            hdpUrl\n        }\n    }\n"]);
  _templateObject$1 = function _templateObject() {
    return data;
  };
  return data;
}
var HDP_URL_QUERY = gql(_templateObject$1());

var getFallbackHdpUrl = function getFallbackHdpUrl(zpid) {
  return "/homedetails/" + zpid + "_zpid/";
};
var getHdpUrl = function getHdpUrl(client, zpid) {
  return new Promise(function ($return, $error) {
    var _data$property$hdpUrl, _data$property, _await$client$query, data, errors, _ref, cause;
    var $Try_1_Catch = function (err) {
      try {
        console.error(err);
        _ref = err, cause = _ref.cause;
        if (Array.isArray(cause)) {
          cause.forEach(function (_ref2) {
            var message = _ref2.message;
            if (message) {
              console.error(message);
            }
          });
        }
        return $return(getFallbackHdpUrl(zpid));
      } catch ($boundEx) {
        return $error($boundEx);
      }
    };
    try {
      return Promise.resolve(client.query({
        query: HDP_URL_QUERY,
        variables: {
          zpid: zpid
        }
      })).then(function ($await_2) {
        try {
          _await$client$query = $await_2, data = _await$client$query.data, errors = _await$client$query.errors;
          if (!data || errors) {
            throw new Error('Error(s) encountered while fetching HDP URL', {
              cause: errors
            });
          }
          return $return((_data$property$hdpUrl = (_data$property = data.property) === null || _data$property === void 0 ? void 0 : _data$property.hdpUrl) !== null && _data$property$hdpUrl !== void 0 ? _data$property$hdpUrl : getFallbackHdpUrl(zpid));
        } catch ($boundEx) {
          return $Try_1_Catch($boundEx);
        }
      }, $Try_1_Catch);
    } catch (err) {
      $Try_1_Catch(err);
    }
  });
};

// eslint-disable-next-line zillow/@typescript-eslint/no-type-alias

var getTreatment = function getTreatment(abTests, key) {
  var _abTests$key;
  return (_abTests$key = abTests === null || abTests === void 0 ? void 0 : abTests[key]) !== null && _abTests$key !== void 0 ? _abTests$key : 'CONTROL';
};

var _this$1 = undefined,
  _jsxFileName$1 = "/builds/zillow/searchxp/static-search-box/src/SearchBox.tsx";

// The height of the Listbox is not configurable via the Combobox interface.
// We need fixed height ListBox across all viewport sizes and full width on narrow viewports.
// The useMaximizeListboxHeight hook is used to set the height of the Listbox.
// Updating the height via a prop here is less performant than setting an inline style.
// Height ond width of the parent Dropdown is set below.
// The Dropdown component being styled: https://gitlab.zgtools.net/constellation/constellation/-/blob/v8.108.2/src/components/Combobox/ControlledCombobox.jsx#L567
// StyledDropdown works because of the way Dropdown is implemented: https://gitlab.zgtools.net/constellation/constellation/-/blob/v8.108.2/src/components/Dropdown/Dropdown.jsx#L25
// This needs to be updated if these details change in a future Constellation version.
var StyledCombobox = /*#__PURE__*/memo(styled(Combobox).withConfig({
  displayName: "SearchBox__StyledCombobox",
  componentId: "sxp-sb__sc-1romu78-0"
})(["@media screen and (min-width:890px){&&& ", "{min-width:540px;}}@media screen and (max-width:889px){&&& ", "{width:100vw !important;}}&&& ", "{height:auto;z-index:100;}input,label{background-color:", ";border:none;}input{text-overflow:ellipsis;}"], StyledDropdown, StyledDropdown, StyledDropdown, token('colors.backgroundWhite')));
var StyledForm = styled(Form).withConfig({
  displayName: "SearchBox__StyledForm",
  componentId: "sxp-sb__sc-1romu78-1"
})(["border:1px solid;border-radius:", "px;border-color:", ";background-color:", ";&:active,&:hover,&:focus{border-color:", ";}.fullscreen &{position:absolute;left:0px;right:0px;}.fullscreen &&& ", "{height:100vh !important;}"], token('radii.md'), token('colors.gray300'), token('colors.backgroundWhite'), token('colors.blue400'), StyledDropdown);
var createUniqueOptionValue = function createUniqueOptionValue(displayName, category) {
  return "[" + category + "]" + displayName;
};
var getSuggestionCategoryIcon = function getSuggestionCategoryIcon(category) {
  switch (category) {
    case SuggestionCategory.region:
      return /*#__PURE__*/React.createElement(IconLocationOutline, {
        __self: _this$1,
        __source: {
          fileName: _jsxFileName$1,
          lineNumber: 159,
          columnNumber: 20
        }
      });
    case SuggestionCategory.school:
      return /*#__PURE__*/React.createElement(IconGraduationCapOutline, {
        __self: _this$1,
        __source: {
          fileName: _jsxFileName$1,
          lineNumber: 161,
          columnNumber: 20
        }
      });
    case SuggestionCategory.semantic:
      return /*#__PURE__*/React.createElement(IconSearchOutline, {
        __self: _this$1,
        __source: {
          fileName: _jsxFileName$1,
          lineNumber: 163,
          columnNumber: 20
        }
      });
    case SuggestionCategory.currentLocation:
      return /*#__PURE__*/React.createElement(IconLocationOutline, {
        __self: _this$1,
        __source: {
          fileName: _jsxFileName$1,
          lineNumber: 165,
          columnNumber: 20
        }
      });
    case SuggestionCategory.recentSearch:
      return /*#__PURE__*/React.createElement(IconClockOutline, {
        __self: _this$1
      });
    case SuggestionCategory.savedSearch:
      return /*#__PURE__*/React.createElement(IconSavedSearchOutline, {
        "data-test": "saved-search-icon",
        __self: _this$1,
        __source: {
          fileName: _jsxFileName$1,
          lineNumber: 169,
          columnNumber: 20
        }
      });
    case SuggestionCategory.savedHome:
    case SuggestionCategory.claimedHome:
      return /*#__PURE__*/React.createElement(IconFavoriteHomeOutline, {
        __self: _this$1,
        __source: {
          fileName: _jsxFileName$1,
          lineNumber: 172,
          columnNumber: 20
        }
      });
    default:
      return null;
  }
};
var convertRemoteSuggestionsToOptions = function convertRemoteSuggestionsToOptions(suggestions) {
  return suggestions.map(function (_ref, index) {
    var displayName = _ref.displayName,
      category = _ref.category;
    return {
      icon: getSuggestionCategoryIcon(category),
      index: index,
      label: displayName,
      // Workaround for SXP-12248
      value: createUniqueOptionValue(displayName !== null && displayName !== void 0 ? displayName : '', category)
    };
  });
};
var convertLocalSuggestionsToOptions = function convertLocalSuggestionsToOptions(suggestions) {
  var _optionGroups;
  var optionGroups = (_optionGroups = {}, _optionGroups[SuggestionCategory.currentLocation] = [{
    label: 'Current Location',
    visuallyHidden: true
  }], _optionGroups[SuggestionCategory.claimedHome] = [{
    label: 'Claimed Home',
    visuallyHidden: true
  }], _optionGroups[SuggestionCategory.savedHome] = [{
    label: 'Saved Homes',
    visuallyHidden: true
  }], _optionGroups[SuggestionCategory.recentSearch] = [{
    label: 'Search History'
  }], _optionGroups[SuggestionCategory.semantic] = [{
    label: 'Suggested Searches'
  }], _optionGroups[SuggestionCategory.savedSearch] = [{
    label: 'Saved Searches'
  }], _optionGroups);
  suggestions.forEach(function (_ref2, index) {
    var displayName = _ref2.displayName,
      category = _ref2.category,
      notificationCount = _ref2.notificationCount;
    if (optionGroups[category]) {
      optionGroups[category].push({
        index: index,
        label: displayName,
        // Workaround for SXP-12248
        value: createUniqueOptionValue(displayName !== null && displayName !== void 0 ? displayName : '', category),
        icon: getSuggestionCategoryIcon(category),
        notificationCount: notificationCount
      });
    }
  });
  return Object.values(optionGroups).filter(function (options) {
    return options.length > 1;
  });
};

// eslint-disable-next-line no-confusing-arrow
var getSize = function getSize(size) {
  return size === 'xl' ? 'lg' : size;
};
var suggestionsToString = function suggestionsToString(suggestions) {
  return suggestions.map(function (_ref3) {
    var displayName = _ref3.displayName,
      category = _ref3.category;
    return createUniqueOptionValue(displayName !== null && displayName !== void 0 ? displayName : '', category);
  }).join(',');
};

// define empty array as a constant to avoid unnecessary re-renders
var EMPTY_ARRAY = [];

// eslint-disable-next-line zillow/@typescript-eslint/no-type-alias

var DEFAULT_INIT_SEARCH_TERM = {
  displayName: '',
  category: 'RAW_SEARCH'
};
var defaultFetchSuggestionsError = function defaultFetchSuggestionsError(err) {
  return console.error(err);
};
var DEFAULT_GA_EVENTS_CONFIG = {
  category: 'searchbox',
  // eslint-disable-next-line no-confusing-arrow
  getClickActionName: function getClickActionName(category) {
    return category === 'RAW_SEARCH' ? 'submit' : 'select-suggestion';
  },
  getFocusActionName: function getFocusActionName() {
    return 'focus';
  }
};
var DEFAULT_INPUT_REF = /*#__PURE__*/React.createRef();
var emptyFunction = function emptyFunction() {};
var SearchBox = function SearchBox(_ref4) {
  var _currentSearchTerm$di2;
  var abKey = _ref4.abKey,
    abTests = _ref4.abTests,
    _ref4$allowEmptyIniti = _ref4.allowEmptyInitialClickstreamBlocks,
    allowEmptyInitialClickstreamBlocks = _ref4$allowEmptyIniti === void 0 ? false : _ref4$allowEmptyIniti,
    centroid = _ref4.centroid,
    className = _ref4.className,
    _ref4$clickstreamDeco = _ref4.clickstreamDecorators,
    clickstreamDecorators = _ref4$clickstreamDeco === void 0 ? null : _ref4$clickstreamDeco,
    _ref4$clickstreamTrig = _ref4.clickstreamTriggerOverrides,
    clickstreamTriggerOverrides = _ref4$clickstreamTrig === void 0 ? null : _ref4$clickstreamTrig,
    clientId = _ref4.clientId,
    _ref4$disableCurrentL = _ref4.disableCurrentLocation,
    disableCurrentLocation = _ref4$disableCurrentL === void 0 ? false : _ref4$disableCurrentL,
    _ref4$useFirstAutocom = _ref4.useFirstAutocompleteSuggestion,
    useFirstAutocompleteSuggestion = _ref4$useFirstAutocom === void 0 ? false : _ref4$useFirstAutocom,
    _ref4$enableOnBlur = _ref4.enableOnBlur,
    enableOnBlur = _ref4$enableOnBlur === void 0 ? true : _ref4$enableOnBlur,
    _ref4$fetchSuggestion = _ref4.fetchSuggestions,
    fetchSuggestions = _ref4$fetchSuggestion === void 0 ? defaultFetchSuggestions : _ref4$fetchSuggestion,
    _ref4$fetchSuggestion2 = _ref4.fetchSuggestionsError,
    fetchSuggestionsError = _ref4$fetchSuggestion2 === void 0 ? defaultFetchSuggestionsError : _ref4$fetchSuggestion2,
    _ref4$filterSuggestio = _ref4.filterSuggestions,
    filterSuggestions = _ref4$filterSuggestio === void 0 ? defaultFilterSuggestions : _ref4$filterSuggestio,
    _ref4$gaEventsConfig = _ref4.gaEventsConfig,
    gaEventsConfig = _ref4$gaEventsConfig === void 0 ? DEFAULT_GA_EVENTS_CONFIG : _ref4$gaEventsConfig,
    _ref4$handleChangeCal = _ref4.handleChangeCallback,
    handleChangeCallback = _ref4$handleChangeCal === void 0 ? emptyFunction : _ref4$handleChangeCal,
    _ref4$handleSubmit = _ref4.handleSubmit,
    handleSubmit = _ref4$handleSubmit === void 0 ? emptyFunction : _ref4$handleSubmit,
    _ref4$initSearchTerm = _ref4.initSearchTerm,
    initSearchTerm = _ref4$initSearchTerm === void 0 ? DEFAULT_INIT_SEARCH_TERM : _ref4$initSearchTerm,
    _ref4$inputRef = _ref4.inputRef,
    inputRef = _ref4$inputRef === void 0 ? DEFAULT_INPUT_REF : _ref4$inputRef,
    _ref4$isCurrentLocati = _ref4.isCurrentLocationSearch,
    isCurrentLocationSearch = _ref4$isCurrentLocati === void 0 ? false : _ref4$isCurrentLocati,
    _ref4$isUserPositionI = _ref4.isUserPositionInMapBounds,
    isUserPositionInMapBounds = _ref4$isUserPositionI === void 0 ? true : _ref4$isUserPositionI,
    _ref4$onClearButtonCl = _ref4.onClearButtonClick,
    onClearButtonClick = _ref4$onClearButtonCl === void 0 ? emptyFunction : _ref4$onClearButtonCl,
    _ref4$onReCenter = _ref4.onReCenter,
    onReCenter = _ref4$onReCenter === void 0 ? emptyFunction : _ref4$onReCenter,
    _ref4$platform = _ref4.platform,
    platform = _ref4$platform === void 0 ? 'DESKTOP' : _ref4$platform,
    _ref4$platformConfig = _ref4.platformConfig,
    platformConfig = _ref4$platformConfig === void 0 ? defaultPlatformConfig : _ref4$platformConfig,
    recentSearchLocalStorageKey = _ref4.recentSearchLocalStorageKey,
    resultTypes = _ref4.resultTypes,
    searchBoxOnPopState = _ref4.searchBoxOnPopState,
    searchBoxDidFocusInput = _ref4.searchBoxDidFocusInput,
    searchBoxDidSelectOption = _ref4.searchBoxDidSelectOption,
    shouldShowSavedHomes = _ref4.shouldShowSavedHomes,
    shouldShowSavedSearches = _ref4.shouldShowSavedSearches,
    _ref4$showAdornments = _ref4.showAdornments,
    showAdornments = _ref4$showAdornments === void 0 ? true : _ref4$showAdornments,
    _ref4$size = _ref4.size,
    searchBoxSize = _ref4$size === void 0 ? INPUT_SIZES.sm : _ref4$size,
    _ref4$trackSearchBoxI = _ref4.trackSearchBoxInputFocus,
    trackSearchBoxInputFocus = _ref4$trackSearchBoxI === void 0 ? null : _ref4$trackSearchBoxI,
    _ref4$userSearchConte = _ref4.userSearchContext,
    userSearchContext = _ref4$userSearchConte === void 0 ? 'FOR_SALE' : _ref4$userSearchConte,
    zgGraphClient = _ref4.zgGraphClient,
    renderInputProp = _ref4.renderInput,
    mapBounds = _ref4.mapBounds;
  var _useState = useState(true),
    _useState2 = _slicedToArray(_useState, 2),
    isLocalQuery = _useState2[0],
    setIsLocalQuery = _useState2[1];
  var _useState3 = useState(EMPTY_ARRAY),
    _useState4 = _slicedToArray(_useState3, 2),
    autocompleteSuggestions = _useState4[0],
    setAutocompleteSuggestions = _useState4[1];
  var _useState5 = useState(false),
    _useState6 = _slicedToArray(_useState5, 2),
    autocompleteSuggestionsLoading = _useState6[0],
    setAutocompleteSuggestionsLoading = _useState6[1];
  var _useState7 = useState(false),
    _useState8 = _slicedToArray(_useState7, 2),
    shouldUpdateSuggestions = _useState8[0],
    setShouldUpdateSuggestions = _useState8[1];
  var _useState9 = useState({}),
    _useState10 = _slicedToArray(_useState9, 2),
    location = _useState10[0],
    setLocation = _useState10[1];
  var _useState11 = useState(initSearchTerm),
    _useState12 = _slicedToArray(_useState11, 2),
    currentSearchTerm = _useState12[0],
    setCurrentSearchTerm = _useState12[1];
  var _useState13 = useState(true),
    _useState14 = _slicedToArray(_useState13, 2),
    showLocationIcon = _useState14[0],
    setShowLocationIcon = _useState14[1];
  var _useState15 = useState(null),
    _useState16 = _slicedToArray(_useState15, 2),
    screenReaderMessage = _useState16[0],
    setScreenReaderMessage = _useState16[1];
  var _useState17 = useState(false),
    _useState18 = _slicedToArray(_useState17, 2),
    isSearchBoxFocused = _useState18[0],
    setIsSearchBoxFocused = _useState18[1];
  var _useState19 = useState(false),
    _useState20 = _slicedToArray(_useState19, 2),
    shouldSelectFirstOption = _useState20[0],
    setShouldSelectFirstOption = _useState20[1];
  var screenReaderTimeoutRef = useRef();
  var comboboxRef = useRef(null);
  var interactionIdRef = useRef();
  var autocompleteRequestIdRef = useRef();
  var formRef = useRef(null);
  var shouldFireDismissSearchEventRef = useRef(true);
  var clearButtonRef = useRef(null);
  var submitButtonRef = useRef(null);
  var currentLocationButtonRef = useRef(null);
  var callback = useCallback(function (cb) {
    cb();
  }, []);
  var yieldWrapper = useYieldCallback(callback);
  var yieldHandleSubmit = useYieldCallback(handleSubmit);
  var sxpSearchBoxUrlSchoolTreatment = getTreatment(abTests, 'SXP_SEARCH_BOX_URL_SCHOOL');
  var sxpSearchBoxUrlAddressTreatment = getTreatment(abTests, 'SXP_SEARCH_BOX_URL_ADDRESS');
  var onContainerClick = useCallback(function (eventTarget) {
    // Don't focus on input if the clear button is clicked
    if (![clearButtonRef, submitButtonRef, currentLocationButtonRef].some(function (ref) {
      return eventTarget.contains(ref.current);
    })) {
      setIsSearchBoxFocused(true);
    }
  }, [clearButtonRef]);
  var onContainerClickOutside = useCallback(function () {
    var _formRef$current;
    if (document.activeElement !== ((_formRef$current = formRef.current) === null || _formRef$current === void 0 ? void 0 : _formRef$current.querySelector('input'))) {
      setIsSearchBoxFocused(false);
    }
  }, []);
  useOnClickInsideOutside(formRef, onContainerClick, onContainerClickOutside);
  useMaximizeListboxHeight(formRef);
  var getResultTypes = useCallback(function (isManualSearch) {
    var _currentSearchTerm$di;
    if (resultTypes) {
      // The following check can be removed after QU support for builder community is added
      if (isManualSearch) {
        return resultTypes.filter(function (type) {
          return type !== 'BUILDER_COMMUNITIES';
        });
      }
      return resultTypes;
    }
    var types = ['REGIONS', 'FORSALE', 'RENTALS', 'SOLD', 'COMMUNITIES'];
    if (sxpSearchBoxUrlSchoolTreatment === 'ON') {
      types.push('SCHOOLS', 'SCHOOL_DISTRICTS');
    }
    if (isManualSearch || ((_currentSearchTerm$di = currentSearchTerm.displayName) === null || _currentSearchTerm$di === void 0 ? void 0 : _currentSearchTerm$di.length) >= 7) {
      types.push('SEMANTIC_REGIONS');
    }
    if (!isManualSearch) {
      types.push('BUILDER_COMMUNITIES');
    }
    return types;
  }, [sxpSearchBoxUrlSchoolTreatment, (_currentSearchTerm$di2 = currentSearchTerm.displayName) === null || _currentSearchTerm$di2 === void 0 ? void 0 : _currentSearchTerm$di2.length, resultTypes]);

  // autocomplete config only used for default fetch
  var suggestionsAutocompleteConfig = useMemo(function () {
    var config = {
      abKey: abKey,
      clientId: clientId,
      centroid: centroid,
      recentSearchLocalStorageKey: recentSearchLocalStorageKey,
      fetchSavedHomes: shouldShowSavedHomes,
      fetchSavedSearches: shouldShowSavedSearches,
      disableCurrentLocation: disableCurrentLocation,
      resultTypes: getResultTypes(false),
      userSearchContext: userSearchContext
    };
    return config;
  }, [abKey, centroid, clientId, disableCurrentLocation, getResultTypes, recentSearchLocalStorageKey, shouldShowSavedHomes, shouldShowSavedSearches, userSearchContext]);
  var fetchSuggestionsWrapper = useCallback(function (query, reason) {
    if (useFirstAutocompleteSuggestion) {
      setAutocompleteSuggestionsLoading(true);
    }
    fetchSuggestions(query, location, suggestionsAutocompleteConfig, reason, undefined, zgGraphClient, mapBounds).then(function (data) {
      var suggestions = 'suggestions' in data ? data.suggestions : data;
      var filteredSuggestions = filterSuggestions(suggestions);
      setIsLocalQuery(data.isLocalQuery);
      setAutocompleteSuggestions(function (previousSuggestions) {
        if (filteredSuggestions.length === 0) {
          return EMPTY_ARRAY;
        }
        // use previous suggestions if they are the same to avoid unnecessary rerenders
        if (suggestionsToString(filteredSuggestions) !== suggestionsToString(previousSuggestions)) {
          return filteredSuggestions;
        }
        return previousSuggestions;
      });
      if ('requestId' in data) {
        autocompleteRequestIdRef.current = data.requestId;
      }
    }).catch(fetchSuggestionsError).finally(function () {
      if (useFirstAutocompleteSuggestion) {
        setAutocompleteSuggestionsLoading(false);
      }
    });
  }, [useFirstAutocompleteSuggestion, fetchSuggestions, fetchSuggestionsError, filterSuggestions, location, suggestionsAutocompleteConfig, zgGraphClient, mapBounds]);
  var delayedFetch = useDebouncedCallback(fetchSuggestionsWrapper, AUTOCOMPLETE_REQUEST_DELAY);

  // update search term, update suggestions if search term changes
  useEffect(function () {
    setCurrentSearchTerm(initSearchTerm);
    setShouldUpdateSuggestions(true);
  }, [initSearchTerm]);

  // update suggestions if userSearchContext changes
  useEffect(function () {
    setShouldUpdateSuggestions(true);
  }, [userSearchContext]);

  // update suggestions if shouldShowSavedHomes or shouldShowSavedSearches changes
  useEffect(function () {
    setShouldUpdateSuggestions(true);
  }, [shouldShowSavedHomes, shouldShowSavedSearches]);
  useEffect(function () {
    if (shouldUpdateSuggestions) {
      if (isSearchBoxFocused) {
        var _currentSearchTerm$di3;
        var _reason = ((_currentSearchTerm$di3 = currentSearchTerm.displayName) === null || _currentSearchTerm$di3 === void 0 ? void 0 : _currentSearchTerm$di3.length) > 0 ? FREEFORM : INIT_LOCAL_RESULTS;
        fetchSuggestionsWrapper(currentSearchTerm.displayName, _reason);
        setShouldUpdateSuggestions(false);
      } else {
        // wait to fetch suggestions until focus.
        // clear suggestions for now so old suggestions don't show.
        setAutocompleteSuggestions(EMPTY_ARRAY);
      }
    }
  }, [shouldUpdateSuggestions, isSearchBoxFocused, fetchSuggestionsWrapper, currentSearchTerm.displayName]);
  var alertUserToNewSuggestions = function alertUserToNewSuggestions() {
    setScreenReaderMessage(autocompleteSuggestions.length + " suggested results are available. Use up and down arrow keys to navigate");
    clearTimeout(screenReaderTimeoutRef.current);
    screenReaderTimeoutRef.current = window.setTimeout(function () {
      setScreenReaderMessage(null);
    }, SCREEN_READER_MESSAGE_TIMEOUT);
    return function () {
      return clearTimeout(screenReaderTimeoutRef.current);
    };
  };

  /**
   * Setting isSearchBoxFocused to false closes suggestions by clearing out the options
   */
  var closeSuggestions = useCallback(function () {
    setIsSearchBoxFocused(false);
  }, []);
  var onPopState = function onPopState() {
    var listener = function listener() {
      return searchBoxOnPopState === null || searchBoxOnPopState === void 0 ? void 0 : searchBoxOnPopState(comboboxRef, closeSuggestions);
    };
    window.addEventListener('popstate', listener);
    return function () {
      return window.removeEventListener('popstate', listener);
    };
  };
  useEffect(onPopState, [searchBoxOnPopState, closeSuggestions, enableOnBlur]);
  useEffect(alertUserToNewSuggestions, [autocompleteSuggestions]);

  /**
   * User clicked Current Location or Location icon and granted access
   */
  var onLocationAccessGranted = useCallback(function (position) {
    var updatedSearchTerm = {
      displayName: '',
      category: SuggestionCategory.currentLocation
    };
    setLocation(position.coords);
    setShowLocationIcon(true);
    setCurrentSearchTerm(updatedSearchTerm);
    yieldHandleSubmit(updatedSearchTerm, {
      accuracy: position.coords.accuracy,
      latitude: position.coords.latitude,
      longitude: position.coords.longitude
    });
  }, [yieldHandleSubmit]);
  var onLocationAccessFailed = useCallback(function () {
    setShowLocationIcon(false);
    // eslint-disable-next-line no-alert
    alert('There is no location support on this device or it is disabled. Please check your settings.');
  }, []);
  var locationSubmit = useCallback(function (accessGranted, accessFailed) {
    if (accessGranted === void 0) {
      accessGranted = onLocationAccessGranted;
    }
    if (accessFailed === void 0) {
      accessFailed = onLocationAccessFailed;
    }
    // eslint-disable-next-line zillow/@typescript-eslint/no-unnecessary-condition
    if (global.navigator.geolocation) {
      global.navigator.geolocation.getCurrentPosition(accessGranted, accessFailed);
    } else {
      console.warn('No location object available');
    }
  }, [onLocationAccessFailed, onLocationAccessGranted]);

  /**
   * User submitted a NLS near me search
   * @param {Object} searchSelection
   * @param {number} viewLatitudeDelta
   */
  var nearMeSubmit = useCallback(function (searchSelection, viewLatitudeDelta) {
    var onAccess = function onAccess(position) {
      setLocation(position.coords);
      setShowLocationIcon(true);
      var _position$coords = position.coords,
        latitude = _position$coords.latitude,
        longitude = _position$coords.longitude;
      var userLocation = {
        latitude: latitude,
        longitude: longitude,
        mapBounds: calculateMapBounds(latitude, longitude, viewLatitudeDelta)
      };
      yieldHandleSubmit(searchSelection, userLocation);
    };
    locationSubmit(onAccess);
  }, [locationSubmit, yieldHandleSubmit]);
  function calculateMapBounds(latitude, longitude, latitudeDelta) {
    // Calculate the longitude delta based on the latitude delta and the center point latitude
    var longitudeDelta = latitudeDelta / Math.cos(latitude * Math.PI / 180);

    // Calculate and return the top, bottom, left, and right bounds of the map
    return {
      north: latitude + latitudeDelta / 2,
      south: latitude - latitudeDelta / 2,
      west: longitude - longitudeDelta / 2,
      east: longitude + longitudeDelta / 2
    };
  }

  /**
   * @param {Suggestion} suggestion
   * @param {*} newFilterState
   */
  var submitSuggestion = useCallback(function (suggestion, newFilterState) {
    // send region info if a region is selected
    if (isRegionSuggestion(suggestion) || isRecentRegionSuggestion(suggestion)) {
      var displayName = suggestion.displayName,
        data = suggestion.data,
        _category = suggestion.category,
        subCategory = suggestion.subCategory;
      RecentSearchClient.addToRecentSearches(suggestion);
      return yieldHandleSubmit({
        displayName: displayName,
        regions: [{
          regionId: data.regionId
        }],
        category: _category,
        subCategory: subCategory
      }, location);
    }
    // send NLS info if a semantic search is selected
    if (isSemanticSuggestion(suggestion) || isRecentSemanticSuggestion(suggestion)) {
      var _displayName = suggestion.displayName,
        _data = suggestion.data,
        _category2 = suggestion.category,
        _subCategory = suggestion.subCategory;
      RecentSearchClient.addToRecentSearches(suggestion);
      var selection = {
        displayName: _displayName,
        category: _category2,
        subCategory: _subCategory,
        regions: _data.regions.map(function (entry) {
          return {
            regionId: entry.id
          };
        }),
        filters: newFilterState,
        queryResolutionStatus: suggestion.data.queryResolutionStatus,
        isSemanticSearchResult: true,
        isSimpleFilterChange: isSimpleFilterChange(suggestion.data.filters)
      };
      if (_data.nearMeSearch && _data.viewLatitudeDelta) {
        return nearMeSubmit(selection, _data.viewLatitudeDelta);
      }
      return yieldHandleSubmit(selection);
    }
    // handle other suggestions: address, saved search
    return yieldHandleSubmit(suggestion, location);
  }, [location, nearMeSubmit, yieldHandleSubmit]);
  var fireClickEvent = useCallback(function (suggestion, suggestionIndex) {
    var categoryTrackingConfig = trackingConfigsByCategory[suggestion.category];
    var getSearchBoxClickLabel = categoryTrackingConfig === null || categoryTrackingConfig === void 0 ? void 0 : categoryTrackingConfig.getSearchBoxClickLabel;
    if (typeof getSearchBoxClickLabel !== 'function') {
      console.error("No ga click event configured for search term with category " + suggestion.category);
    } else {
      var trackEventProp = {
        category: gaEventsConfig.category,
        action: gaEventsConfig.getClickActionName(suggestion.category),
        label: getSearchBoxClickLabel({
          chosenSuggestion: suggestion,
          allSuggestions: autocompleteSuggestions,
          chosenIndex: suggestionIndex + 1,
          // analytics expects a 1-indexed index
          searchBoxValue: currentSearchTerm.displayName
        })
      };
      fireTrackEvent({
        trackEventProp: trackEventProp,
        autocompleteResult: suggestion,
        clickstreamTriggerOverrides: clickstreamTriggerOverrides,
        clickstreamDecorators: clickstreamDecorators,
        allowEmptyInitialClickstreamBlocks: allowEmptyInitialClickstreamBlocks
      });
    }
  }, [allowEmptyInitialClickstreamBlocks, autocompleteSuggestions, clickstreamDecorators, clickstreamTriggerOverrides, currentSearchTerm.displayName, gaEventsConfig]);
  var fireFocusEvent = useCallback(function () {
    if (typeof trackSearchBoxInputFocus === 'function') {
      yieldWrapper(function () {
        return trackSearchBoxInputFocus();
      });
    } else {
      fireTrackEvent({
        trackEventProp: {
          category: 'searchbox',
          action: 'focus',
          label: 'searchpage'
        },
        eventName: EventNames.searchBoxFocus,
        clickstreamTriggerOverrides: clickstreamTriggerOverrides,
        clickstreamDecorators: clickstreamDecorators,
        allowEmptyInitialClickstreamBlocks: allowEmptyInitialClickstreamBlocks
      });
    }
  }, [allowEmptyInitialClickstreamBlocks, clickstreamDecorators, clickstreamTriggerOverrides, trackSearchBoxInputFocus, yieldWrapper]);
  var getSelectionType = useCallback(function (suggestion) {
    if (isRecentSearch(suggestion)) {
      return 'RECENT';
    }
    if (isSavedSearchSuggestion(suggestion)) {
      return 'SAVED_SEARCH';
    }
    if (isLocalQuery && isSemanticSuggestion(suggestion)) {
      return 'SUGGESTED';
    }
    return 'AUTOCOMPLETE';
  }, [isLocalQuery]);
  var blurSearchInput = function blurSearchInput() {
    if (inputRef.current instanceof HTMLElement) {
      inputRef.current.blur();
    }
  };
  var submitSearch = useCallback(function (suggestion, suggestionIndex) {
    return new Promise(function ($return, $error) {
      var _suggestion$displayNa, rawValue, wasAutocompleteSearch, _suggestion$data, _getUpdatedFilterData, filterState, filterBlock, metadata, _suggestions$, _data2, selectionType, rawSearchQuery, _await$fetchZgGraphSu, _suggestions, requestId, requestError, quSuggestion, _quSuggestion$data, hdpUrl, _calculateMapBounds, north, east, south, west, _getUpdatedFilterData2, _filterState, _filterBlock, _metadata, _data3;
      // do not fire dismiss search event if the user is submitting a search
      shouldFireDismissSearchEventRef.current = false;
      rawValue = currentSearchTerm.displayName;
      searchBoxDidSelectOption === null || searchBoxDidSelectOption === void 0 ? void 0 : searchBoxDidSelectOption(comboboxRef);
      fireClickEvent(suggestion, suggestionIndex);
      if (((_suggestion$displayNa = suggestion.displayName) === null || _suggestion$displayNa === void 0 ? void 0 : _suggestion$displayNa.length) === 0) {
        closeSuggestions();
        return $return();
      }
      // Current location row should set location before handleSubmit
      if (isCurrentLocationSuggestion(suggestion)) {
        blurSearchInput();
        locationSubmit();
        return $return();
      }
      setCurrentSearchTerm(suggestion);
      wasAutocompleteSearch = suggestionIndex !== -1;
      /**
       * Submit search using the suggestion selected from autocomplete
       * Applies to all autocomplete selections that aren't recent semantic searches or addresses
       * Recent semantic searches require a request to QU as they use the current search context
       * Address require additional data to be fetched
       */
      if (wasAutocompleteSearch && !isRecentSemanticSuggestion(suggestion) && (sxpSearchBoxUrlAddressTreatment === 'CONTROL' ? true : !isAddressSuggestion(suggestion) && !isRecentAddressSuggestion(suggestion))) {
        _getUpdatedFilterData = getUpdatedFilterData((_suggestion$data = suggestion.data) === null || _suggestion$data === void 0 ? void 0 : _suggestion$data.filters), filterState = _getUpdatedFilterData.filterState, filterBlock = _getUpdatedFilterData.filterBlock;
        metadata = {
          interactionId: interactionIdRef.current,
          requestId: autocompleteRequestIdRef.current,
          selectionType: getSelectionType(suggestion),
          selectionIndex: suggestionIndex,
          rawValue: rawValue,
          searchFilterBlock: filterBlock
        };
        fireEvent({
          eventName: EventNames.queryUnderstandingResponse,
          suggestion: Object.assign({}, suggestion, {
            metadata: metadata
          }),
          clickstreamTriggerOverrides: clickstreamTriggerOverrides,
          clickstreamDecorators: clickstreamDecorators,
          allowEmptyInitialClickstreamBlocks: allowEmptyInitialClickstreamBlocks
        });
        submitSuggestion(suggestion, filterState);
        blurSearchInput();
        setShouldUpdateSuggestions(true);
        return $If_1.call(this);
      }
      // Submit search using QU for any manual, recent semantic search, or address search
      else {
        selectionType = function () {
          if (isRecentSemanticSuggestion(suggestion)) {
            return 'RECENT';
          }
          if (isAddressSuggestion(suggestion)) {
            return 'AUTOCOMPLETE';
          }
          return 'MANUAL';
        }();
        rawSearchQuery = suggestion.displayName;
        return Promise.resolve(fetchZgGraphSuggestions(rawSearchQuery, {
          zgGraphClient: zgGraphClient,
          graphqlQuery: getQueryUnderstandingResults,
          autocompleteConfig: {
            abKey: abKey,
            centroid: centroid,
            resultTypes: getResultTypes(true),
            userSearchContext: userSearchContext,
            fetchLimit: 2
          },
          extraVariables: {
            querySource: selectionType
          },
          mapBounds: mapBounds
        })).then(function ($await_5) {
          try {
            _await$fetchZgGraphSu = $await_5, _suggestions = _await$fetchZgGraphSu.suggestions, requestId = _await$fetchZgGraphSu.requestId, requestError = _await$fetchZgGraphSu.requestError;
            quSuggestion = (_suggestions$ = _suggestions[0]) !== null && _suggestions$ !== void 0 ? _suggestions$ : {
              category: SuggestionCategory.semantic,
              displayName: null,
              data: {
                queryResolutionStatus: requestError ? 'ERROR' : 'FAILED'
              }
            };
            if (isRecentSemanticSuggestion(suggestion)) {
              quSuggestion.category = SuggestionCategory.recentSearch;
              quSuggestion.subCategory = SuggestionCategory.semantic;
            }
            if (sxpSearchBoxUrlAddressTreatment !== 'CONTROL' && shouldCallBuildingService(_suggestions, rawSearchQuery)) {
              quSuggestion.data = (_quSuggestion$data = quSuggestion.data) !== null && _quSuggestion$data !== void 0 ? _quSuggestion$data : {};
              return Promise.resolve(getBuildingUrl(zgGraphClient, quSuggestion.data.lat, quSuggestion.data.lng)).then(function ($await_6) {
                try {
                  quSuggestion.data.url = $await_6;
                  quSuggestion.data.zpid = undefined;
                  quSuggestion.displayName = rawSearchQuery;
                  return $If_2.call(this);
                } catch ($boundEx) {
                  return $error($boundEx);
                }
              }.bind(this), $error);
            } else {
              if (['BDP_HDP', 'BDP_HDP_SRP'].includes(sxpSearchBoxUrlAddressTreatment) && isAddressSuggestion(quSuggestion)) {
                if (quSuggestion.data.zpid) {
                  return Promise.resolve(getHdpUrl(zgGraphClient, quSuggestion.data.zpid)).then(function ($await_7) {
                    try {
                      hdpUrl = $await_7;
                      quSuggestion.data.url = hdpUrl;
                      return $If_4.call(this);
                    } catch ($boundEx) {
                      return $error($boundEx);
                    }
                  }.bind(this), $error);
                } else {
                  if (sxpSearchBoxUrlAddressTreatment === 'BDP_HDP_SRP' && isMaloneAddressSuggestion(quSuggestion)) {
                    if (quSuggestion.data.lat && quSuggestion.data.lng) {
                      _calculateMapBounds = calculateMapBounds(quSuggestion.data.lat, quSuggestion.data.lng, MALONE_ADDRESS_LATITUDE_DELTA), north = _calculateMapBounds.north, east = _calculateMapBounds.east, south = _calculateMapBounds.south, west = _calculateMapBounds.west;
                      quSuggestion.data.url = "/homes/" + north + "," + east + "," + south + "," + west + "_rect/";
                    } else {
                      quSuggestion.data.url = "/homes/";
                    }
                  }
                  return $If_4.call(this);
                }
                function $If_4() {
                  return $If_3.call(this);
                }
              }
              function $If_3() {
                return $If_2.call(this);
              }
              return $If_3.call(this);
            }
            function $If_2() {
              _getUpdatedFilterData2 = getUpdatedFilterData((_data2 = quSuggestion.data) === null || _data2 === void 0 ? void 0 : _data2.filters), _filterState = _getUpdatedFilterData2.filterState, _filterBlock = _getUpdatedFilterData2.filterBlock;
              _metadata = {
                interactionId: interactionIdRef.current,
                requestId: requestId,
                selectionType: selectionType,
                selectionIndex: suggestionIndex,
                rawValue: rawValue,
                searchFilterBlock: _filterBlock
              };
              fireEvent({
                eventName: EventNames.queryUnderstandingResponse,
                suggestion: Object.assign({}, quSuggestion, {
                  metadata: _metadata
                }),
                clickstreamTriggerOverrides: clickstreamTriggerOverrides,
                clickstreamDecorators: clickstreamDecorators,
                allowEmptyInitialClickstreamBlocks: allowEmptyInitialClickstreamBlocks
              });
              if (!quSuggestion.displayName) {
                yieldHandleSubmit({
                  displayName: suggestion.displayName,
                  regions: [],
                  filters: {},
                  queryResolutionStatus: requestError ? 'ERROR' : 'FAILED',
                  category: SuggestionCategory.semantic,
                  isSimpleFilterChange: true
                });
              } else {
                // If the query is partially resolved, we want to maintain the raw search query
                // rather than replacing it with only the resolved portion
                if (((_data3 = quSuggestion.data) === null || _data3 === void 0 ? void 0 : _data3.queryResolutionStatus) === 'PARTIAL_RESOLUTION') {
                  quSuggestion.displayName = rawSearchQuery;
                }
                submitSuggestion(quSuggestion, _filterState);
              }
              blurSearchInput();
              if (wasAutocompleteSearch) {
                setShouldUpdateSuggestions(true);
              }
              return $If_1.call(this);
            }
          } catch ($boundEx) {
            return $error($boundEx);
          }
        }.bind(this), $error);
      }
      function $If_1() {
        closeSuggestions();
        return $return();
      }
    });
  }, [abKey, sxpSearchBoxUrlAddressTreatment, allowEmptyInitialClickstreamBlocks, centroid, clickstreamDecorators, clickstreamTriggerOverrides, closeSuggestions, currentSearchTerm.displayName, fireClickEvent, getResultTypes, inputRef, location, locationSubmit, searchBoxDidSelectOption, submitSuggestion, userSearchContext, yieldHandleSubmit, zgGraphClient, getSelectionType, mapBounds]);

  /**
   * When useFirstAutocompleteSuggestion is enabled, and a user hits enter to search, we want to select the first option
   * after the suggestions are loaded and if a suggestion is available. Otherwise search the search term
   */
  useEffect(function () {
    if (!autocompleteSuggestionsLoading && shouldSelectFirstOption && useFirstAutocompleteSuggestion) {
      if (autocompleteSuggestions.length > 0 && !isLocalQuery) {
        void submitSearch(autocompleteSuggestions[0], 0);
      } else {
        void submitSearch(currentSearchTerm, -1);
      }
      setShouldSelectFirstOption(false);
    }
  }, [autocompleteSuggestionsLoading, shouldSelectFirstOption, autocompleteSuggestions, isLocalQuery, submitSearch, currentSearchTerm, useFirstAutocompleteSuggestion]);
  var onComboboxFocus = useCallback(function () {
    yieldWrapper(function () {
      setIsSearchBoxFocused(true);
      searchBoxDidFocusInput === null || searchBoxDidFocusInput === void 0 ? void 0 : searchBoxDidFocusInput();
    });
    fireFocusEvent();
    shouldFireDismissSearchEventRef.current = true;
    if (abKey) {
      interactionIdRef.current = abKey + "-" + new Date().toISOString();
    }
    setTimeout(function () {
      if (inputRef.current instanceof HTMLInputElement) {
        var length = inputRef.current.value.length;
        inputRef.current.setSelectionRange(length, length);
      }
    });
  }, [abKey, fireFocusEvent, inputRef, searchBoxDidFocusInput, yieldWrapper]);
  var onComboboxChange = useCallback(function (value, _ref5) {
    var reason = _ref5.reason;
    if (typeof value === 'string' && (reason === FREEFORM && !value.match(categoryValuePattern) || reason === CLEAR_INPUT)) {
      handleChangeCallback();
      setCurrentSearchTerm({
        displayName: value,
        category: 'RAW_SEARCH'
      });
      setShowLocationIcon(false);
      delayedFetch(value, reason);
    }
  }, [delayedFetch, handleChangeCallback]);

  // For backward compatibility, we have keep platformConfigs and do this check here,
  // To Do, We need to ensure this component is agnostic to host components
  // by removing keys like SearchResultsPageDesktop, etc.
  // This should allow:
  // host pages to change these configs with needing to update/rebuild this package
  // will allow this package to serve more scenarios without the need to have knowledge of them
  var getPlatformConfiguration = useCallback(
  // eslint-disable-next-line no-confusing-arrow
  function () {
    return platform in platformConfig ? platformConfig[platform] : {};
  }, [platform, platformConfig]);
  var clearInput = useCallback(function () {
    onComboboxChange('', {
      reason: CLEAR_INPUT
    });
    yieldWrapper(function () {
      return onClearButtonClick();
    });
  }, [onClearButtonClick, onComboboxChange, yieldWrapper]);
  var sizeProps = useMemo(function () {
    return {
      // defines the padding that should be used at each window size breakpoint (sm, md, lg)
      buttonPadding: searchBoxSize === 'xl' ? {
        sm: T_SHIRT_SIZES.sm,
        md: 4.375,
        lg: 4.375
      } : {},
      inputPadding: searchBoxSize === 'xl' ? {
        paddingY: {
          sm: T_SHIRT_SIZES.sm,
          md: T_SHIRT_SIZES.md,
          lg: T_SHIRT_SIZES.md
        }
      } : {},
      size: getSize(searchBoxSize)
    };
  }, [searchBoxSize]);
  var _getPlatformConfigura = getPlatformConfiguration(),
    placeholderText = _getPlatformConfigura.placeholderText;
  var buttonPadding = sizeProps.buttonPadding,
    inputPadding = sizeProps.inputPadding,
    size = sizeProps.size;
  var onInputBlur = useCallback(function (e, originalInputBlur) {
    if (shouldFireDismissSearchEventRef.current) {
      fireEvent({
        eventName: EventNames.dismissSearch,
        clickstreamTriggerOverrides: clickstreamTriggerOverrides,
        clickstreamDecorators: clickstreamDecorators,
        allowEmptyInitialClickstreamBlocks: allowEmptyInitialClickstreamBlocks
      });
    }
    if (enableOnBlur) {
      if (typeof originalInputBlur === 'function') {
        originalInputBlur(e);
      }
      setIsSearchBoxFocused(false);
    }
  }, [allowEmptyInitialClickstreamBlocks, clickstreamDecorators, clickstreamTriggerOverrides]);
  var renderInput = useSearchBoxInput({
    disableCurrentLocation: disableCurrentLocation,
    showLocationIcon: showLocationIcon,
    isCurrentLocationSearch: isCurrentLocationSearch,
    size: size,
    buttonPadding: buttonPadding,
    inputPadding: inputPadding,
    clearInput: clearInput,
    isUserPositionInMapBounds: isUserPositionInMapBounds,
    currentSearchTerm: currentSearchTerm,
    onLocationAccessGranted: onLocationAccessGranted,
    onLocationAccessFailed: onLocationAccessFailed,
    onReCenter: onReCenter,
    inputRef: inputRef,
    clearButtonRef: clearButtonRef,
    submitButtonRef: submitButtonRef,
    currentLocationButtonRef: currentLocationButtonRef,
    onBlur: onInputBlur,
    showAdornments: showAdornments,
    renderInput: renderInputProp
  });
  var renderOption = useSearchBoxOption({
    isLocalQuery: isLocalQuery,
    currentSearchTerm: currentSearchTerm
  });
  var onOptionSelect = useCallback(function (_ref6) {
    var index = _ref6.index;
    var selection = autocompleteSuggestions[index];
    if (selection) {
      void submitSearch(selection, index);
    }
  }, [autocompleteSuggestions, submitSearch]);
  var onFormSubmit = useCallback(function (e) {
    e.preventDefault();
    if (currentSearchTerm.category === SuggestionCategory.rawSearch) {
      if (useFirstAutocompleteSuggestion) {
        // Suggestions are loading, select first option after loaded
        if (delayedFetch.isPending()) {
          delayedFetch.flush();
          setShouldSelectFirstOption(true);
        } else if (autocompleteSuggestionsLoading) {
          setShouldSelectFirstOption(true);
        } else if (autocompleteSuggestions.length && !isLocalQuery) {
          void submitSearch(autocompleteSuggestions[0], 0);
        } else {
          void submitSearch(currentSearchTerm, -1);
        }
      } else {
        void submitSearch(currentSearchTerm, -1);
      }
    }
  }, [autocompleteSuggestions, isLocalQuery, useFirstAutocompleteSuggestion, autocompleteSuggestionsLoading, currentSearchTerm, delayedFetch, submitSearch]);
  var options = useMemo(
  // eslint-disable-next-line no-confusing-arrow
  function () {
    if (autocompleteSuggestions.length === 0) {
      return EMPTY_ARRAY;
    }
    return isLocalQuery ? convertLocalSuggestionsToOptions(autocompleteSuggestions) : convertRemoteSuggestionsToOptions(autocompleteSuggestions);
  }, [isLocalQuery, autocompleteSuggestions]);
  return /*#__PURE__*/React.createElement(StyledForm, {
    ref: formRef,
    className: className,
    onSubmit: onFormSubmit,
    __self: _this$1,
    __source: {
      fileName: _jsxFileName$1,
      lineNumber: 1358,
      columnNumber: 9
    }
  }, /*#__PURE__*/React.createElement(StyledCombobox, {
    allowSubmitWhileOpen: true,
    fluidDropdown: true,
    freeForm: true,
    appearance: "input",
    autoCompleteBehavior: "manual",
    optionFilter: null,
    renderEmptyState: null,
    focusFirstOption: useFirstAutocompleteSuggestion && !(isLocalQuery || delayedFetch.isPending() || autocompleteSuggestionsLoading)
    // Combobox doesn't accept the 'xs' size, but it works with our usage
    ,
    size: size,
    selectCallbackFrequency: "always",
    ref: comboboxRef,
    renderInput: renderInput,
    onChange: onComboboxChange,
    onFocus: onComboboxFocus,
    placeholder: placeholderText,
    value: getSuggestionValue(currentSearchTerm)
    // need to cast renderOption, onOptionSelect, and options because we add some extra fields to our options
    ,
    renderOption: renderOption,
    onOptionSelect: onOptionSelect
    // Prevents options from opening on clear button click
    ,
    options: !isSearchBoxFocused ? EMPTY_ARRAY : options,
    __self: _this$1,
    __source: {
      fileName: _jsxFileName$1,
      lineNumber: 1359,
      columnNumber: 13
    }
  }), screenReaderMessage && /*#__PURE__*/React.createElement(VisuallyHidden, {
    "aria-live": "polite",
    "aria-atomic": "true",
    __self: _this$1,
    __source: {
      fileName: _jsxFileName$1,
      lineNumber: 1391,
      columnNumber: 17
    }
  }, /*#__PURE__*/React.createElement("p", {
    __self: _this$1,
    __source: {
      fileName: _jsxFileName$1,
      lineNumber: 1392,
      columnNumber: 21
    }
  }, screenReaderMessage)));
};
var SearchBox$1 = SearchBox;

var _jsxFileName = "/builds/zillow/searchxp/static-search-box/src/SearchBoxModal.tsx",
  _this = undefined;
function _templateObject() {
  var data = _taggedTemplateLiteralLoose(["\n    html,\n    body {\n        overflow: hidden !important;\n    }\n"]);
  _templateObject = function _templateObject() {
    return data;
  };
  return data;
}

// eslint-disable-next-line zillow/@typescript-eslint/naming-convention
var ModalGlobalStyle = createGlobalStyle(_templateObject());
var SearchBoxDialog = styled.div.withConfig({
  displayName: "SearchBoxModal__SearchBoxDialog",
  componentId: "sxp-sb__xw1c73-0"
})(["&.fullscreen{position:fixed;top:0px;left:0px;z-index:", ";height:100%;width:100%;box-sizing:border-box;display:flex;justify-content:center;align-items:center;padding:0px;-webkit-overflow-scrolling:touch;}"], token('zIndices.modal'));
var SearchBoxDialogSection = styled.section.withConfig({
  displayName: "SearchBoxModal__SearchBoxDialogSection",
  componentId: "sxp-sb__xw1c73-1"
})([".fullscreen &{z-index:2;max-width:100%;border-radius:0px;min-height:100%;max-height:100%;box-sizing:border-box;background-color:", ";outline:none;position:relative;display:flex;flex-direction:column;height:auto;width:100%;form{margin-left:", "px;margin-right:", "px;}}"], token('colors.backgroundWhite'), token('spacing.xl'), token('spacing.md'));
var SearchBoxDialogBody = styled.div.withConfig({
  displayName: "SearchBoxModal__SearchBoxDialogBody",
  componentId: "sxp-sb__xw1c73-2"
})([".fullscreen &{overflow-y:auto;padding:12px ", "px 0px;flex:1 1 auto;margin-top:0px;section{box-shadow:none;border-top:1px solid ", ";border-color:none;margin-top:", "px !important;border-radius:0;ul{border:none;}}}form{position:relative;z-index:2;}"], token('spacing.sm'), token('colors.borderDark'), token('spacing.xs'));
var SearchBoxDialogClose = styled.div.withConfig({
  displayName: "SearchBoxModal__SearchBoxDialogClose",
  componentId: "sxp-sb__xw1c73-3"
})([".fullscreen &{position:absolute;z-index:", ";top:", "px;left:0px;}"], token('zIndices.dialog'), token('spacing.xs'));
var StyledIconButton = styled(IconButton).withConfig({
  displayName: "SearchBoxModal__StyledIconButton",
  componentId: "sxp-sb__xw1c73-4"
})(["display:flex;"]);
/**
 * Storing the following as variables to fix why-did-you-render warnings:
 *  - closeIcon
 * If they are not stored like this, the components that consume them will
 * be re-rendered un-necessarily due to its prop being different.
 */
var closeIcon = /*#__PURE__*/React.createElement(IconChevronLeft, {
  __self: undefined,
  __source: {
    fileName: _jsxFileName,
    lineNumber: 115,
    columnNumber: 19
  }
});
var SearchBoxModal = function SearchBoxModal(_ref) {
  var children = _ref.children,
    fullScreen = _ref.fullScreen,
    onClose = _ref.onClose;
  return /*#__PURE__*/React.createElement(SearchBoxDialog, {
    className: fullScreen ? 'fullscreen' : '',
    "aria-label": "Search box modal",
    __self: _this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 118,
      columnNumber: 5
    }
  }, /*#__PURE__*/React.createElement(SearchBoxDialogSection, {
    __self: _this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 119,
      columnNumber: 9
    }
  }, /*#__PURE__*/React.createElement(SearchBoxDialogBody, {
    __self: _this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 120,
      columnNumber: 13
    }
  }, children), fullScreen && /*#__PURE__*/React.createElement(SearchBoxDialogClose, {
    __self: _this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 122,
      columnNumber: 17
    }
  }, /*#__PURE__*/React.createElement(StyledIconButton, {
    "aria-label": "Close modal",
    title: "Close modal",
    icon: closeIcon,
    onClick: onClose,
    size: BUTTON_SIZES.md,
    buttonType: "secondary",
    type: "button",
    bare: true,
    __self: _this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 123,
      columnNumber: 21
    }
  }))), fullScreen && /*#__PURE__*/React.createElement(ModalGlobalStyle, {
    __self: _this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 136,
      columnNumber: 24
    }
  }));
};
var SearchBoxModalContainer = function SearchBoxModalContainer(props) {
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    isFullScreen = _useState2[0],
    setIsFullScreen = _useState2[1];
  var modalDidUpdate = props.modalDidUpdate;
  var focusTimeoutRef = useRef();
  useEffect(function () {
    return clearTimeout(focusTimeoutRef.current);
  }, []);
  var delegateOpen = useCallback(function () {
    setIsFullScreen(true);
    modalDidUpdate === null || modalDidUpdate === void 0 ? void 0 : modalDidUpdate(true);
  }, [modalDidUpdate]);
  var delegateClose = useCallback(function (comboboxRef, closeSuggestions) {
    var _comboboxRef$current, _comboboxRef$current$;
    setIsFullScreen(false);
    modalDidUpdate === null || modalDidUpdate === void 0 ? void 0 : modalDidUpdate(false);
    (_comboboxRef$current = comboboxRef.current) === null || _comboboxRef$current === void 0 ? void 0 : (_comboboxRef$current$ = _comboboxRef$current.querySelector('input')) === null || _comboboxRef$current$ === void 0 ? void 0 : _comboboxRef$current$.blur();
    closeSuggestions === null || closeSuggestions === void 0 ? void 0 : closeSuggestions();
  }, [modalDidUpdate]);
  var onCloseSync = useCallback(function () {
    setIsFullScreen(false);
    modalDidUpdate === null || modalDidUpdate === void 0 ? void 0 : modalDidUpdate(false);
  }, [modalDidUpdate]);
  var onClose = useYieldCallback(onCloseSync);

  // Close modal on unmount
  useEffect(function () {
    return onCloseSync;
  }, [onCloseSync]);
  var searchBoxProps = Object.assign({}, props, {
    enableOnBlur: false,
    searchBoxDidFocusInput: delegateOpen,
    searchBoxDidSelectOption: delegateClose,
    searchBoxOnPopState: delegateClose
  });
  return /*#__PURE__*/React.createElement(SearchBoxModal, {
    fullScreen: isFullScreen,
    onClose: onClose,
    __self: _this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 188,
      columnNumber: 9
    }
  }, /*#__PURE__*/React.createElement(SearchBox$1, _extends({}, searchBoxProps, {
    __self: _this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 189,
      columnNumber: 13
    }
  })));
};
var SearchBoxModalContainer$1 = SearchBoxModalContainer;

export { SRPNavigationSubmitHandler, SearchBox$1 as SearchBox, SearchBoxModalContainer$1 as SearchBoxModal, buildURL, isSearchBoxSelection, isSuggestion };
